<script setup>
/* ============================================================================
   ATD_1.1 — Caixa de entrada  ·  ATD_1.2 — Conversa
   ----------------------------------------------------------------------------
   Um componente serve as duas rotas: a conversa abre AO LADO da lista, como o
   06_Conteudo_das_Telas desenha. `/atendimento/:id` só entra já com uma
   selecionada.

   🚨 A MÍDIA VEM DO WEBHOOK, NÃO DE DOWNLOAD. O Evolution entrega o binário
   em `base64` dentro do próprio evento; o backend só o move para o disco. Aqui
   ele é buscado por `pedirBlob` porque o token vive no cabeçalho e <img src>
   não carrega cabeçalho — apontar o `src` para a rota daria 401 mudo.

   🚨 "NÃO IDENTIFICADO" É CASO NORMAL, NÃO EXCEÇÃO. Medido em 07/08: dos 9
   números que trocaram mensagem, 1 estava no cadastro. A lista mostra o
   telefone quando não há contato, e a ficha explica QUAL é o caso — não é
   cliente, ou o número responde por vários cadastros. As duas situações pedem
   ações diferentes de quem atende.
   ============================================================================ */
import { ref, reactive, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { notificacoes } from '../estado/notificacoes.js'
import { api, pedirBlob, ErroDeApi, relatarErroDeBotao } from '../api/cliente.js'
import { codigosPermitidos } from '../estado/sessao.js'
import { linkificar, marcar, partir } from '../util/destaque.js'
import { corDaInicial, iniciais } from '../util/avatar.js'
import { corDoEstado, rotuloDoEstado } from '../util/estado.js'
import AjudaDaTela from '../componentes/AjudaDaTela.vue'
import BotaoMensagensRapidas from '../componentes/BotaoMensagensRapidas.vue'
import ModalAgendar from '../componentes/ModalAgendar.vue'

const route = useRoute()
const router = useRouter()

const lista = ref([])
const resumo = ref(null)
const aberta = ref(null)
/* 🔵 25/09, celular: *"verifique a compatibilidade ... via navegador mobile,
   os mesmos recursos, porém somente os chats"*. Abaixo de 860 px a tela é
   UMA COISA POR VEZ -- lista OU conversa (o CSS lê `painel--com-conversa`).
   `acoesCelular` abre a barra de ações, que no celular fica recolhida atrás
   de um botão; `balaoTocado` é o balão cujas ações o toque mostrou -- em
   tela de toque não existe "passar o mouse". */
const acoesCelular = ref(false)
/* 🔵 07/10: menu "Outro" do compositor (anexo, áudio, mensagens rápidas)
   recolhido atrás de um botão, no celular e no computador. */
const outroAberto = ref(false)
const balaoTocado = ref(null)
/* Destaque transiente de "clicar na citação vai até a original" (29/09) --
   ver `irParaMensagem`. Não é `balao--atual` (preso ao estado da busca). */
const balaoRealcado = ref(null)
let realceBalaoTimer = null
const semMouse = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(hover: none)') : { matches: false }
const carregando = ref(true)
const erro = ref('')
const recado = ref('')
const filtro = ref('todas')
const busca = ref('')

/* Enquanto a IA não existe, transferir é a TRIAGEM feita por gente. */
const times = ref([])
const classificacoes = ref([])
// '' | 'transferir' | 'convidar' | 'encerrar' | 'vincular'
// Quais mensagens editadas estão com o texto ANTERIOR aberto. Por id, e só
// nesta sessão: é conferência pontual ("o que ele tinha escrito antes?"),
// não um estado que valha guardar.
const originalAberto = ref(new Set())
function alternarOriginal (id) {
  const s = new Set(originalAberto.value)
  s.has(id) ? s.delete(id) : s.add(id)
  originalAberto.value = s
}

const painelAcao = ref('')
const timeEscolhido = ref('')
/* 🔵 15/09: transferir para PESSOA sempre existiu no backend
   (`conversas.transferir` recebe `para_atendente_id` desde a primeira versão)
   e a tela nunca teve o seletor -- só o de time. Os dois são exclusivos:
   escolher um limpa o outro, porque a API aceita um destino por vez. */
const atendenteEscolhido = ref('')
const transferiveis = ref([])

/* 🔵 15/09, pedido da Claudia: o nome do contato nascia do apelido do
   WhatsApp e às vezes era só um emoji. Quem atende digita o nome certo aqui.
   ⚠️ VAZIO NÃO É ERRO: o backend deriva do apelido (já limpo) ou usa o
   telefone. A regra de limpeza mora LÁ, não aqui -- duplicá-la no navegador
   seria criar uma segunda verdade que diverge na primeira mudança. */
const nomeDoContato = ref('')
const motivo = ref('')
const classificacaoEscolhida = ref('')
const comentario = ref('')

const resposta = ref('')
const enviando = ref(false)

/* ---- RASCUNHO POR CONVERSA (Bloco 7, 15/10) ------------------------------
   🔵 Pedido dele nesta rodada: o que foi digitado e nao enviado tem de ficar
   guardado por conversa, e morrer com o login do dia.

   🚨 ISTO CORRIGE UM DEFEITO, nao so acrescenta recurso. `resposta` sobrevivia
   a troca de conversa: quem digitava meia frase para a empresa A, trocava para
   a B e apertava Enter mandava o texto da A para a B. Agora o texto e DAQUELA
   conversa, e so dela.

   ⚠️ `rascunho` aqui e o TEXTO DO COMPOSITOR. Nao confundir com
   `rascunhoNome`, que e o nome do contato sendo editado na ficha (07/10).

   ⚠️ `localStorage` em try/catch, no padrao `movizap.*` do projeto: em janela
   privada, ou com dados do site bloqueados, o acesso ESTOURA -- e perder o
   rascunho e aceitavel, derrubar a tela nao e. */
const PREFIXO_RASCUNHO = 'movizap.rascunho.'
/* Quais conversas tem rascunho guardado, para a lista poder marcar. Vive em
   memoria de proposito: ler o `localStorage` dentro do `v-for` seria uma
   leitura por conversa em cada desenho da lista (sao centenas). */
const comRascunho = ref(new Set())

function chaveRascunho(id) { return PREFIXO_RASCUNHO + id }

function lerRascunho(id) {
  try { return localStorage.getItem(chaveRascunho(id)) || '' } catch { return '' }
}

function apagarRascunho(id = null) {
  const alvo = id ?? aberta.value?.id
  if (!alvo) return
  try { localStorage.removeItem(chaveRascunho(alvo)) } catch { /* sem storage */ }
  if (comRascunho.value.has(alvo)) {
    const s = new Set(comRascunho.value)
    s.delete(alvo)
    comRascunho.value = s
  }
}

/* Guarda o que esta no compositor DA CONVERSA ABERTA AGORA. Chamada ANTES de
   trocar de conversa, ao fechar, e no `beforeunload`. */
function guardarRascunho() {
  const id = aberta.value?.id
  if (!id) return
  const texto = resposta.value
  if (!texto || !texto.trim()) { apagarRascunho(id); return }
  try { localStorage.setItem(chaveRascunho(id), texto) } catch { return }
  if (!comRascunho.value.has(id)) {
    const s = new Set(comRascunho.value)
    s.add(id)
    comRascunho.value = s
  }
}

/* Uma varredura so, no arranque: quais conversas tem rascunho. */
function lerRascunhosGuardados() {
  const achados = new Set()
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const chave = localStorage.key(i)
      if (chave && chave.startsWith(PREFIXO_RASCUNHO)) {
        const id = Number(chave.slice(PREFIXO_RASCUNHO.length))
        if (id) achados.add(id)
      }
    }
  } catch { /* sem storage: a marca simplesmente nao aparece */ }
  comRascunho.value = achados
}

/* ---- LIGACOES DESTA PESSOA (Bloco 5, 15/10) ------------------------------
   🔵 *"aqui na conversa, o modal so trara ultimos 7 dias, para mais dias um
   botao vai levar para a aba Ligacoes"* e *"importante a separacao basica
   entre, feitas, recebidas, etc"*.

   🚨 A ROTA E `ATD_1.2`, a permissao da CONVERSA. Quem atende ve as ligacoes
   do numero que esta atendendo, mesmo sem a aba Ligacoes. */
const ligacoesAbertas = ref(false)
const ligacoesCarregando = ref(false)
const ligacoesDados = ref(null)
const ligacoesErro = ref('')
const tocandoLigacao = ref({ id: null, url: '' })
const SENTIDOS_LIGACAO = [
  ['feita', 'Feitas', 'bi-telephone-outbound'],
  ['recebida', 'Recebidas', 'bi-telephone-inbound'],
  ['perdida', 'Perdidas', 'bi-telephone-x'],
]
/* O salto para a aba so aparece para quem TEM a aba: para os outros seria um
   botao que leva a um 403. */
const vejoAbaLigacoes = computed(() => codigosPermitidos.value.has('LIG_1.1'))

function soltarPlayerLigacao() {
  if (tocandoLigacao.value.url) URL.revokeObjectURL(tocandoLigacao.value.url)
  tocandoLigacao.value = { id: null, url: '' }
}

function fecharLigacoes() {
  soltarPlayerLigacao()
  ligacoesAbertas.value = false
}

async function verLigacoes() {
  if (!aberta.value) return
  ligacoesAbertas.value = true
  ligacoesCarregando.value = true
  ligacoesErro.value = ''
  try {
    ligacoesDados.value = await api.get(`/api/conversas/${aberta.value.id}/ligacoes?dias=7`)
  } catch (e) {
    ligacoesDados.value = null
    ligacoesErro.value = e instanceof ErroDeApi ? e.message : 'Nao consegui ler as ligacoes.'
  } finally {
    ligacoesCarregando.value = false
  }
}

async function tocarLigacao(g) {
  if (tocandoLigacao.value.id === g.id) return soltarPlayerLigacao()
  soltarPlayerLigacao()
  try {
    const blob = await pedirBlob(`/api/ligacoes/gravacao/${g.id}`)
    tocandoLigacao.value = { id: g.id, url: URL.createObjectURL(blob) }
  } catch (e) {
    ligacoesErro.value = e instanceof ErroDeApi ? e.message : 'Nao consegui abrir a gravacao.'
  }
}

async function baixarLigacao(g) {
  try {
    const blob = await pedirBlob(`/api/ligacoes/gravacao/${g.id}`)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = g.nome || `ligacao-${g.id}.mp3`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    ligacoesErro.value = e instanceof ErroDeApi ? e.message : 'Nao consegui baixar a gravacao.'
  }
}

function todasAsLigacoes() {
  const numero = aberta.value?.telefone_e164
  fecharLigacoes()
  router.push({ path: '/ligacoes', query: { numero, dias: 30 } })
}

function duracaoLigacao(s) {
  const n = Number(s || 0)
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`
}

function horaLigacao(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}

/* ---- OUTROS CONTATOS DA EMPRESA (Bloco 6, 15/10) -------------------------
   🔵 Pedido dele nesta rodada: ver os outros contatos da empresa vinculada,
   com telefone, e poder pular para a conversa de um deles.

   🚨 ROTA IRMA SOB `ATD_1.2`. `/api/clientes/{id}` e `CAD_1.1`, que o perfil
   `atendimento` nao tem desde 10/08: a gaveta tomaria 403 justamente para
   quem atende. */
const outrosAbertos = ref(false)
const outrosCarregando = ref(false)
const outros = ref(null)
const outrosErro = ref('')

function fecharOutros() { outrosAbertos.value = false }

/* ---- o nome do contato quando NINGUEM digitou um (15/10, desenho A) -------
   🚨 `contato.nome` é DERIVADO quando ninguém o preencheu: o backend usa o
   apelido do WhatsApp (já limpo) ou, não havendo nenhum, o próprio telefone.
   Quando ele é o telefone, mostrá-lo na ficha repete o número que o topo já
   diz e faz **ausência de dado parecer dado**. A ficha passa a dizer o que é.

   ⚠️ Compara só dígitos e exige 10 ou mais: nome curto de números ("2 Irmãos"
   não tem, mas "1000" teria) não pode ser confundido com telefone. */
function soDigitos(valor) {
  return String(valor || '').replace(/\D/g, '')
}

const contatoSemNome = computed(() => {
  const nome = aberta.value?.empresa?.contato?.nome
  if (!nome || !nome.trim()) return true
  const digitos = soDigitos(nome)
  return digitos.length >= 10 && digitos === soDigitos(aberta.value?.telefone_e164)
})

async function verOutrosContatos() {
  if (!aberta.value) return
  outrosAbertos.value = true
  outrosCarregando.value = true
  outrosErro.value = ''
  try {
    outros.value = await api.get(`/api/conversas/${aberta.value.id}/contatos-da-empresa`)
  } catch (e) {
    outros.value = null
    outrosErro.value = e instanceof ErroDeApi ? e.message : 'Nao consegui ler os contatos.'
  } finally {
    outrosCarregando.value = false
  }
}

/* Pular para a conversa de outro numero da mesma empresa.

   🚨 REUSA A RECEITA QUE JA EXISTE (a do `?numero=` que vem da ficha do
   cliente): busca o numero, 1 acerto abre a conversa, 0 acerto abre "Nova
   mensagem" preenchida. Nao existe rota que apenas localize ou crie conversa
   sem enviar, e inventar uma so para este botao seria um segundo caminho de
   conversa nascer. */
async function conversarCom(linha) {
  if (!linha.e164) return
  fecharOutros()
  busca.value = linha.e164
  await carregar()
  if (lista.value.length === 1) {
    await abrir(lista.value[0].id)
  } else if (!lista.value.length) {
    abrirNova()
    novoNumero.value = linha.e164
  }
}

/* ---- responder citando (aprovado em 12/08, feito em 25/08) ---------------
   🚨 RECEBÍAMOS A CITAÇÃO E NÃO SABÍAMOS ENVIAR. O balão já desenha
   `citada_conteudo` desde sempre -- só o caminho de volta faltava. Citar é
   campo do `sendText` (`quoted`), não rota própria: foi por isso que não deu
   para provar sondando rotas. */
const citando = ref(null)

function citar(m) {
  citando.value = m
  /* Foco no campo: citar e não poder escrever em seguida obriga a um clique
     que não existe no WhatsApp. */
  nextTick(() => document.querySelector('.compositor textarea')?.focus())
}

/* ---- reagir com emoji ----------------------------------------------------
   ⚠️ Seis emojis, não a grade inteira: reação é resposta de UM clique. Quem
   quer escrever escreve. São os mesmos seis do WhatsApp, e por isso ninguém
   precisa aprender. */
const REACOES = ['👍', '❤️', '😂', '😮', '😢', '🙏']
const reagindoEm = ref(null)

/* 🚨 `m.reacoes` É LISTA DESDE 26/08, e não mais um emoji só. O cliente passou
   a reagir também, e 40% das reações são em GRUPO: com um campo só, o último
   que reagisse apagaria os outros em silêncio. Cada item é
   `{emoji, n, nosso}` — o `nosso` é o que deixa o botão aceso. */
function nossaReacao(m) {
  return (m.reacoes || []).find((r) => r.nosso)?.emoji || null
}

async function reagir(m, emoji) {
  reagindoEm.value = null
  /* Vira na tela antes da resposta: reação é clique de meio segundo. Se
     falhar, desfaz e diz. */
  const antes = m.reacoes ? [...m.reacoes] : []
  const atual = nossaReacao(m)
  const novo = atual === emoji ? '' : emoji

  /* O eco otimista mexe só NA NOSSA parte da lista: tirar a linha inteira
     apagaria da tela a reação de outra pessoa, que não mudou. */
  const outros = antes.map((r) => ({ ...r, n: r.nosso ? r.n - 1 : r.n }))
    .filter((r) => r.n > 0)
    .map((r) => ({ ...r, nosso: false }))
  if (novo) {
    const igual = outros.find((r) => r.emoji === novo)
    if (igual) { igual.n += 1; igual.nosso = true }
    else outros.push({ emoji: novo, n: 1, nosso: true })
  }
  m.reacoes = outros

  try {
    await api.post(`/api/conversas/${aberta.value.id}/reagir`, {
      mensagem_id: m.id,
      emoji: novo,
    })
  } catch (e) {
    m.reacoes = antes
    erro.value = e instanceof ErroDeApi ? e.message : 'Não consegui reagir.'
    relatarErroDeBotao('reagir', e, aberta.value?.id)
  }
}

/* ---- mídia em tela cheia -------------------------------------------------
   🚨 A FOTO ABRIA DO TAMANHO DO BALÃO. Print de erro e foto de avaria são
   exatamente o que precisa de zoom, e eram justamente o que não dava para
   ver. */
const emTelaCheia = ref(null)

/* ---- gravar áudio --------------------------------------------------------
   🚨 `sendWhatsAppAudio`, não anexo: pela rota de voz a mensagem chega com
   onda e tocar-seguido; por `sendMedia`, chega como arquivo para baixar. */
const gravando = ref(false)
const segundosGravados = ref(0)
let gravador = null
let pedacos = []
let relogioGravacao = null

async function comecarGravacao() {
  try {
    const trilha = await navigator.mediaDevices.getUserMedia({ audio: true })
    pedacos = []
    gravador = new MediaRecorder(trilha)
    gravador.ondataavailable = (e) => { if (e.data.size) pedacos.push(e.data) }
    gravador.onstop = () => {
      /* ⚠️ Solta o microfone SEMPRE. Sem isto o navegador fica com a luz de
         gravação acesa depois de enviar, e a pessoa acha que o painel está
         ouvindo. */
      trilha.getTracks().forEach((t) => t.stop())
    }
    gravador.start()
    gravando.value = true
    segundosGravados.value = 0
    relogioGravacao = setInterval(() => { segundosGravados.value += 1 }, 1000)
  } catch {
    erro.value = 'Não consegui usar o microfone. Verifique a permissão do navegador.'
  }
}

function pararRelogio() {
  clearInterval(relogioGravacao)
  gravando.value = false
}

/* Cancelar existe porque gravar sem poder desistir faz a pessoa não gravar. */
function cancelarGravacao() {
  if (!gravador) return
  gravador.onstop = null
  gravador.stream?.getTracks().forEach((t) => t.stop())
  gravador.stop()
  gravador = null
  pedacos = []
  pararRelogio()
}

async function enviarGravacao() {
  if (!gravador) return
  const pronto = new Promise((resolve) => {
    const antes = gravador.onstop
    gravador.onstop = (e) => { antes?.(e); resolve() }
  })
  gravador.stop()
  await pronto
  pararRelogio()

  /* 🔵 25/09, celular: o iPhone (Safari) grava MP4/AAC, não Opus. O rótulo
     passa a dizer a verdade SÓ nesse caso; o Chrome do computador continua
     como sempre foi. A Evolution converte para voz do WhatsApp de qualquer
     jeito -- a prova é um áudio real mandado do iPhone. */
  const doIphone = (gravador.mimeType || '').includes('mp4')
  const blob = new Blob(pedacos, { type: doIphone ? 'audio/mp4' : 'audio/ogg; codecs=opus' })
  gravador = null
  pedacos = []
  if (!blob.size) return

  enviando.value = true
  try {
    const dados = new FormData()
    dados.append('arquivo', blob, doIphone ? 'audio.m4a' : 'audio.ogg')
    const r = await fetch(`/api/conversas/${aberta.value.id}/audio`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('movizap.token')}` },
      body: dados,
    })
    if (!r.ok) throw new Error((await r.json()).detail || 'falhou')
    await Promise.all([abrir(aberta.value.id), carregar({ silencioso: true })])
  } catch (e) {
    erro.value = e.message || 'Não consegui enviar o áudio.'
    relatarErroDeBotao('enviar_audio', e, aberta.value?.id)
  } finally {
    enviando.value = false
  }
}

/* ---- o TIPO da pessoa, na própria ficha -----------------------------------
   🚨 A FAIXA MOSTRAVA A EMPRESA E NÃO MOSTRAVA O TIPO. Apontado por ele em
   26/08. Até 25/08 o tipo era só uma etiqueta de cadastro e a falta passava;
   desde então ele decide se a **saudação automática** dispara, e desde 26/08
   se a **IA** atende — quem está falando com a pessoa é quem sabe o que ela
   é, e mandá-lo a outra tela para marcar isso é o caminho que ninguém faz.

   ⚠️ A LISTA É CÓPIA DO CHECK DO BANCO (`contato_relacao_check`, migração
   031), igual à da tela de Contatos. Divergir aqui daria 400 na cara do
   atendente com o valor que a própria tela ofereceu.

   ⚠️ QUEM NÃO TEM `CAD_1.2` VÊ, MAS NÃO TROCA. A rota exige essa permissão, e
   oferecer um seletor que responde 403 é pior que mostrar o valor: o
   frontend desenha, não decide. */
const RELACOES = [
  ['cliente', 'Cliente'], ['fornecedor', 'Fornecedor'], ['parceiro', 'Parceiro'],
  ['tecnico', 'Técnico'], ['lead', 'Lead'], ['colaborador', 'Colaborador'],
  ['teste', 'Teste'], ['sem_identificacao', 'Sem identificação'],
]
const NOME_RELACAO = Object.fromEntries(RELACOES)

/* 🚫 A LISTA FILTRADA SAIU EM 18/09. Eu tinha tirado `sem_identificacao` da
   escolha em 28/08 -- decisão MINHA, declarada na época -- tratando-o como
   lixo de nascimento da migração 031. Ele corrigiu: *"sem identificação é sem
   identificação mesmo, não é só porque tem empresa vinculada que é cliente.
   esse cad deve ser feito a mão"*.

   E o banco já dizia isso: `vincular()` insere o contato SEM definir
   `relacao`, então ele nasce `sem_identificacao` por default -- vincular
   empresa nunca marcou ninguém como cliente. Medido em 18/09: 19 contatos
   assim, todos COM empresa. São pessoas ainda não identificadas, não
   clientes.

   Com o valor de volta à lista, as três telas que mostram o tipo (Contatos,
   conversa com empresa, conversa sem empresa) passam a oferecer os mesmos 8
   valores do CHECK do banco. */

const podeTrocarTipo = computed(() => codigosPermitidos.value.has('CAD_1.2'))

/* 🚨 MARCAR O TIPO DE DENTRO DA CONVERSA É ATD_1.2, NÃO CAD_1.2. O perfil
   `atendimento` não tem CAD_1.2 desde 10/08 -- exigir a permissão do cadastro
   esconderia o seletor justamente de quem está falando com a pessoa. A rota
   nova checa ATD_1.2, e a tela segue a rota. */
const podeMarcarTipo = computed(() => codigosPermitidos.value.has('ATD_1.2'))
const tipoSalvo = ref(false)

/* 🚨 O TIPO NÃO DEPENDE MAIS DE EMPRESA VINCULADA (28/08). Pedido dele:
   *"o tipo… não precisa depender de empresa vinculada para ter o campo"*.

   Antes esta função desistia sem `empresa.contato`, e a ficha só mostrava o
   chip "Sem cadastro" -- em 61% das conversas. O tipo mora em `contato`, então
   escolher CRIA o contato (sem empresa, `origem='movizap'`), pela rota
   `PUT /api/conversas/{id}/tipo`.

   ⚠️ RELÊ DO SERVIDOR EM VEZ DE REMENDAR A TELA. Criar contato muda `empresa`,
   `candidatos`, `bitrix` e o rótulo do botão da ficha de uma vez -- remendar
   quatro campos à mão é onde a tela começa a divergir do banco. Mesma decisão
   do `vincularA`. */

/* 🚨 O TIPO VEM DO SERVIDOR, NÃO DE UMA CÓPIA LOCAL (18/09). Aqui havia um
   `ref` próprio (`tipoSemCadastro`) que nascia vazio e só era escrito ao
   SALVAR, nunca ao abrir a ficha -- então quem já tinha tipo e não tinha
   empresa via "sem cadastro" no lugar do tipo dele. Eram 5 contatos
   `tecnico`, e o campo decide se a automação e a IA atendem a pessoa
   (`CFG_5.1`): a tela mentia sobre o que governa o atendimento.

   ⚠️ É `computed` com get/set para haver UMA fonte: o getter é o dado do
   servidor e o setter é a gravação. Não há mais estado de tela para
   dessincronizar.

   ⚠️ O QUE ISTO **NÃO** CONSERTA, e é anterior a 18/09: quando NÃO há
   contato, o `trocarTipo` não grava otimista (não há `contato` para
   escrever), então um PUT que falha deixa a escolha aparente no seletor --
   o Vue não repinta um valor que, para ele, continua `''`. O que avisa é a
   faixa de erro ("Não consegui marcar o tipo"). Vale para o `:value` de
   antes e para o `v-model` de agora, igual. */
const tipoAtual = computed({
  get: () => aberta.value?.empresa?.contato?.relacao || '',
  set: (nova) => trocarTipo(nova),
})

async function trocarTipo(nova) {
  const contato = aberta.value?.empresa?.contato
  if (!nova || (contato && nova === contato.relacao)) return
  tipoSalvo.value = false
  const antes = contato ? contato.relacao : null
  if (contato) contato.relacao = nova
  try {
    const r = await api.put(`/api/conversas/${aberta.value.id}/tipo`,
                            { relacao: nova })
    tipoSalvo.value = true
    /* 🚨 A AUTOMAÇÃO MUDA NA HORA, e a tela avisa em vez de deixar descobrir
       pelo comportamento: sem cadastro a pessoa seguia a linha `sem_cadastro`
       da CFG_5.1; com o tipo marcado ela passa a seguir a do tipo. */
    if (r.criou_contato) {
      recado.value = `Cadastro criado e marcado como ${NOME_RELACAO[nova] || nova}. `
        + 'A automação por tipo passa a valer para esta pessoa.'
      await abrir(aberta.value.id)
      await carregar({ silencioso: true })
    }
  } catch (e) {
    if (contato) contato.relacao = antes
    erro.value = e instanceof ErroDeApi ? e.message : 'Não consegui marcar o tipo.'
    relatarErroDeBotao('marcar_tipo', e, aberta.value?.id)
  }
}

/* 🔵 07/10 — o tipo do contato governa dois detalhes da ficha:
   - LEAD deixa o nome do contato editável (decisão dele); fora dos tipos
     editáveis o nome fica só-leitura, mas o que foi salvo permanece, porque
     mora em `contato`.
   - TÉCNICO esconde "Empresas vinculadas" e "Vincular a uma empresa": técnico é
     pessoa, não se liga a empresa.
   🔵 19/10 — TÉCNICO entrou na mesma regra de nome do LEAD, por decisão
   dele: *"para tecnicos, adicione ele na regra do tipo 'LEAD' que permite
   editar o nome"*. Motivo real: os técnicos importados chegaram com o nome do
   WhatsApp divergindo do cadastro (7 conversas em 08/10), e quem atende precisa
   corrigir dali mesmo. A trava de identidade não muda: isto edita o NOME do
   contato, não o telefone nem o vínculo. */
const RELACOES_COM_NOME_EDITAVEL = ['lead', 'tecnico']
const relacaoContato = computed(() => aberta.value?.empresa?.contato?.relacao || '')
const ehTecnico = computed(() => relacaoContato.value === 'tecnico')
const nomeEditavel = computed(
  () => RELACOES_COM_NOME_EDITAVEL.includes(relacaoContato.value))

const rascunhoNome = ref('')
const salvandoNome = ref(false)
const nomeSalvo = ref(false)

async function salvarNomeContato() {
  const contato = aberta.value?.empresa?.contato
  const nome = rascunhoNome.value.trim()
  if (!contato || !nome || nome === contato.nome) return
  salvandoNome.value = true
  nomeSalvo.value = false
  try {
    const r = await api.put(`/api/conversas/${aberta.value.id}/contato/nome`,
                            { nome })
    contato.nome = r.nome
    nomeSalvo.value = true
  } catch (e) {
    erro.value = e instanceof ErroDeApi ? e.message : 'Não consegui salvar o nome.'
    relatarErroDeBotao('salvar_nome_contato', e, aberta.value?.id)
  } finally {
    salvandoNome.value = false
  }
}

/* ---- encaminhar ----------------------------------------------------------
   Entrou em 25/08, quando a regra de "não é caixa de disparo" caiu. Cinco
   destinos por vez, que é o limite do próprio WhatsApp. */
const encaminhando = ref(null)
const destinosEscolhidos = ref([])

function abrirEncaminhar(m) {
  encaminhando.value = m
  destinosEscolhidos.value = []
}

async function confirmarEncaminhar() {
  if (!destinosEscolhidos.value.length) return
  try {
    const r = await api.post('/api/conversas/encaminhar', {
      mensagem_id: encaminhando.value.id,
      conversas: destinosEscolhidos.value,
    })
    /* 🚨 A FALHA DIZ O MOTIVO, não só o número. "1 não deu" manda o atendente
       adivinhar; com arquivo, o motivo costuma ser o teto de 25 MB, e é isso
       que ele precisa ler para saber que não foi o número que está errado. */
    recado.value = r.falhas.length
      ? `Encaminhada para ${r.enviadas}; ${r.falhas.length} não deu — `
        + r.falhas.map((f) => f.motivo).filter(Boolean).join(' · ')
      : `Encaminhada para ${r.enviadas} conversa(s).`
    encaminhando.value = null
  } catch (e) {
    erro.value = e instanceof ErroDeApi ? e.message : 'Não consegui encaminhar.'
    relatarErroDeBotao('encaminhar', e, aberta.value?.id)
  }
}

/* ---- buscar DENTRO da conversa (ATD_1.2) --------------------------------
   Pergunta diferente da busca da lista: lá é "com quem eu falei", aqui é
   "onde ele disse isso". Por isso são dois campos e não um.

   🚨 SUBIU PARA O SERVIDOR EM 25/08, junto com a paginação. Ela rodava aqui
   no navegador, sobre `aberta.mensagens` -- e isso estava CERTO enquanto a
   conversa inteira vinha de uma vez. No momento em que a tela passou a abrir
   com 60 mensagens, a mesma busca passaria a responder "nada com esse termo"
   sobre mensagem que existe três telas acima. É por isso que paginar e mover
   a busca são um item só.

   ⚠️ O servidor devolve as POSIÇÕES (ids). Se o acerto está fora da janela
   carregada, a tela carrega para trás até alcançá-lo -- ver `irParaAchado`. */
const buscaNaConversa = ref('')
const achadoAtual = ref(0)
const achadosNaConversa = ref([])

/* A busca abre por botão (27/08): ela é ação eventual e ocupava uma faixa fixa
   na altura mais disputada da tela. */
const buscaAberta = ref(false)
const campoBuscaConversa = ref(null)

function alternarBusca() {
  if (buscaAberta.value) { fecharBusca(); return }
  buscaAberta.value = true
  // Abrir e ter de clicar no campo seria um clique a mais para nada.
  nextTick(() => campoBuscaConversa.value && campoBuscaConversa.value.focus())
}

/* ⚠️ FECHAR LIMPA O TERMO, e isso não é zelo: com o termo guardado, os balões
   continuariam marcados e o contador sumiria junto com o campo — um filtro
   ativo sem nada na tela dizendo que existe. */
function fecharBusca() {
  buscaAberta.value = false
  buscaNaConversa.value = ''
  achadosNaConversa.value = []
  achadoAtual.value = 0
}
const buscandoNaConversa = ref(false)
/* ⚠️ O servidor devolve no máximo 200 acertos. Termo largo numa conversa
   longa encosta nisso, e encostar calado é a mentira por omissão que o teto
   de 1.000 mensagens fazia. */
const achadosLimitados = ref(false)

const idAchado = computed(() => achadosNaConversa.value[achadoAtual.value] ?? null)

function casaNaConversa(m) {
  return achadosNaConversa.value.includes(m.id)
}

async function buscarNaConversa() {
  const alvo = buscaNaConversa.value.trim()
  achadoAtual.value = 0
  if (!alvo || !aberta.value) {
    achadosNaConversa.value = []
    achadosLimitados.value = false
    return
  }
  buscandoNaConversa.value = true
  try {
    const r = await api.get(
      `/api/conversas/${aberta.value.id}/buscar?termo=${encodeURIComponent(alvo)}`)
    achadosNaConversa.value = (r.achados || []).map((a) => a.id)
    achadosLimitados.value = Boolean(r.limitado)
    if (achadosNaConversa.value.length) await irParaAchado(0)
  } catch {
    achadosNaConversa.value = []
  } finally {
    buscandoNaConversa.value = false
  }
}

async function irParaAchado(passo) {
  const total = achadosNaConversa.value.length
  if (!total) return
  // Dá a volta nas duas direções: no último, "próximo" volta ao primeiro.
  achadoAtual.value = (achadoAtual.value + passo + total) % total
  await rolarAteAchado()
}

async function rolarAteAchado() {
  await irParaMensagem(idAchado.value)
}

/* Acha e rola até QUALQUER mensagem da conversa, carregando para trás se
   preciso -- extraído da busca (27/08) para servir também ao clique na
   citação dentro do balão (29/09): "clicar na citação vai até a original".

   🚨 O ALVO PODE ESTAR ACIMA DO QUE ESTÁ CARREGADO. Sem isto, a tela não
   teria para onde rolar -- a busca diria "3/7" e nada se mexeria, e a
   citação simplesmente não abriria nada. Carrega para trás até o balão
   existir, com um limite de voltas para nunca virar laço infinito numa
   conversa gigante. */
async function irParaMensagem(alvo) {
  await nextTick()
  if (!alvo || !baloes.value) return

  let voltas = 0
  while (!baloes.value.querySelector(`[data-mensagem="${alvo}"]`)
         && aberta.value?.tem_anteriores && voltas < 25) {
    await carregarAnteriores()
    await nextTick()
    voltas += 1
  }

  const el = baloes.value.querySelector(`[data-mensagem="${alvo}"]`)
  if (!el) return
  el.scrollIntoView({ block: 'center', behavior: 'smooth' })

  /* Destaque transiente (não é `balao--atual`, que fica preso ao estado da
     busca): pisca e some, só para o olho achar o balão que acabou de rolar
     para a tela. */
  balaoRealcado.value = alvo
  clearTimeout(realceBalaoTimer)
  realceBalaoTimer = setTimeout(() => {
    if (balaoRealcado.value === alvo) balaoRealcado.value = null
  }, 2000)
}

/* ⚠️ Espera a digitação parar: uma rota por tecla faria uma consulta ao banco
   a cada letra. 300 ms é o intervalo em que a pessoa ainda não percebeu que
   esperou. */
let debounceBuscaConversa = null
watch(buscaNaConversa, () => {
  clearTimeout(debounceBuscaConversa)
  debounceBuscaConversa = setTimeout(buscarNaConversa, 300)
})

/* ---- carregar mensagens anteriores --------------------------------------
   🚨 A tela abre com 60 e sobe de 200 em 200 (decisão do usuário, 25/08). O
   teto de 1.000 saiu: a maior conversa da base já tinha 776, e teto que se
   encosta silencia mensagem antiga. */
const carregandoAnteriores = ref(false)

async function carregarAnteriores() {
  if (!aberta.value || carregandoAnteriores.value) return
  if (!aberta.value.mensagens.length) return
  carregandoAnteriores.value = true
  const topo = aberta.value.mensagens[0].id
  /* ⚠️ Guarda a altura ANTES de prepender: sem isto a lista salta e a pessoa
     perde o lugar onde estava lendo -- o conteúdo novo entra por cima e
     empurra tudo para baixo. */
  const alturaAntes = baloes.value ? baloes.value.scrollHeight : 0
  try {
    const r = await api.get(
      `/api/conversas/${aberta.value.id}/mensagens?antes_de=${topo}`)
    aberta.value.mensagens = [...(r.mensagens || []), ...aberta.value.mensagens]
    aberta.value.tem_anteriores = Boolean(r.tem_anteriores)
    await nextTick()
    if (baloes.value) {
      baloes.value.scrollTop = baloes.value.scrollHeight - alturaAntes
    }
  } catch (e) {
    erro.value = e instanceof ErroDeApi ? e.message : 'Não consegui carregar.'
    relatarErroDeBotao('carregar_anteriores', e, aberta.value?.id)
  } finally {
    carregandoAnteriores.value = false
  }
}

/* Participantes — quem mais está acompanhando esta conversa.
   🚨 Convidar NÃO dá acesso: qualquer atendente com ATD_1.2 já abre qualquer
   conversa. O convite faz ela APARECER NA LISTA de quem foi chamado. */
const acompanham = ref([])
const convidaveis = ref([])
const souDono = ref(false)
/* 🔵 23/09: quem sou eu -- editar e apagar só aparecem na MINHA mensagem.
   Vem da rota de participantes (`eu`), que já existia. */
const meuId = ref(null)
const souParticipante = ref(false)
/* Vários de uma vez (12/08): era um <select> de um só, e chamar três pessoas
   custava três idas ao painel. */
const convidados = ref([])
/* 🔵 23/09: o recado de quem convida. Vira NOTA INTERNA na conversa, pela
   mesma rota da nota -- o convite em si não tinha onde guardar texto. */
const recadoConvite = ref('')
const mexendo = ref(false)

/* ---- confirmação --------------------------------------------------------
   Uma caixa só, alimentada por quem chama. `acao` é a função que roda no
   "confirmar"; enquanto ninguém aperta, nada acontece.

   ⚠️ NÃO CONFIRMAR O QUE SE DESFAZ SOZINHO. Encerrar, devolver e sair mudam
   quem responde pelo cliente e nenhum tem "desfazer" -- por isso estes três
   perguntam. Transferir e convidar abrem painel próprio, onde a escolha já é
   deliberada; pedir confirmação ali seria um clique a mais por nada. */
const confirmacao = ref(null)

/* ⚠️ FECHAR LIMPA O QUE FOI DIGITADO. Sem isto, quem abre "encerrar", escolhe
   uma classificação, desiste e depois encerra OUTRA conversa leva a escolha
   antiga junto -- e o histórico grava um rótulo que ninguém escolheu ali. */
function abrirPainel(qual) {
  painelAcao.value = painelAcao.value === qual ? '' : qual
  if (!painelAcao.value) limparPaineis()
}

function fecharPainel() {
  painelAcao.value = ''
  limparPaineis()
}

function limparPaineis() {
  convidados.value = []
  recadoConvite.value = ''
  timeEscolhido.value = ''
  atendenteEscolhido.value = ''
  motivo.value = ''
  classificacaoEscolhida.value = ''
  comentario.value = ''
  /* ⚠️ O termo do vínculo entra aqui pela mesma razão dos outros (28/08):
     quem procura "Velasco", desiste e abre o vínculo de OUTRA conversa
     encontraria a lista da anterior já montada -- e um clique ali vincularia
     a empresa certa ao telefone errado. */
  buscaCliente.value = ''
  achadosCliente.value = []
  nomeDoContato.value = ''
}

/* ---- editar e apagar o que EU mandei (23/09) ----------------------------
   🔵 Pergunta dele: *"então eu envio por lá e posso editar, certo?"*. Até
   23/09 não dava. As janelas são do WhatsApp -- 15 min para editar, cerca de
   dois dias para apagar (o servidor usa 48 h) -- e a tela só oferece o botão
   dentro delas, para não prometer o que o WhatsApp vai recusar. O servidor
   confere de novo: a tela é conveniência, a trava é a rota. */
const JANELA_EDITAR_MIN = 15
const JANELA_APAGAR_MIN = 48 * 60
const editando = ref(null)   // { id, texto }

function idadeMin(m) {
  return (Date.now() - new Date(m.criada_em).getTime()) / 60000
}
function minhaEnviada(m) {
  return m.direcao === 'saida' && m.tipo !== 'nota' && !m.apagada_em &&
    meuId.value != null && m.atendente_id === meuId.value
}
/* 🔵 28/09: nota interna editável/apagável por quem escreveu, SEM janela de
   tempo (decisão dele) -- é registro interno, nunca sai para o cliente. */
function minhaNota(m) {
  return m.tipo === 'nota' && !m.apagada_em &&
    meuId.value != null && m.atendente_id === meuId.value
}
function podeEditar(m) {
  if (m.tipo === 'nota') return minhaNota(m)
  return minhaEnviada(m) && m.tipo === 'texto' && idadeMin(m) <= JANELA_EDITAR_MIN
}
function podeApagar(m) {
  if (m.tipo === 'nota') return minhaNota(m)
  return minhaEnviada(m) && idadeMin(m) <= JANELA_APAGAR_MIN
}
function abrirEdicao(m) {
  editando.value = { id: m.id, texto: m.conteudo || '', nota: m.tipo === 'nota' }
}
async function salvarEdicao() {
  const e = editando.value
  if (!e || !e.texto.trim() || mexendo.value) return
  mexendo.value = true
  erro.value = ''
  try {
    const rota = e.nota ? 'nota/editar' : 'editar'
    await api.post(`/api/conversas/${aberta.value.id}/${rota}`,
                   { mensagem_id: e.id, texto: e.texto })
    editando.value = null
    recado.value = e.nota ? 'Nota editada.'
      : 'Mensagem editada — o cliente vê a versão nova, marcada como editada.'
    await abrir(aberta.value.id)
  } catch (x) {
    erro.value = x instanceof ErroDeApi ? x.message : 'Não consegui editar.'
    relatarErroDeBotao('editar_mensagem', x, aberta.value?.id, e.id)
  } finally {
    mexendo.value = false
  }
}
function pedirParaApagar(m) {
  const nota = m.tipo === 'nota'
  /* 🚨 A CONVERSA É CONGELADA JUNTO COM A MENSAGEM (16/10). A confirmação
     capturava `m` no clique e lia `aberta.value.id` só no "sim" -- se a
     conversa aberta mudasse com o diálogo na tela, o pedido ia para a conversa
     NOVA levando o id da mensagem da ANTIGA, e o painel respondia que a
     mensagem não era daquela conversa. Corrida pequena, erro confuso. */
  const conversaId = aberta.value?.id
  perguntar(nota ? 'Apagar esta nota?' : 'Apagar para todos?',
    nota ? 'A nota some da conversa para a equipe. Continua registrada, marcada como apagada.'
         : 'A mensagem some do WhatsApp do cliente. Aqui ela continua registrada, marcada como apagada.',
    nota ? 'Apagar nota' : 'Apagar para todos',
    async () => {
      try {
        const rota = nota ? 'nota/apagar' : 'apagar'
        await api.post(`/api/conversas/${conversaId}/${rota}`, { mensagem_id: m.id })
        recado.value = nota ? 'Nota apagada.' : 'Mensagem apagada para todos.'
        await abrir(conversaId)
      } catch (x) {
        erro.value = x instanceof ErroDeApi ? x.message : 'Não consegui apagar.'
        relatarErroDeBotao('apagar_mensagem', x, conversaId, m.id)
      }
    }, true)
}

/* ---- bloquear pelo painel (23/09) ----------------------------------------
   🔵 Pedido dele: *"permitir ler e só bloquear se existir pelo painel tbm"*.
   🚨 MEXE NO APARELHO: o número deixa de conseguir falar com a empresa --
   por isso confirma. Desbloquear não confirma: é o lado que desfaz. */
function pedirParaBloquear() {
  perguntar('Bloquear este número?',
    'O número deixa de conseguir mandar mensagem para a empresa pelo WhatsApp. ' +
    'A conversa sai das abas e fica em "Bloqueados", onde se desbloqueia.',
    'Bloquear',
    async () => {
      try {
        await api.post(`/api/conversas/${aberta.value.id}/bloquear`, {})
        recado.value = 'Número bloqueado. A conversa está em "Bloqueados".'
        await Promise.all([abrir(aberta.value.id), carregar({ silencioso: true })])
      } catch (x) {
        erro.value = x instanceof ErroDeApi ? x.message : 'Não consegui bloquear.'
        relatarErroDeBotao('bloquear', x, aberta.value?.id)
      }
    }, true)
}
async function desbloquear() {
  if (mexendo.value) return
  mexendo.value = true
  erro.value = ''
  try {
    await api.post(`/api/conversas/${aberta.value.id}/desbloquear`, {})
    recado.value = 'Número desbloqueado. A conversa voltou para as abas.'
    await Promise.all([abrir(aberta.value.id), carregar({ silencioso: true })])
  } catch (x) {
    erro.value = x instanceof ErroDeApi ? x.message : 'Não consegui desbloquear.'
    relatarErroDeBotao('desbloquear', x, aberta.value?.id)
  } finally {
    mexendo.value = false
  }
}

function perguntar(titulo, texto, rotulo, acao, perigo = false) {
  confirmacao.value = { titulo, texto, rotulo, acao, perigo }
}

async function confirmar() {
  const c = confirmacao.value
  if (!c || mexendo.value) return
  confirmacao.value = null
  await c.acao()
}

async function carregarParticipantes(id) {
  try {
    const r = await api.get(`/api/conversas/${id}/participantes`)
    acompanham.value = r.participantes || []
    convidaveis.value = r.convidaveis || []
    transferiveis.value = r.transferiveis || []
    souDono.value = Boolean(r.sou_dono)
    souParticipante.value = Boolean(r.sou_participante)
    meuId.value = r.eu ?? null
  } catch {
    // A conversa abre mesmo se isto falhar: participante é informação a mais,
    // não pré-requisito para atender.
    acompanham.value = []
    convidaveis.value = []
  }
}

/* 🚨 UM CONVITE POR CHAMADA, DE PROPÓSITO. A rota `/convidar` é atômica por
   pessoa; mandar a lista inteira de uma vez faria três convites virarem uma
   transação só, e um nome inválido derrubaria os outros dois. Aqui cada um vai
   sozinho e o resultado é contado -- o parcial é dito, não escondido.

   ⚠️ NÃO SE ANUNCIA SUCESSO SEM CONTAR: "3 chamados" quando dois falharam é
   exatamente o tipo de tela que mente. */
async function convidar() {
  if (!convidados.value.length || mexendo.value) return
  mexendo.value = true
  erro.value = ''
  // 🚨 ACHADO EM 15/09, validando o relato da Aline sobre "Reabrir e
  // assumir": esta era a ÚNICA das quatro funções que usam `mexendo` sem
  // `try/finally` em volta do corpo inteiro. Uma exceção fora do laço (por
  // exemplo `aberta.value` virar null por troca de conversa no meio do
  // convite) travava `mexendo` em `true` para sempre -- e `confirmar()`
  // (usado pelo modal de "Reabrir e assumir", entre outros) recusa agir
  // enquanto `mexendo` estiver true. Não é a causa confirmada do caso dela,
  // mas é a única fresta real que achei lendo o código inteiro.
  try {
    const nomeDe = (id) => {
      const a = convidaveis.value.find((x) => String(x.id) === String(id))
      return a ? a.nome : `#${id}`
    }
    const entraram = []
    const falharam = []
    for (const id of convidados.value) {
      try {
        const r = await api.post(`/api/conversas/${aberta.value.id}/convidar`,
                                 { atendente_id: Number(id) })
        entraram.push(r.nome || nomeDe(id))
      } catch (e) {
        falharam.push(`${nomeDe(id)} (${e instanceof ErroDeApi ? e.message : 'falhou'})`)
      }
    }
    if (entraram.length) {
      recado.value = entraram.length === 1
        ? `${entraram[0]} foi chamado para a conversa.`
        : `${entraram.length} pessoas foram chamadas: ${entraram.join(', ')}.`
    }
    if (falharam.length) erro.value = `Não entrou: ${falharam.join(' · ')}`
    /* 🔵 23/09: o recado fica NA conversa, como nota interna. Só se alguém
       entrou: recado para ninguém seria uma nota falando de um convite que
       não aconteceu. Falhou a nota, o convite continua valendo e a tela diz. */
    const textoRecado = recadoConvite.value.trim()
    let gravouNota = false
    if (textoRecado && entraram.length) {
      try {
        await api.post(`/api/conversas/${aberta.value.id}/nota`, {
          texto: `Chamou ${entraram.join(', ')} para a conversa. Recado: ${textoRecado}`,
        })
        gravouNota = true
      } catch (e) {
        erro.value = [erro.value, 'O convite entrou, mas o recado não foi gravado.']
          .filter(Boolean).join(' ')
      }
    }
    convidados.value = []
    recadoConvite.value = ''
    painelAcao.value = ''
    await carregarParticipantes(aberta.value.id)
    if (gravouNota) await abrir(aberta.value.id)
  } finally {
    mexendo.value = false
  }
}

/* 🚨 SÓ QUEM ESTÁ NA CONVERSA AGE NELA. Até 12/08 qualquer atendente com a
   tela `ATD_1.2` encerrava, transferia e devolvia conversa alheia -- o
   `souDono || souParticipante` governava só o botão *Sair*. O usuário achou
   saindo de uma conversa e reabrindo pelo painel.

   ⚠️ Esconder o botão NÃO é a trava: a rota recusa com 409. Isto aqui é para
   a tela não oferecer o que vai ser negado. */
const posso = computed(() => souDono.value || souParticipante.value)

async function entrarNaConversa() {
  mexendo.value = true
  try {
    const r = await api.post(`/api/conversas/${aberta.value.id}/entrar`)
    recado.value = r.papel === 'dono'
      ? 'Você já responde por esta conversa.'
      : 'Você entrou — agora pode responder e agir nela.'
    await Promise.all([abrir(aberta.value.id), carregar({ silencioso: true })])
  } catch (e) {
    erro.value = e instanceof ErroDeApi ? e.message : 'Não consegui entrar.'
    relatarErroDeBotao('entrar_na_conversa', e, aberta.value?.id)
  } finally {
    mexendo.value = false
  }
}

async function sairDaConversa() {
  mexendo.value = true
  try {
    const r = await api.post(`/api/conversas/${aberta.value.id}/sair`)
    recado.value = r.para_fila
      ? 'Você saiu e a conversa voltou para a fila.'
      : (r.novo_dono_nome
          ? `Você saiu; ${r.novo_dono_nome} passou a responder pela conversa.`
          : 'Você saiu da conversa.')
    await Promise.all([abrir(aberta.value.id), carregar({ silencioso: true })])
  } catch (e) {
    erro.value = e instanceof ErroDeApi ? e.message : 'Não consegui sair.'
    relatarErroDeBotao('sair_da_conversa', e, aberta.value?.id)
  } finally {
    mexendo.value = false
  }
}

async function removerParticipante(id) {
  mexendo.value = true
  try {
    await api.post(`/api/conversas/${aberta.value.id}/remover/${id}`)
    await carregarParticipantes(aberta.value.id)
  } catch (e) {
    erro.value = e instanceof ErroDeApi ? e.message : 'Não consegui remover.'
    relatarErroDeBotao('remover_participante', e, aberta.value?.id)
  } finally {
    mexendo.value = false
  }
}

let timer = null

function contadorDaAba(valor) {
  if (valor === 'minhas') return notificacoes.abas.minhas || 0
  if (valor === 'time') return notificacoes.abas.time || 0
  return 0
}

const FILTROS = [
  { valor: 'todas', rotulo: 'Todas' },
  { valor: 'sem_dono', rotulo: 'Sem dono' },
  { valor: 'minhas', rotulo: 'Minhas' },
  /* 🔵 23/09 (pedido dele): as conversas SEM DONO dos times de que eu sou
     membro -- para onde vai o "transferir para o time". Antes disso a
     transferência para um time caía em "Sem dono" para todo mundo. */
  { valor: 'time', rotulo: 'Time' },
]

/* 🔵 25/09: *"o filtro dele pode ter filtro por times que a pessoa estiver
   inserida. Pois podem ter conversas do mesmo cliente com o time que ela
   esteja, daí saber de qual é pode ser bom."* O seletor só aparece para quem
   está em mais de um time -- com um só, não há o que escolher. Vazio = todos. */
const meusTimes = computed(() => times.value.filter((t) => t.sou_membro))
const timeDaAba = ref('')

/* ---- filtro por tipo de cadastro (pedido do usuário em 25/08) -----------
   🚨 "SEM CADASTRO" E "SEM IDENTIFICAÇÃO" SÃO COISAS DIFERENTES, e a ordem
   aqui reflete isso: `sem_cadastro` é a conversa que não tem contato nenhum
   na base (211 de 332, 64%); `sem_identificacao` é o flag da ficha de um
   contato que existe. Os dois ficam lado a lado de propósito -- juntá-los
   esconderia o caso majoritário. */
const TIPOS = [
  { valor: 'sem_cadastro', rotulo: 'Sem cadastro' },
  { valor: 'cliente', rotulo: 'Cliente' },
  { valor: 'fornecedor', rotulo: 'Fornecedor' },
  { valor: 'tecnico', rotulo: 'Técnico' },
  { valor: 'teste', rotulo: 'Teste' },
  { valor: 'sem_identificacao', rotulo: 'Sem identificação' },
]
const tiposMarcados = ref([])
const filtroAberto = ref(false)

/* 🔵 30/09, pedido dele: *"no botão filtro pode ter abas de opção como
   Contato; Grupos // Se é na conversa ou nome // e os tipos em um grupo de
   'Tipos' no filtro mesmo"*.

   🚨 É CHIP DE FILTRO, NÃO ABA. A migração 027 criou uma aba separada de grupo
   e a 028 desfez -- a decisão registrada no backend é "grupo e conversa direta
   na mesma lista, como no WhatsApp". Um chip estreita quando se pede; uma aba
   dividiria a lista por padrão e reabriria aquilo.

   ⚠️ `emMensagens` NASCE DESLIGADO (decisão dele em 30/09). O conteúdo das
   mensagens entrava SEMPRE, e era ele que trazia volume sem relação com o
   termo: "weso" devolvia 10, sendo 4 os grupos e 6 conversas que só
   mencionavam a palavra. */
const emNome = ref(true)
const emMensagens = ref(false)
const ESCOPOS = [
  { valor: 'tudo', rotulo: 'Tudo' },
  { valor: 'contatos', rotulo: 'Contatos' },
  { valor: 'grupos', rotulo: 'Grupos' },
]
const escopo = ref('tudo')

/* 🚨 OS DOIS NÃO PODEM FICAR DESLIGADOS. O backend cai no nome nesse caso,
   mas deixar a pessoa nesse estado é oferecer um botão que não faz nada:
   desmarcar o último religa o outro. */
function alternarOnde(qual) {
  if (qual === 'nome') emNome.value = !emNome.value
  else emMensagens.value = !emMensagens.value
  if (!emNome.value && !emMensagens.value) {
    if (qual === 'nome') emMensagens.value = true
    else emNome.value = true
  }
  carregar()
}

function trocarEscopo(valor) {
  escopo.value = valor
  carregar()
}

/* Quantos filtros estão desviando do padrão -- é o número no ícone. Função e
   não `computed` para não depender de import novo neste arquivo. */
function contaFiltros() {
  let n = tiposMarcados.value.length
  if (escopo.value !== 'tudo') n += 1
  if (emMensagens.value) n += 1
  if (!emNome.value) n += 1
  return n
}

/* O texto embaixo do campo: o ícone diz QUE há filtro, esta linha diz QUAL. */
function resumoFiltros() {
  const partes = []
  if (escopo.value !== 'tudo') {
    partes.push(ESCOPOS.find((e) => e.valor === escopo.value).rotulo)
  }
  if (!emNome.value) partes.push('sem nome')
  if (emMensagens.value) partes.push('nas mensagens')
  for (const t of TIPOS) {
    if (tiposMarcados.value.includes(t.valor)) partes.push(t.rotulo)
  }
  return partes.join(' · ')
}

function limparFiltros() {
  tiposMarcados.value = []
  escopo.value = 'tudo'
  emNome.value = true
  emMensagens.value = false
  carregar()
}

/* ⚠️ FECHA AO CLICAR FORA. Popover que só fecha no próprio botão fica aberto
   por cima da lista enquanto a pessoa tenta clicar numa conversa -- e ela
   clica duas vezes achando que a tela travou. */
function fecharFiltroSeForaDele(evento) {
  if (!filtroAberto.value) return
  if (!evento.target.closest('.filtro')) filtroAberto.value = false
}

function alternarTipo(valor) {
  tiposMarcados.value = tiposMarcados.value.includes(valor)
    ? tiposMarcados.value.filter((t) => t !== valor)
    : [...tiposMarcados.value, valor]
  carregar()
}

function parametros() {
  const p = new URLSearchParams()
  if (filtro.value === 'sem_dono') p.set('sem_dono', 'true')
  if (filtro.value === 'minhas') p.set('minhas', 'true')
  if (filtro.value === 'time') p.set('meus_times', 'true')
  if (filtro.value === 'time' && timeDaAba.value) p.set('time_id', timeDaAba.value)
  if (filtro.value === 'bloqueados') p.set('bloqueados', 'true')
  if (busca.value.trim()) p.set('busca', busca.value.trim())
  if (tiposMarcados.value.length) p.set('relacoes', tiposMarcados.value.join(','))
  /* 🔵 30/09: só vai o que DESVIA do padrão -- URL limpa diz o que foi pedido. */
  if (!emNome.value) p.set('em_nome', 'false')
  if (emMensagens.value) p.set('em_mensagens', 'true')
  if (escopo.value !== 'tudo') p.set('escopo', escopo.value)
  return p.toString()
}

/* ---- o botão `+`: falar primeiro com quem ainda não escreveu ------------
   🚨 Até 25/08 não havia caminho de saída: conversa só nascia quando CHEGAVA
   mensagem. Este painel é o único lugar do painel onde se escolhe para quem
   mandar. */
const novaAberta = ref(false)
const novoNumero = ref('')
/* 🟡 S15, 15/09: o mesmo `+` agora abre os dois destinos. */
const modoGrupo = ref(false)
const grupoNome = ref('')
const grupoNumeros = ref('')
const novoTexto = ref('')
const enviandoNova = ref(false)
const erroNova = ref('')
const semWhatsapp = ref(false)

function abrirNova() {
  novaAberta.value = true
  novoNumero.value = ''
  novoTexto.value = ''
  erroNova.value = ''
  semWhatsapp.value = false
}

async function criarGrupo() {
  enviandoNova.value = true
  erroNova.value = ''
  semWhatsapp.value = false
  try {
    // Vírgula, ponto-e-vírgula ou linha: quem cola de uma planilha não devia
    // precisar arrumar o separador à mão.
    const numeros = grupoNumeros.value
      .split(/[\n,;]+/).map((n) => n.trim()).filter(Boolean)
    const r = await api.post('/api/grupos', {
      nome: grupoNome.value.trim(),
      numeros,
    })
    recado.value = `Grupo "${grupoNome.value.trim()}" criado com `
      + `${numeros.length} participante(s). Ele aparece aqui quando alguém escrever.`
    novaAberta.value = false
    modoGrupo.value = false
    grupoNome.value = ''
    grupoNumeros.value = ''
    await carregar({ silencioso: true })
    return r
  } catch (e) {
    erroNova.value = e instanceof ErroDeApi ? e.message : 'Não consegui criar o grupo.'
    relatarErroDeBotao('criar_grupo', e)
  } finally {
    enviandoNova.value = false
  }
}

async function enviarNova() {
  enviandoNova.value = true
  erroNova.value = ''
  semWhatsapp.value = false
  try {
    const r = await api.post('/api/conversas/nova', {
      numero: novoNumero.value,
      texto: novoTexto.value,
    })
    novaAberta.value = false
    /* O recado diz o que aconteceu com a IDENTIFICAÇÃO, porque é o que
       diferencia este caminho: mandar para número desconhecido é normal. */
    recado.value = r.identificada
      ? `Enviado. Já vinculei a ${r.cliente_nome || r.contato_nome}.`
      : (r.nasceu
          ? 'Enviado. Este número não está no cadastro — dá para vincular pela ficha.'
          : 'Enviado na conversa que já estava aberta com este número.')
    await carregar()
    await abrir(r.conversa_id)
  } catch (e) {
    /* O backend devolve o corpo inteiro no 409: `sem_whatsapp` distingue "o
       número não existe no WhatsApp" de "falhou por outro motivo", e as duas
       pedem reação diferente de quem está na tela. */
    const detalhe = e instanceof ErroDeApi ? e.detalhe : null
    semWhatsapp.value = Boolean(detalhe && detalhe.sem_whatsapp)
    erroNova.value = (detalhe && detalhe.motivo)
      || (e instanceof ErroDeApi ? e.message : 'Não consegui enviar.')
    relatarErroDeBotao('iniciar_conversa', e)
  } finally {
    enviandoNova.value = false
  }
}

/* ⚠️ O GIRANDO SÓ APARECE DEPOIS DE 3 SEGUNDOS. Busca rápida — que é o caso
   normal, medido entre 5 e 30 ms — não pode piscar um indicador: pisca-pisca a
   cada tecla cansa mais do que espera nenhuma. Passando de 3 s, o silêncio é
   que vira problema: sem sinal, a pessoa acha que a tela travou e digita de
   novo. Decisão do usuário em 12/08. */
const DEMORA_ATE_AVISAR_MS = 3000
const buscaDemorada = ref(false)
let avisoDemora = null

async function carregar({ silencioso = false } = {}) {
  if (!silencioso) carregando.value = true
  clearTimeout(avisoDemora)
  avisoDemora = setTimeout(() => { buscaDemorada.value = true }, DEMORA_ATE_AVISAR_MS)
  try {
    const [conversas, r] = await Promise.all([
      api.get(`/api/conversas?${parametros()}`),
      api.get('/api/conversas/resumo'),
    ])
    lista.value = conversas
    resumo.value = r
    erro.value = ''
  } catch (e) {
    if (!silencioso) erro.value = e instanceof ErroDeApi ? e.message : 'Falha ao ler as conversas.'
  } finally {
    // 🚨 Apagar o aviso no `finally`, nunca no caminho de sucesso: busca que
    // FALHA depois de 4 s deixaria o girando na tela para sempre.
    clearTimeout(avisoDemora)
    buscaDemorada.value = false
    carregando.value = false
  }
}

async function abrir(id) {
  try {
    /* 🚨 O RASCUNHO DA CONVERSA ANTERIOR É GUARDADO AQUI, ANTES DE TROCAR --
       depois de `aberta.value` mudar não há mais de quem ele é. */
    guardarRascunho()
    // Solta o que a conversa anterior segurava ANTES de trocar: o object URL
    // da foto de outro cliente não tem por que continuar vivo.
    soltarMidias()
    // ⚠️ Modal aberto para a conversa anterior não pode sobreviver à troca:
    // ele confirmaria sobre a conversa nova com o texto da velha.
    painelAcao.value = ''
    confirmacao.value = null
    // Os modais novos também não atravessam a troca: o de Ligações mostraria
    // o telefone da conversa anterior, e o player ficaria tocando.
    fecharLigacoes()
    fecharOutros()
    limparPaineis()
    // 🚨 PELA MESMA RAZÃO, A MENÇÃO NÃO ATRAVESSA A TROCA. Um chamado montado
    // no grupo A seguiria para o grupo B — e lá o JID nem existe. A lista de
    // quem dá para chamar também é da conversa, e é remontada sob demanda.
    mencionados.value = []
    chamados.value = []
    listaArroba.value = []
    chamaveis.value = []
    chamaveisDaConversa = null
    chamaveisInternos.value = []
    internosDaConversa = null
    // A busca é DESTA conversa: carregá-la em outra mostraria contador e
    // marcações de um termo que ninguém procurou aqui.
    // ⚠️ E o CAMPO fecha junto (27/08): deixá-lo aberto na conversa nova
    // devolveria a faixa de altura que o botão existe para não gastar.
    fecharBusca()
    /* ⚠️ A citação é DESTA conversa. Sem limpar, a barra continuava apontando
       para uma mensagem da conversa anterior: o backend recusa ("só dá para
       citar mensagem desta conversa"), mas a tela mentia até a pessoa
       tentar. */
    citando.value = null
    editando.value = null
    // ⚠️ Os ACHADOS também. Antes de 25/08 eles eram `computed` e sumiam
    // sozinhos com o termo; agora são estado, e estado não se limpa sozinho --
    // sobrariam marcações de balão de outra conversa.
    achadosNaConversa.value = []
    achadosLimitados.value = false
    gaveta.value = false
    empresas.value = []
    empresasAbertas.value = false
    buscaCliente.value = ''
    achadosCliente.value = []
    aberta.value = await api.get(`/api/conversas/${id}`)
    /* 🔵 15/10: o compositor passa a nascer com o rascunho DESTA conversa --
       e vazio quando ela não tem nenhum, que é o que mata o vazamento de
       texto de uma conversa para a outra. */
    resposta.value = lerRascunho(id)
    // 🔵 07/10: semeia o rascunho do nome (editável quando o tipo for Lead).
    rascunhoNome.value = aberta.value?.empresa?.contato?.nome || ''
    carregarVinculoTecnico()
    nomeSalvo.value = false
    carregarParticipantes(id)
    carregarMidiasDaConversa(aberta.value)
    carregarFoto(aberta.value)
    rolarParaOFim()
    // 🔵 15/10, Bloco 12: quem olha as notas é remontado por conversa -- os
    // elementos são outros, e o que estava pendente vai antes de trocar.
    vistoAberto.value = null
    olharAsNotas()
    recado.value = ''
    /* ⚠️ Da LISTA para a conversa é `push`: no celular, o "voltar" do aparelho
       tem de trazer de volta à lista, não sair da tela. Entre conversas
       continua `replace`, para o histórico não virar uma fila de conversas. */
    if (route.params.id !== String(id)) {
      if (route.params.id) router.replace({ path: `/atendimento/${id}` })
      else router.push({ path: `/atendimento/${id}` })
    }
    acoesCelular.value = false
    outroAberto.value = false
    balaoTocado.value = null
  } catch (e) {
    erro.value = e instanceof ErroDeApi ? e.message : 'Falha ao abrir a conversa.'
  }
}

/* 🔵 Achado pelo relato do Rodrigo (15/09): o polling de 8s só atualizava a
   LISTA (`carregar`), nunca a conversa aberta -- a resposta do cliente
   aparecia na prévia do contato e nunca dentro da conversa que a pessoa
   estava lendo.

   ⚠️ NÃO REUSA `abrir()`. `abrir()` é troca de conversa: solta mídia, fecha
   busca, limpa citação e menção, zera a gaveta -- tudo isso ao vivo, a cada
   8s, apagaria o que a pessoa está fazendo na tela sem ela ter clicado em
   nada. Este refresh só toca no que pode ter mudado de fora: mensagem nova,
   tique de entrega/leitura, e quem é o dono da conversa.

   🚨 SÓ ADICIONA, NUNCA SUBSTITUI O ARRAY INTEIRO. Quem já clicou "carregar
   anteriores" tem mensagens mais antigas que a janela desta rota não traz de
   volta -- substituir apagaria da tela o que a pessoa acabou de pedir pra
   ver. */
async function atualizarMensagens() {
  if (!aberta.value) return
  try {
    /* 🔵 25/09 (*"Só marca lida vista"*): com a aba escondida, reler NÃO marca
       como lida. Antes marcava, e a mensagem que chegava com a pessoa fora da
       tela virava "lida" em até 8 s -- sem tocar, sem bolinha. */
    const ler = document.hidden ? 'false' : 'true'
    const r = await api.get(`/api/conversas/${aberta.value.id}?ler=${ler}`)
    aberta.value.estado = r.estado
    aberta.value.atendente_id = r.atendente_id
    aberta.value.atendente_nome = r.atendente_nome
    aberta.value.atendente_estado = r.atendente_estado
    aberta.value.bloqueio = r.bloqueio

    const porId = new Map(r.mensagens.map((m) => [m.id, m]))
    for (const m of aberta.value.mensagens) {
      const fresca = porId.get(m.id)
      if (fresca && fresca.entrega !== m.entrega) m.entrega = fresca.entrega
    }

    const idsAtuais = new Set(aberta.value.mensagens.map((m) => m.id))
    const novas = r.mensagens.filter((m) => !idsAtuais.has(m.id))
    if (!novas.length) return

    // Só rola pro fim se a pessoa já estava lá -- senão puxaria quem está
    // lendo mensagem antiga pro meio de uma resposta que acabou de chegar.
    const noFim = baloes.value
      ? baloes.value.scrollTop + baloes.value.clientHeight >= baloes.value.scrollHeight - 40
      : true
    aberta.value.mensagens.push(...novas)
    /* 🚨 A MÍDIA DA MENSAGEM NOVA TAMBÉM PRECISA SER BUSCADA. Sem esta linha,
       imagem/áudio/vídeo que chega com a conversa ABERTA aparece como botão
       "Baixar" em vez de aparecer no balão -- e só se conserta fechando e
       reabrindo a conversa. Quem abre a conversa pra conferir nunca vê o
       defeito (o `abrir()` carrega tudo); quem fica atendendo o dia todo vê
       sempre. Foi um defeito MEU, introduzido junto com este refresh em
       15/09, e achado pelo usuário no mesmo dia. */
    carregarMidiasDaConversa({ mensagens: novas })
    if (noFim) rolarParaOFim()
  } catch {
    // silencioso -- é atualização de fundo, não a abertura da conversa
  }
}

/* Assumir serve os dois casos: conversa na fila e conversa ENCERRADA.
   Encerrada, assumir REABRE -- antes de 12/08 encerrar era porta só de ida e o
   único jeito de voltar a falar era o cliente escrever primeiro. */
async function assumir(id = null) {
  const alvo = id || aberta.value.id
  try {
    const r = await api.post(`/api/conversas/${alvo}/assumir`)
    recado.value = r.reaberta
      ? 'Conversa reaberta — você passou a responder por ela.'
      : 'Conversa assumida.'
    await Promise.all([abrir(alvo), carregar({ silencioso: true })])
  } catch (e) {
    // 🚨 409 aqui é o caso projetado: outra pessoa clicou primeiro, ou este
    // número já tem outra conversa aberta e é nela que a resposta chega.
    erro.value = e instanceof ErroDeApi ? e.message : 'Não consegui assumir.'
    relatarErroDeBotao('assumir', e, alvo)
  }
}

/* Pela lista: sem dono assume direto; encerrada abre a conversa ANTES de
   perguntar, para quem vai reabrir ver do que se trata em vez de confirmar às
   cegas a partir de uma linha de lista. */
async function assumirDaLista(c) {
  if (c.estado !== 'resolvida') {
    await assumir(c.id)
    return
  }
  await abrir(c.id)
  if (aberta.value && aberta.value.estado === 'resolvida') pedirParaAssumir()
}

/* 🔵 30/09 (pedido dele): numa conversa EM ANDAMENTO com dono, a linha oferece
   "Entrar" em vez de "assumir". Entrar é acompanhar -- quem responde continua
   sendo o dono --, e o mecanismo (migração 021) já existia; faltava a porta na
   lista. Abre a conversa e reusa o `entrarNaConversa` já testado de dentro
   dela, em vez de duplicar a chamada. */
async function entrarDaLista(c) {
  await abrir(c.id)
  if (aberta.value) await entrarNaConversa()
}

function pedirParaAssumir() {
  if (aberta.value.estado === 'resolvida') {
    perguntar(
      'Reabrir esta conversa?',
      'Ela sai do Histórico, volta para o WhatsApp e você passa a '
        + 'responder por ela. O tempo de atendimento é recontado no próximo '
        + 'encerramento.',
      'Reabrir e assumir',
      () => assumir(),
    )
    return
  }
  assumir()
}

const exigeComentario = computed(() => {
  const c = classificacoes.value.find((x) => String(x.id) === String(classificacaoEscolhida.value))
  return Boolean(c && c.exige_comentario)
})

/* O destino vem do BOTÃO clicado, não de um estado guardado. Era `modoNota`,
   um seletor invisível que o usuário não conseguia entender -- e com razão. */
const ocupado = computed(() => enviando.value || enviandoArquivo.value)
const temAlgoParaEnviar = computed(
  () => arquivos.value.length > 0 || Boolean(resposta.value.trim()),
)

async function enviar(interna = false) {
  const texto = resposta.value.trim()
  if (!texto || ocupado.value) return
  enviando.value = true
  erro.value = ''
  recado.value = ''
  const caminho = interna ? 'nota' : 'responder'
  try {
    /* ⚠️ Só vai quem AINDA está escrito: apagar o "@Fulano" à mão tem de
       desfazer a menção, senão a pessoa é chamada sem aparecer nada.
       ⚠️ Nota interna não chama ninguém: ela nunca vai ao WhatsApp. */
    const vivos = interna ? []
      : mencionados.value.filter((p) => texto.includes('@' + p.nome))
    /* ⚠️ Mesma regra para os chamados da nota: só vai quem AINDA está
       escrito. Apagar o "@Fulano" à mão desfaz o chamado. */
    const chamadosVivos = chamados.value.filter(
      (p) => texto.includes('@' + p.nome))
    /* 🚨 O DESTINO VEM DO BOTÃO, e o `@` da conversa direta é interno: se
       este texto vai AO CLIENTE, o "@Fulano" segue como texto comum e ninguém
       é chamado. A tela avisa antes em vez de deixar a pessoa achar que
       avisou o colega. */
    if (!interna && chamadosVivos.length) {
      const nomes = chamadosVivos.map((p) => p.nome).join(', ')
      if (!window.confirm(
        `Esta mensagem vai PARA O CLIENTE, e ${nomes} não será chamado: `
        + 'o nome segue como texto comum.\n\nOK envia ao cliente. Cancelar '
        + 'volta — use "Nota interna" para chamar alguem nosso.')) {
        enviando.value = false
        return
      }
    }
    const resultado = await api.post(
      `/api/conversas/${aberta.value.id}/${caminho}`, {
        texto,
        /* ⚠️ Nota interna não cita: ela nunca foi ao WhatsApp, e o backend
           recusaria a chave. */
        ...(interna
          ? { chamar: chamadosVivos.map((p) => p.id) }
          : { citando_id: citando.value?.id ?? null,
              mencionados: vivos.map((p) => p.jid) }),
      })
    resposta.value = ''
    apagarRascunho()
    citando.value = null
    mencionados.value = []
    chamados.value = []
    listaArroba.value = []
    // 🚨 Recarrega a conversa em vez de empurrar o balão na mão: o que vale é
    // o que o banco gravou, não o que a tela supõe ter acontecido.
    await Promise.all([abrir(aberta.value.id), carregar({ silencioso: true })])
    /* 🚨 QUEM FOI AVISADO É O QUE O BACKEND DIZ, não o que a tela pediu: o
       aviso pelo chat pode falhar sem derrubar a nota, e a tela tem de contar
       isso em vez de deixar quem escreveu achando que chamou alguem.

       ⚠️ DEPOIS DO RECARREGAR, nunca antes: `abrir()` limpa o recado, e um
       recado posto antes dele morria sem aparecer -- achado pelo teste. */
    if (interna && resultado?.avisos_falhos?.length) {
      recado.value = 'A nota ficou gravada, mas não consegui avisar '
        + resultado.avisos_falhos.map((f) => f.nome).join(', ')
        + ' pelo chat interno.'
    } else if (interna && resultado?.chamados?.length) {
      recado.value = 'Avisei '
        + resultado.chamados.map((c) => c.nome).join(', ')
        + ' pelo chat interno.'
    }
  } catch (e) {
    erro.value = e instanceof ErroDeApi ? e.message : 'Não consegui enviar.'
    relatarErroDeBotao('enviar_mensagem', e, aberta.value?.id)
  } finally {
    enviando.value = false
  }
}

/* ---- chamar alguém com @, só em grupo (27/08) ----------------------------
   Pedido do usuário: *"interessante e pode ser, tanto no interno quanto no do
   whatsa"*.

   🚨 SÓ EM GRUPO, e não é escolha de tela: fora dele o WhatsApp IGNORA
   `mentioned`. Oferecer o `@` numa conversa direta seria prometer o que o
   outro lado não faz — o backend também não repassa.

   🚨 A LISTA VEM DO EVOLUTION, ao vivo, e é pedida quando alguém digita `@` —
   nunca por mensagem. É a única fonte que liga o `@lid` do participante a um
   telefone: desde que o WhatsApp passou a usar LID nos grupos, TODOS os
   remetentes que gravamos estão nesse formato. */
const campoResposta = ref(null)

/* 🔵 25/09 (Plano 3): a mensagem rápida entra NO CURSOR, e o que já estava
   escrito fica. `{cliente}` é a empresa; `{contato}`, a pessoa -- sem nome
   no cadastro, vale o nome do WhatsApp. */
const dadosDaConversa = computed(() => ({
  cliente: aberta.value?.cliente_nome || '',
  contato: aberta.value?.contato_nome || aberta.value?.nome_whatsapp || '',
}))
function inserirNoCampo(texto) {
  const el = campoResposta.value
  const atual = resposta.value || ''
  const ini = el?.selectionStart ?? atual.length
  const fim = el?.selectionEnd ?? atual.length
  resposta.value = atual.slice(0, ini) + texto + atual.slice(fim)
  nextTick(() => {
    el?.focus()
    el?.setSelectionRange(ini + texto.length, ini + texto.length)
  })
}
/* 🔵 19/10, decisão 4 dele: o botão Agendar fica JUNTO de anexo, áudio e
   mensagem rápida -- atrás do `+`. O modal é componente à parte: esta tela já
   tem mais de seis mil linhas, e três abas novas aqui dentro seriam três
   coisas a mais para quebrar junto. */
const agendarAberto = ref(false)

/* 🔵 21/10, pedido dele: o contato tipo técnico mostra a qual cadastro do
   Harmonit ele está ligado -- *"já traz sugerido pelo número dele"*. A
   varredura diária mantém os automáticos; o que for escolhido aqui vira
   MANUAL e ela não encosta mais (é assim que a exceção do 6168 → Iago vive).

   ⚠️ A ficha não depende disto responder: vínculo é informação a mais, e
   falha aqui não pode esvaziar a ficha inteira. */
const vinculoTecnico = ref(null)
const vinculoCarregando = ref(false)
const buscaTecnico = ref('')
const tecnicosAchados = ref([])
const vinculoErro = ref('')

async function carregarVinculoTecnico() {
  vinculoTecnico.value = null
  vinculoErro.value = ''
  const contatoId = aberta.value?.empresa?.contato?.id
  if (!contatoId || !ehTecnico.value) return
  vinculoCarregando.value = true
  try {
    vinculoTecnico.value = await api.get(`/api/contatos/${contatoId}/tecnico-harmonit`)
  } catch (e) {
    vinculoErro.value = e instanceof ErroDeApi ? e.message : 'Não consegui ler o vínculo.'
  } finally {
    vinculoCarregando.value = false
  }
}

async function buscarTecnicoHarmonit() {
  vinculoErro.value = ''
  try {
    const r = await api.get(
      `/api/tecnicos-harmonit?busca=${encodeURIComponent(buscaTecnico.value)}`)
    tecnicosAchados.value = r.tecnicos || []
  } catch (e) {
    vinculoErro.value = e instanceof ErroDeApi ? e.message : 'Não consegui buscar.'
  }
}

async function definirVinculoTecnico(tecnicoId) {
  const contatoId = aberta.value?.empresa?.contato?.id
  if (!contatoId) return
  vinculoCarregando.value = true
  vinculoErro.value = ''
  try {
    // Relê o que ficou gravado, em vez de montar a resposta aqui.
    vinculoTecnico.value = await api.put(
      `/api/contatos/${contatoId}/tecnico-harmonit`,
      { harmonit_tecnico_id: tecnicoId })
    tecnicosAchados.value = []
    buscaTecnico.value = ''
  } catch (e) {
    vinculoErro.value = e instanceof ErroDeApi ? e.message : 'Não consegui gravar.'
  } finally {
    vinculoCarregando.value = false
  }
}

/* O cliente da conversa, quando há. ⚠️ Em conversa de TÉCNICO isto é vazio
   por desenho (decisão dele de 07/10): `contato.cliente_id` é NULL, e a
   escolha do cliente é feita dentro do modal, com busca própria. */
const clienteHarmonitDaConversa = computed(
  () => aberta.value?.empresa?.cliente?.harmonit_id || '')
const clienteNomeDaConversa = computed(
  () => aberta.value?.empresa?.cliente?.nome || '')

const mencionados = ref([])
/* 🚨 OS CHAMADOS DA NOTA SÃO OUTRA LISTA, e não a mesma com um campo a
   mais: `mencionados` guarda participante de WhatsApp (JID, só vale em grupo)
   e `chamados` guarda ATENDENTE (id), que é quem uma nota interna chama. Uma
   lista só acabaria mandando id onde o backend espera JID. */
const chamados = ref([])
const chamaveisInternos = ref([])
let internosDaConversa = null
const listaArroba = ref([])
const arrobaEscolhido = ref(0)
const chamaveis = ref([])
let chamaveisDaConversa = null   // de qual conversa a lista carregada é
let inicioDaArroba = -1

async function carregarChamaveis() {
  const id = aberta.value?.id
  if (!id || !ehGrupo.value) { chamaveis.value = []; return }
  if (chamaveisDaConversa === id) return
  try {
    const r = await api.get(`/api/conversas/${id}/quem-chamar`)
    chamaveis.value = r.participantes || []
    chamaveisDaConversa = id
  } catch {
    // Não pode derrubar a conversa: sem a lista, escreve-se normalmente.
    chamaveis.value = []
  }
}

/* 🔵 21/10 — quem a NOTA pode chamar: o dono e os convidados ativos desta
   conversa, pela mesma régua de quem pode agir nela. Cacheada por conversa,
   como a do grupo. */
async function carregarInternos() {
  const id = aberta.value?.id
  if (!id) { chamaveisInternos.value = []; return }
  if (internosDaConversa === id) return
  try {
    const r = await api.get(`/api/conversas/${id}/quem-chamar-interno`)
    chamaveisInternos.value = r.pessoas || []
    internosDaConversa = id
  } catch {
    // Igual à do grupo: sem a lista, escreve-se normalmente.
    chamaveisInternos.value = []
  }
}

async function olharArroba(evento) {
  const campo = evento.target
  const ate = campo.value.slice(0, campo.selectionStart)
  const at = ate.lastIndexOf('@')
  // ⚠️ `@` colado em palavra não abre a lista: "email@movisat" não é menção.
  const antes = at > 0 ? ate[at - 1] : ' '
  if (at === -1 || !/\s/.test(antes)) { listaArroba.value = []; return }
  const termo = ate.slice(at + 1)
  if (/\s{2,}|\n/.test(termo)) { listaArroba.value = []; return }

  /* 🚨 DOIS UNIVERSOS, A MESMA TECLA (21/10, decisão dele). Em GRUPO o `@`
     chama participante do WhatsApp, que é o que o `mentioned` notifica. Em
     conversa DIRETA o WhatsApp ignora menção -- então ali o `@` chama GENTE
     NOSSA, e o aviso sai pelo chat interno. Nunca os dois juntos: cliente não
     se chama em nota, e colega não se chama em grupo do WhatsApp. */
  if (ehGrupo.value) await carregarChamaveis()
  else await carregarInternos()
  inicioDaArroba = at
  const busca = termo.trim().toLowerCase()
  const fonte = ehGrupo.value ? chamaveis.value : chamaveisInternos.value
  const jaEscolhido = (p) => (ehGrupo.value
    ? mencionados.value.some((m) => m.jid === p.jid)
    : chamados.value.some((m) => m.id === p.id))
  listaArroba.value = fonte
    .filter((p) => !jaEscolhido(p))
    .filter((p) => !busca || (p.nome || '').toLowerCase().includes(busca))
    .slice(0, 8)
  arrobaEscolhido.value = 0
}

/* ⚠️ Só rouba as setas quando a lista está aberta. Sem isto, subir e descer no
   texto deixaria de funcionar em toda conversa de grupo. */
function andarNaLista(passo, evento) {
  if (!listaArroba.value.length) return
  evento.preventDefault()
  const n = listaArroba.value.length
  arrobaEscolhido.value = (arrobaEscolhido.value + passo + n) % n
}

/* Enter escolhe da lista quando ela está aberta. Fora disso NÃO envia: aqui a
   mensagem vai para o cliente e não volta, e o envio é `Ctrl+Enter` desde
   sempre — a fricção se paga. */
function enterNoCompositor(evento) {
  /* A lista de menção `@` aberta ganha do resto: Enter ali escolhe a pessoa,
     e é o que a pessoa acabou de pedir ao digitar `@`. */
  if (listaArroba.value.length) {
    evento.preventDefault()
    escolherArroba(listaArroba.value[arrobaEscolhido.value])
    return
  }
  // 🟢 Só para quem LIGOU (Erika, 15/09). Desligado, Enter segue quebrando
  // linha -- o `.exact` do template garante que Shift+Enter nunca cai aqui.
  if (enterEnvia.value) {
    evento.preventDefault()
    enviar()
  }
}

function escolherArroba(pessoa) {
  if (!pessoa) return
  const campo = campoResposta.value
  const fim = campo ? campo.selectionStart : resposta.value.length
  if (inicioDaArroba >= 0) {
    resposta.value = resposta.value.slice(0, inicioDaArroba)
      + '@' + pessoa.nome + ' ' + resposta.value.slice(fim)
  }
  /* O destino do escolhido é o universo da conversa, nunca o campo que a
     pessoa tem: participante de grupo tem `jid`, atendente tem `id`. */
  if (ehGrupo.value) {
    if (!mencionados.value.some((m) => m.jid === pessoa.jid)) {
      mencionados.value.push(pessoa)
    }
  } else if (!chamados.value.some((m) => m.id === pessoa.id)) {
    chamados.value.push(pessoa)
  }
  listaArroba.value = []
  inicioDaArroba = -1
  nextTick(() => campo && campo.focus())
}

function tirarMencionado(jid) {
  mencionados.value = mencionados.value.filter((m) => m.jid !== jid)
}

function tirarChamado(id) {
  chamados.value = chamados.value.filter((m) => m.id !== id)
}

/* ---- envio de arquivo ----------------------------------------------------
   🚨 O DESTINATÁRIO NÃO É ESCOLHIDO AQUI. A rota tira o número da conversa;
   o formulário manda só o arquivo e a legenda.

   ⚠️ NÃO passa pelo `api.post`, que serializa JSON. Arquivo vai por
   `FormData`, e nesse caso o navegador precisa montar o `Content-Type` com o
   boundary sozinho -- definir o cabeçalho na mão quebra o upload em silêncio,
   com o servidor recebendo corpo vazio. */
/* 25 MB é decisão do usuário (12/08). Eu tinha posto 16 por conta própria, e
   teto é decisão dele -- regra que ele já tinha dado no dia anterior. */
const TETO_ARQUIVO_MB = 25
/* 🔵 25/09: até 10 anexos por envio, texto só na primeira mensagem. */
const TETO_ANEXOS = 10
const arquivos = ref([])   // File[], até TETO_ANEXOS
const enviandoArquivo = ref(false)

/* ---- colar print (Ctrl+V) ------------------------------------------------
   🚨 O CAMINHO MAIS CURTO PARA MANDAR UM PRINT. Sem isto, quem tira print
   precisa salvar em arquivo, achar a pasta e anexar -- três passos para o que
   o WhatsApp resolve com um. */
function colar(evento) {
  const itens = Array.from(evento.clipboardData?.items || [])
  const imagem = itens.find((i) => i.type.startsWith('image/'))
  if (!imagem) return          // colar texto continua sendo colar texto
  const arq = imagem.getAsFile()
  if (!arq) return
  if (arquivos.value.length >= TETO_ANEXOS) {
    erro.value = `Já tem ${TETO_ANEXOS} arquivos — o máximo por envio.`
    return
  }
  evento.preventDefault()
  /* Nome com hora: "image.png" três vezes na conversa não distingue nada. */
  const carimbo = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
  arquivos.value = [...arquivos.value,
    new File([arq], `print-${carimbo}.png`, { type: arq.type })]
}

/* 🔵 25/09: até 10 anexos por envio. O backend não trava anexo por
   mensagem (cada `POST .../arquivo` já é independente) -- então isto é
   mudança só de frontend, com um laço sequencial no envio. */
function escolherArquivo(evento) {
  const novos = Array.from(evento.target.files || [])
  erro.value = ''
  const cabem = TETO_ANEXOS - arquivos.value.length
  if (novos.length > cabem) {
    erro.value = cabem > 0
      ? `Dá para mandar no máximo ${TETO_ANEXOS} arquivos por envio — cabem mais ${cabem}.`
      : `Já tem ${TETO_ANEXOS} arquivos — o máximo por envio.`
  }
  const aceitos = []
  for (const f of novos.slice(0, Math.max(cabem, 0))) {
    if (f.size > TETO_ARQUIVO_MB * 1024 * 1024) {
      // Barra aqui também, além do servidor: subir 40 MB para levar 413 no
      // fim é desperdício de tempo de quem está atendendo.
      // 🚨 SEM O NOME DO ARQUIVO NO AVISO: ele não deve aparecer em lugar
      // nenhum da tela para um arquivo que foi recusado e não entrou.
      erro.value = `O arquivo tem ${(f.size / 1024 / 1024).toFixed(1)} MB e o `
        + `teto é ${TETO_ARQUIVO_MB} MB.`
      continue
    }
    aceitos.push(f)
  }
  arquivos.value = [...arquivos.value, ...aceitos]
  evento.target.value = ''   // deixa escolher o mesmo arquivo de novo depois
}

function limparArquivo(indice) {
  arquivos.value = arquivos.value.filter((_, i) => i !== indice)
}

function limparArquivos() {
  arquivos.value = []
  const campo = document.getElementById('campo-arquivo')
  if (campo) campo.value = ''
}

async function enviarUmArquivo(arq, legenda, interna) {
  const forma = new FormData()
  forma.append('arquivo', arq)
  forma.append('legenda', legenda)
  // Anexo vale nos dois destinos: como nota, o arquivo é guardado na
  // conversa e não sai para o cliente.
  forma.append('interna', interna ? 'true' : 'false')
  const r = await fetch(`/api/conversas/${aberta.value.id}/arquivo`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('movizap.token')}` },
    body: forma,
  })
  // 🚨 Não confiar no código: ler o corpo e decidir por ele.
  const texto = await r.text()
  let corpo = null
  try { corpo = JSON.parse(texto) } catch { corpo = null }
  if (!r.ok) {
    throw new Error((corpo && corpo.detail) || `Falha ao enviar (${r.status}).`)
  }
}

async function enviarArquivo(interna = false) {
  if (!arquivos.value.length || ocupado.value) return
  enviandoArquivo.value = true
  erro.value = ''
  recado.value = ''
  const total = arquivos.value.length
  let enviados = 0
  try {
    // 🔵 25/09: texto só na 1ª mensagem, os demais anexos vão sem legenda.
    // Sequencial, não em paralelo -- preserva a ordem dos balões e não
    // estoura conexão simultânea com a Evolution.
    for (const arq of arquivos.value) {
      await enviarUmArquivo(arq, enviados === 0 ? resposta.value.trim() : '', interna)
      enviados++
    }
    limparArquivos()
    resposta.value = ''
    apagarRascunho()
    recado.value = total > 1 ? `${enviados} arquivos enviados.` : 'Arquivo enviado.'
    await Promise.all([abrir(aberta.value.id), carregar({ silencioso: true })])
  } catch (e) {
    // 🔵 28/09 (minha decisão de engenharia, documentada no plano): se um
    // arquivo do meio falhar, PARA -- não desfaz o que já chegou ao
    // cliente, nem segue tentando os demais sem avisar.
    arquivos.value = arquivos.value.slice(enviados)
    erro.value = (enviados > 0
        ? `${enviados} de ${total} enviados; parei no que falhou: `
        : '') + (e.message || 'Não consegui enviar o arquivo.')
    relatarErroDeBotao('enviar_arquivo', e, aberta.value?.id)
    if (enviados > 0) {
      resposta.value = ''
      apagarRascunho()
      await Promise.all([abrir(aberta.value.id), carregar({ silencioso: true })])
    }
  } finally {
    enviandoArquivo.value = false
  }
}

function tamanhoDoArquivo(f) {
  if (!f) return ''
  return f.size < 1024 * 1024
    ? `${Math.round(f.size / 1024)} KB`
    : `${(f.size / 1024 / 1024).toFixed(1)} MB`
}

const destinoOffline = computed(() => {
  if (!atendenteEscolhido.value) return false
  const escolhido = transferiveis.value.find((a) => a.id === Number(atendenteEscolhido.value))
  return escolhido?.estado === 'offline'
})

async function transferir() {
  try {
    const paraPessoa = Number(atendenteEscolhido.value) || null
    await api.post(`/api/conversas/${aberta.value.id}/transferir`, {
      time_id: paraPessoa ? null : (Number(timeEscolhido.value) || null),
      para_atendente_id: paraPessoa,
      observacao: motivo.value || null,
    })
    /* O recado muda porque a consequência muda: time tira o dono e volta pra
       fila; pessoa já entrega com dono. É o que a própria função do backend
       diz na docstring, e o atendente precisa saber qual dos dois fez. */
    recado.value = paraPessoa
      ? `Transferida — ${transferiveis.value.find((a) => a.id === paraPessoa)?.nome
          || 'a pessoa escolhida'} passou a responder por ela.`
      : 'Transferida — a conversa foi para a fila desse time.'
    painelAcao.value = ''
    timeEscolhido.value = ''
    atendenteEscolhido.value = ''
    motivo.value = ''
    await Promise.all([abrir(aberta.value.id), carregar({ silencioso: true })])
  } catch (e) {
    erro.value = e instanceof ErroDeApi ? e.message : 'Não consegui transferir.'
    relatarErroDeBotao('transferir', e, aberta.value?.id)
  }
}

async function devolver() {
  try {
    await api.post(`/api/conversas/${aberta.value.id}/devolver`)
    recado.value = 'Devolvida para a fila.'
    await Promise.all([abrir(aberta.value.id), carregar({ silencioso: true })])
  } catch (e) {
    erro.value = e instanceof ErroDeApi ? e.message : 'Não consegui devolver.'
    relatarErroDeBotao('devolver', e, aberta.value?.id)
  }
}

function pedirParaDevolver() {
  perguntar(
    'Devolver à fila?',
    `A conversa fica sem dono e volta para a fila, onde qualquer atendente pode `
      + `assumir. Você deixa de ser responsável por ela. O cliente não é avisado.`,
    'Devolver à fila',
    devolver,
  )
}

function pedirParaSair() {
  /* ⚠️ O texto muda conforme o papel, porque a consequência muda: dono que sai
     PASSA A POSSE (ou joga na fila); participante que sai só deixa de ver. */
  const texto = souDono.value
    ? (acompanham.value.length
        ? 'Você é quem responde por ela. Ao sair, a posse passa para quem está '
          + 'acompanhando há mais tempo.'
        : 'Você é quem responde por ela e não há mais ninguém acompanhando — '
          + 'ao sair, a conversa volta para a fila.')
    : 'Ela deixa de aparecer na sua lista. Quem responde pela conversa não muda.'
  perguntar('Sair da conversa?', texto, 'Sair da conversa', sairDaConversa)
}

/* CONCLUIR ATENDIMENTO — era "Encerrar" até 25/08.
   ⚠️ A rota continua `/encerrar`: rótulo é da tela, e trocar o caminho
   derrubaria quem estivesse com o painel aberto no meio do deploy. */
async function encerrar() {
  try {
    const r = await api.post(`/api/conversas/${aberta.value.id}/encerrar`, {
      // 🚨 `Number('')` é 0, e 0 não é "sem classificação" -- é um id que
      // não existe. Classificar virou opcional em 11/08, então o que vai é
      // null quando ninguém escolheu.
      classificacao_id: classificacaoEscolhida.value
        ? Number(classificacaoEscolhida.value)
        : null,
      comentario: comentario.value || null,
    })
    // O recado diz o que MUDOU, não que "deu certo": a conversa saiu da sua
    // lista e voltou para "sem dono", e isso surpreende quem não esperava.
    recado.value = r.participantes_saidos
      ? `Atendimento concluído. A conversa voltou para "sem dono" e saiu da `
        + `lista de ${r.participantes_saidos} pessoa(s) que acompanhavam.`
      : 'Atendimento concluído. A conversa voltou para "sem dono" e está no Histórico.'
    painelAcao.value = ''
    classificacaoEscolhida.value = ''
    comentario.value = ''
    await Promise.all([abrir(aberta.value.id), carregar({ silencioso: true })])
  } catch (e) {
    erro.value = e instanceof ErroDeApi
      ? e.message
      : 'Não consegui concluir o atendimento.'
    relatarErroDeBotao('concluir', e, aberta.value?.id)
  }
}

/* 🚨 ATALHOS (28/08). O E-mail tinha 6 teclas e as ensinava no ícone de
   ajuda; esta tela, que é a mais usada, tinha ZERO.

   ⚠️ NUNCA DENTRO DE CAMPO DE TEXTO — mesma guarda do E-mail. Sem ela,
   escrever "javali" para o cliente pularia de conversa no meio da palavra.
   `Ctrl`/`Cmd`/`Alt` também saem: são atalhos do navegador.

   ⚠️ Nenhuma tecla DESTRÓI. `c` abre o modal de concluir, não conclui — a
   confirmação continua sendo o que decide. */

/* 🚨 OS ATALHOS SÓ EXISTEM SE A PESSOA OS LIGOU (28/08). Ele pediu a tela de
   Configurações com o interruptor DESLIGADO -- e desligado é o padrão do
   banco, não uma opção que eu escolhi aqui.

   ⚠️ Começa FALSO e só vira verdadeiro quando `/api/eu/atalhos` responder:
   entre montar a tela e a resposta chegar, nenhuma tecla age. Se a chamada
   falhar, continua falso -- o lado seguro é o teclado inerte. */
const atalhosLigados = ref(false)
/* 🟢 Erika (15/09). Desligado, Enter quebra linha e `Ctrl+Enter` envia --
   o comportamento de sempre. Ligado, inverte: Enter envia e `Shift+Enter`
   quebra linha, que é o que ela conhece do WhatsApp. */
const enterEnvia = ref(false)
const atalhosTeclas = ref({})

function tecla(acao) {
  return atalhosTeclas.value[acao]
}

async function carregarAtalhos() {
  try {
    const r = await api.get('/api/eu/atalhos')
    atalhosTeclas.value = r.teclas || {}
    atalhosLigados.value = Boolean(r.ligados)
    enterEnvia.value = Boolean(r.enviar_com_enter)
  } catch {
    atalhosLigados.value = false
    enterEnvia.value = false
  }
}

/* 🟡 S18, feito em 15/09: os sete modais da conversa fechavam só no clique
   fora ou no botão Cancelar -- nenhum escutava Esc.

   🚨 NÃO DÁ PRA PENDURAR NO `atalho()` ABAIXO: aquele é opt-in por pessoa
   (CFG_6.1, nasce desligado) e sai cedo quando há painel aberto, que é
   exatamente quando o Esc importa. E não dá pra pôr `@keydown.esc` no `div`
   do modal: `div` não recebe foco, então o evento nunca chegaria nele.

   ⚠️ FECHA O DE CIMA PRIMEIRO. Com confirmação aberta sobre um painel, Esc
   tem de desfazer a pergunta, não sumir com os dois. */
function fecharComEsc(evento) {
  if (evento.key !== 'Escape') return
  if (confirmacao.value) { confirmacao.value = null; return }
  // Modal fica ACIMA da gaveta, então Esc fecha o modal primeiro.
  if (ligacoesAbertas.value) { fecharLigacoes(); return }
  if (outrosAbertos.value) { fecharOutros(); return }
  if (emTelaCheia.value) { emTelaCheia.value = null; return }
  if (encaminhando.value) { encaminhando.value = null; return }
  if (novaAberta.value) { novaAberta.value = false; return }
  if (painelAcao.value) { fecharPainel() }
}

function atalho(evento) {
  const alvo = evento.target
  const digitando = alvo?.isContentEditable
    || ['INPUT', 'TEXTAREA', 'SELECT'].includes(alvo?.tagName)
  if (!atalhosLigados.value) return
  if (digitando || evento.ctrlKey || evento.metaKey || evento.altKey) return
  if (painelAcao.value || confirmacao.value || novaAberta.value) return

  if (evento.key === tecla('buscar')) {
    evento.preventDefault()
    document.querySelector('.campo--busca input')?.focus()
    return
  }
  if (evento.key === tecla('proxima') || evento.key === tecla('anterior')) {
    evento.preventDefault()
    const ordem = lista.value
    const atual = ordem.findIndex((c) => aberta.value && c.id === aberta.value.id)
    const passo = evento.key === tecla('proxima') ? 1 : -1
    const proximo = ordem[Math.min(Math.max(atual + passo, 0), ordem.length - 1)]
    if (proximo && (!aberta.value || proximo.id !== aberta.value.id)) abrir(proximo.id)
    return
  }
  if (!aberta.value) return
  if (evento.key === tecla('assumir') && (!aberta.value.atendente_id
      || aberta.value.estado === 'resolvida')) {
    evento.preventDefault()
    pedirParaAssumir()
  }
  if (evento.key === tecla('concluir') && aberta.value.estado !== 'resolvida') {
    evento.preventDefault()
    abrirPainel('encerrar')
  }
}

onMounted(async () => {
  document.addEventListener('keydown', atalho)
  document.addEventListener('keydown', fecharComEsc)
  /* 🚨 FECHAR A ABA TAMBÉM TEM DE GUARDAR. Sem isto, o F5 e o fechar levavam
     embora o que estava digitado -- e é justamente aí que a pessoa reclama. */
  window.addEventListener('beforeunload', guardarRascunho)
  lerRascunhosGuardados()
  document.addEventListener('click', fecharFiltroSeForaDele)
  /* 🚨 O FILTRO VEM DA URL QUANDO A TELA INICIAL MANDA. Os cartões da INI_1.1
     apontam para `/atendimento?minhas=1` e `?sem_dono=1`: sem ler a query, o
     clique cairia na lista inteira e o número da tela inicial não bateria com
     o que aparece aqui -- que é a forma mais rápida de a pessoa parar de
     confiar nos dois. */
  if (route.query.minhas) filtro.value = 'minhas'
  else if (route.query.sem_dono) filtro.value = 'sem_dono'
  else if (route.query.time) filtro.value = 'time'

  /* 🚨 VEM DA FICHA DO CLIENTE. O botão "Conversar" da CAD_1.1 manda o número
     para cá: se já existe conversa aberta com ele, abre; senão, abre o painel
     do `+` já preenchido. Sem isto, o botão levaria à caixa de entrada
     genérica e a pessoa teria de procurar o número que acabou de clicar. */
  const numeroPedido = route.query.numero
  if (numeroPedido) {
    busca.value = String(numeroPedido)
  }
  carregarAtalhos()
  await carregar()
  /* 🔴 CADA LISTA POR CONTA PRÓPRIA (23/09). Eram um `Promise.all`: a rota
     de classificações dava 403 para quem atende, e a rejeição levava a de
     TIMES junto -- o atendente abria "Transferir" com a lista de times vazia
     desde 07/08. A rota foi aberta à leitura, e mesmo assim as duas ficam
     separadas: uma lista que falha não pode apagar a outra. Sem elas a tela
     ainda mostra conversa; só a ação daquela lista fica sem opção. */
  const [rTimes, rClassif] = await Promise.allSettled([
    api.get('/api/times'),
    api.get('/api/classificacoes'),
  ])
  if (rTimes.status === 'fulfilled') times.value = rTimes.value
  if (rClassif.status === 'fulfilled') classificacoes.value = rClassif.value
  if (route.params.id) await abrir(route.params.id)
  else if (numeroPedido) {
    /* A busca já rodou com o número: se achou exatamente uma, abre; se não
       achou nenhuma, oferece começar a conversa com ele. */
    if (lista.value.length === 1) await abrir(lista.value[0].id)
    else if (!lista.value.length) {
      abrirNova()
      novoNumero.value = String(numeroPedido)
    }
  }
  // A fila é consumida a cada 5s no servidor; a tela reflete isso sem F5.
  timer = setInterval(() => {
    carregar({ silencioso: true })
    atualizarMensagens()
  }, 8000)
})

onUnmounted(() => {
  clearInterval(timer)
  // Sair da Caixa de entrada para outra tela não é descartar o que foi
  // digitado: guarda, e solta o áudio que estiver tocando.
  guardarRascunho()
  soltarPlayerLigacao()
  pararDeOlharAsNotas()
  window.removeEventListener('beforeunload', guardarRascunho)
  document.removeEventListener('keydown', atalho)
  document.removeEventListener('keydown', fecharComEsc)
  document.removeEventListener('click', fecharFiltroSeForaDele)
  /* 🚨 O MICROFONE TEM DE SER SOLTO AO SAIR (achado na auditoria de 25/08).
     Eu tratei disso no `onstop` do gravador e esqueci a saída pela porta:
     trocar de tela no meio de uma gravação deixava a trilha aberta e a luz
     vermelha do navegador acesa, com a pessoa achando que o painel continua
     ouvindo. É pior que o defeito original, porque não há nem o botão de
     cancelar à vista. */
  cancelarGravacao()
})

watch(filtro, () => carregar())
watch(timeDaAba, () => { if (filtro.value === 'time') carregar() })

/* 🔵 25/09: o Notificador precisa saber qual conversa está aberta -- com a
   tela à vista, ela não toca. */
/* 🔵 28/10: quantas notas internas desta conversa eu ainda não vi. Lê o mapa
   que o Notificador preenche; `0` quando não há nada, nunca `undefined` -- o
   `v-if` do selo compara com número. */
function notasDaConversa(id) {
  return (notificacoes.notasPorConversa || {})[id] || 0
}

watch(() => aberta.value?.id ?? null, (id) => { notificacoes.conversaAberta = id })

/* O clique no balão do Windows leva a `/atendimento/<id>`. Com a Caixa já
   aberta o componente é o mesmo e não remonta: sem isto, a URL mudava e a
   conversa não. */
watch(() => route.params.id, (id) => {
  if (id && String(aberta.value?.id) !== String(id)) abrir(id)
  // Sem id (o "voltar" do celular, ou o menu): fecha a conversa e mostra a lista.
  if (!id && aberta.value) fecharConversa()
})

/* 🔵 25/09, celular: o "Voltar" do cabeçalho. Fecha como a troca de conversa
   fecha (solta mídia, modal, citação), e a URL volta para a lista. */
function fecharConversa() {
  // O texto não se perde ao fechar a conversa: ele é rascunho dela.
  guardarRascunho()
  fecharLigacoes()
  fecharOutros()
  soltarMidias()
  painelAcao.value = ''
  confirmacao.value = null
  citando.value = null
  editando.value = null
  acoesCelular.value = false
  balaoTocado.value = null
  aberta.value = null
}
function voltarParaLista() {
  if (route.params.id) router.push({ path: '/atendimento' })
  else fecharConversa()
}

/* Tela de toque não tem hover: tocar no balão mostra as ações dele (reagir,
   citar, editar...). Toque em link, botão, foto ou player continua sendo
   daquilo -- só o "fundo" do balão alterna. */
function tocarBalao(m, evento) {
  if (!semMouse.matches) return
  if (evento.target.closest('a, button, img, audio, video, input, textarea')) return
  balaoTocado.value = balaoTocado.value === m.id ? null : m.id
}

/* Voltou para a aba: relê já, e aí sim marca como lida (ela está à vista). */
function aoVoltarParaAAba() {
  if (!document.hidden && aberta.value) atualizarMensagens()
}
document.addEventListener('visibilitychange', aoVoltarParaAAba)
onUnmounted(() => {
  document.removeEventListener('visibilitychange', aoVoltarParaAAba)
  notificacoes.conversaAberta = null
})

const filaParada = computed(
  () => resumo.value && resumo.value.eventos_pendentes > 50,
)

/* Quem está falando, na ordem do WhatsApp: o apelido que a pessoa escolheu,
   depois o nome do cadastro, e o número só quando não há nem um nem outro.

   🚨 O apelido vem PRIMEIRO de propósito. 35 das 37 conversas não têm vínculo
   com o cadastro -- com `contato_nome` na frente, a tela mostrava número cru
   quase sempre, num painel em que o nome da pessoa já tinha chegado. */
function quem(c) {
  // ⚠️ Grupo tem nome próprio e não tem telefone. Sem este primeiro caso, a
  // linha do grupo cairia no `telefone_e164`, que é NULO, e a lista mostraria
  // vazio.
  if (c.tipo === 'grupo') return c.grupo_nome || 'Grupo sem nome'
  return c.nome_whatsapp || c.contato_nome || c.telefone_e164
}

const ehGrupo = computed(() => Boolean(aberta.value && aberta.value.tipo === 'grupo'))

function quando(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const agora = new Date()
  const min = Math.round((agora - d) / 60000)
  if (min < 1) return 'agora'
  if (min < 60) return `${min} min`
  if (min < 60 * 24) return `${Math.round(min / 60)} h`
  return d.toLocaleDateString('pt-BR')
}

/* ---- separador de dia ---------------------------------------------------
   🚨 SEM ISTO O FIO É UM BLOCO SÓ. Uma conversa de meses desenhava mensagem
   atrás de mensagem sem nenhuma marca de tempo além da hora -- e "14:32" não
   diz se foi hoje ou em julho. É a primeira coisa que falta quando se compara
   com o WhatsApp lado a lado. */
function _diaDe(iso) {
  return iso ? new Date(iso).toDateString() : ''
}

function comecaODia(m, i) {
  if (!aberta.value) return false
  if (i === 0) return true
  return _diaDe(m.criada_em) !== _diaDe(aberta.value.mensagens[i - 1].criada_em)
}

/* 🔵 07/10: o histórico é contínuo mesmo quando a conversa foi transferida ou
   reaberta — nesse caso nasce uma conversa NOVA (outro `conversa_id`) e o
   backend junta as irmãs do mesmo número. Esta marca separa um atendimento do
   outro no mesmo fio. Não aparece no primeiro da janela (lá a marca seria sobre
   o que está ACIMA, que a janela ainda não trouxe). */
function comecaOutraConversa(m, i) {
  if (!aberta.value || i === 0) return false
  return m.conversa_id !== aberta.value.mensagens[i - 1].conversa_id
}

function rotuloDoDia(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const hoje = new Date()
  const ontem = new Date()
  ontem.setDate(hoje.getDate() - 1)
  if (d.toDateString() === hoje.toDateString()) return 'Hoje'
  if (d.toDateString() === ontem.toDateString()) return 'Ontem'
  /* Menos de um ano: dia e mês bastam. Mais que isso, o ano importa. */
  const mesmoAno = d.getFullYear() === hoje.getFullYear()
  return d.toLocaleDateString('pt-BR', mesmoAno
    ? { day: '2-digit', month: 'long' }
    : { day: '2-digit', month: 'long', year: 'numeric' })
}

function hora(iso) {
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

/* ---- EVENTO: o log de ações dentro da conversa (Bloco 11, 15/10) ---------
   🔵 *"log dentro da conversa interno apenas sobre ações de transfernecia,
   inserir pessoas, etc, pode ser em Nota interna do Movizap"*.

   🚨 A lista fechada vive no BANCO (`ck_evento_conhecido`, migração 064). Este
   mapa é só desenho: evento que chegue sem ícone aqui continua aparecendo, com
   um ponto -- a tela não esconde o que não reconhece.

   ⚠️ A FRASE VEM DO SERVIDOR, não daqui. Montá-la no navegador faria o mesmo
   log ter dois textos quando esta tela mudasse, e o histórico não se reescreve
   junto com o frontend. */
/* ---- "VISTO POR" NA NOTA (Bloco 12, 15/10) -------------------------------
   🔵 *"um icone que ao clicar mostra que ja leu aquela nota, junto ao carimbo
   de data e hora, bem pequeno mesmo"*.

   🚨 MARCA QUANDO A NOTA APARECE NA TELA, não quando a conversa abre. Abrir
   uma conversa de 200 mensagens e não rolar até o fim não é ver a nota que
   está lá atrás -- carimbar isso seria inventar o dado. Daí o
   `IntersectionObserver`: ele é a única coisa que sabe o que está visível.

   🚨 E O RÓTULO É "VISTO POR", NUNCA "LEU": o sistema sabe que a nota esteve
   na tela, não que a pessoa leu. Dizer "leu" vira prova de algo que não se
   mediu, e alguém cobra com base nisso.

   ⚠️ EM LOTE, com espera: rolar a conversa passa por dezenas de notas, e uma
   requisição por nota seria uma rajada. Junta e manda uma vez. */
const vistoAberto = ref(null)
const notasParaMarcar = new Set()
let observadorDeNotas = null
let timerDeNotas = null

function alternarVisto(id) {
  vistoAberto.value = vistoAberto.value === id ? null : id
}

async function mandarNotasVistas() {
  timerDeNotas = null
  const ids = [...notasParaMarcar]
  notasParaMarcar.clear()
  if (!ids.length || !aberta.value) return
  try {
    await api.post(`/api/conversas/${aberta.value.id}/notas/vistas`, { ids })
  } catch {
    /* ⚠️ Falhar aqui não pode virar erro na tela: a pessoa está LENDO, e não
       pediu nada. O carimbo se perde e a nota será marcada na próxima vez em
       que aparecer. */
  }
}

function olharAsNotas() {
  pararDeOlharAsNotas()
  if (typeof IntersectionObserver === 'undefined') return
  observadorDeNotas = new IntersectionObserver((entradas) => {
    for (const e of entradas) {
      if (!e.isIntersecting) continue
      const id = Number(e.target.dataset.nota)
      if (id) notasParaMarcar.add(id)
      // Uma vez visto, não precisa ser observado de novo.
      observadorDeNotas.unobserve(e.target)
    }
    if (notasParaMarcar.size && !timerDeNotas) {
      timerDeNotas = setTimeout(mandarNotasVistas, 1200)
    }
  }, { threshold: 0.6 })
  nextTick(() => {
    document.querySelectorAll('[data-nota]').forEach((el) => {
      observadorDeNotas?.observe(el)
    })
  })
}

/* ⚠️ O refresh de 8s e o "carregar anteriores" trazem nota nova, e elemento
   novo não nasce observado. Sem este `watch`, nota que chegasse depois da
   abertura nunca seria marcada -- e o recurso pareceria funcionar, porque as
   antigas aparecem certas. */
watch(() => aberta.value?.mensagens?.length, () => {
  if (aberta.value) olharAsNotas()
})

function pararDeOlharAsNotas() {
  observadorDeNotas?.disconnect()
  observadorDeNotas = null
  if (timerDeNotas) {
    clearTimeout(timerDeNotas)
    timerDeNotas = null
  }
  // O que já estava visto vai agora: fechar a conversa não apaga o fato.
  if (notasParaMarcar.size) mandarNotasVistas()
}

const ICONE_EVENTO = {
  assumiu: 'bi-hand-index-thumb',
  entrou: 'bi-box-arrow-in-right',
  convidou: 'bi-person-plus',
  saiu: 'bi-box-arrow-right',
  removeu: 'bi-person-dash',
  devolveu: 'bi-arrow-counterclockwise',
  transferiu: 'bi-arrow-left-right',
  encerrou: 'bi-check2-circle',
  vinculou: 'bi-link-45deg',
  desvinculou: 'bi-x-circle',
  mudou_tipo: 'bi-tag',
  bloqueou: 'bi-slash-circle',
  desbloqueou: 'bi-unlock',
}

/* `partir` e `marcar` mudaram para `util/destaque.js` em 12/08 — funções puras
   dentro de um `.vue` são inalcançáveis por teste, e elas sustentam o destaque
   da busca inclusive na parte de segurança. Ver `destaque.teste.js`. */

const ICONE = {
  imagem: 'bi-image', audio: 'bi-mic', video: 'bi-camera-video',
  documento: 'bi-file-earmark', figurinha: 'bi-emoji-smile',
  localizacao: 'bi-geo-alt', contato: 'bi-person-vcard',
}

/* ---- mídia --------------------------------------------------------------
   O que aparece dentro do balão (imagem, figurinha, áudio, vídeo) é buscado
   sob demanda e guardado aqui pelo id da mídia. Documento não é pré-carregado:
   nada ganha em baixar um PDF que ninguém abriu.

   ⚠️ Os object URLs são liberados ao trocar de conversa. Sem isso o navegador
   segura o binário de toda conversa já aberta -- num painel que fica aberto o
   dia inteiro, isso vira centenas de MB. */
const midias = reactive({})
const MOSTRAM_SOZINHAS = ['imagem', 'figurinha', 'audio', 'video']

/* O estado de entrega, no símbolo que quem atende já lê sem pensar (27/08).
   🚨 SÓ OS ESTADOS QUE TÊM TIQUE. `pendente` e `falhou` continuam saindo por
   extenso: um relógio e um "x" pequenininho no canto seriam justamente os dois
   casos em que a pessoa PRECISA parar e ler. */
const TIQUE = { enviada: '✓', entregue: '✓✓', lida: '✓✓' }

/* Que tipo de mídia é esta mensagem, para decidir se mostra foto, áudio ou só
   o link de baixar.

   🚨 NÃO DÁ PARA OLHAR SÓ `m.tipo`. Nota com anexo tem `tipo = 'nota'` -- o
   CHECK `ck_nota_e_interna` do banco exige isso --, então uma foto anexada a
   uma nota apareceria como "arquivo genérico" e nunca como imagem. Quem sabe
   o que é o arquivo é o MIME dele. */
const FAMILIA = { image: 'imagem', audio: 'audio', video: 'video' }

function tipoDaMidia(m) {
  if (!m.midia_id) return m.tipo
  if (m.tipo === 'nota' || m.tipo === 'documento') {
    return FAMILIA[(m.midia_mime || '').split('/')[0]] || 'documento'
  }
  return m.tipo
}

function tamanhoLegivel(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

async function carregarMidia(m) {
  if (!m.midia_id || midias[m.midia_id] !== undefined) return
  midias[m.midia_id] = ''   // marca "em andamento": evita buscar duas vezes
  try {
    const blob = await pedirBlob(`/api/midia/${m.midia_id}/ver`)
    midias[m.midia_id] = URL.createObjectURL(blob)
  } catch {
    // Falhar aqui não pode derrubar a conversa: o balão mostra o botão de
    // baixar e o texto, que é o que importa.
    midias[m.midia_id] = null
  }
}

/* 🟢 Erika (15/09): a foto de perfil de quem escreve.

   🚨 NÃO DÁ PARA USAR `<img src="/api/...">`: a tag não manda o cabeçalho
   `Authorization`, e a rota exige sessão -- é armadilha já registrada neste
   projeto. Vai pelo mesmo caminho da mídia: busca com token e vira object
   URL.

   ⚠️ 404 é o caso NORMAL (metade das pessoas não tem foto ou a escondeu), e
   por isso falhar aqui não mostra erro nenhum: a inicial colorida continua
   sendo o que identifica. */
const fotoUrl = ref('')

function soltarFoto() {
  if (fotoUrl.value) URL.revokeObjectURL(fotoUrl.value)
  fotoUrl.value = ''
}

async function carregarFoto(conversa) {
  soltarFoto()
  if (!conversa || conversa.tipo === 'grupo') return
  try {
    const blob = await pedirBlob(`/api/conversas/${conversa.id}/foto`)
    fotoUrl.value = URL.createObjectURL(blob)
  } catch {
    // sem foto: silêncio, a inicial já resolve
  }
}

function soltarMidias() {
  for (const [id, url] of Object.entries(midias)) {
    if (url) URL.revokeObjectURL(url)
    delete midias[id]
  }
}

async function baixarMidia(m) {
  const blob = await pedirBlob(`/api/midia/${m.midia_id}`)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = m.midia_nome || `midia-${m.midia_id}`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/* ---- gaveta do contato ---------------------------------------------------
   Abre por botão, como clicar no contato no WhatsApp. Quando há vínculo mostra
   a empresa; quando não há -- que é o caso de 35 das 41 conversas -- mostra a
   busca para vincular à mão.

   🚨 A busca é o produto aqui, não um detalhe. 51% dos clientes ativos estão
   fora do alcance por cadastro incompleto; cada vínculo feito nesta gaveta é
   um telefone que passa a existir, digitado por quem está falando com a
   pessoa. */
const baloes = ref(null)

/* 🚨 A CONVERSA ABRE NA MENSAGEM MAIS RECENTE. Abrir no topo obriga o
   atendente a rolar até o fim toda vez para achar o que a pessoa acabou de
   dizer -- e em conversa longa isso é o primeiro gesto, sempre.

   ⚠️ `nextTick` é obrigatório: sem ele a rolagem acontece antes de os balões
   existirem no DOM, e não faz nada -- em silêncio. */
async function rolarParaOFim() {
  await nextTick()
  if (baloes.value) baloes.value.scrollTop = baloes.value.scrollHeight
}

const gaveta = ref(false)

/* 🚫 S17 (15/09) DESFEITO EM 18/09, a pedido dele: *"proponha o botão Ficha -
   vincular para a tela original, de volta, pois não pedi isso a você. Era
   para abrir o modal a partir do botão 'vincular empresa', já dentro da
   ficha e não no botão de Ficha-Vincular"*. O S17 era sugestão minha, não
   pedido dele, e ela tinha um custo que eu não tinha visto: sem contato
   algum (61-63% das conversas), a gaveta nunca abria, e o Tipo "sem
   cadastro" e o aviso do Bitrix -- que moram dentro dela -- ficavam
   inalcançáveis. Ficha volta a abrir sempre a gaveta; o modal só abre pelo
   botão "Vincular a uma empresa", que já mora dentro dela. */
function abrirFicha() {
  gaveta.value = !gaveta.value
}

/* As empresas que o telefone alcança -- o grupo da pessoa.

   🚨 Número em vários cadastros NÃO é ambiguidade: são grupos empresariais
   com o mesmo responsável. A identidade é da PESSOA; as empresas são consulta
   rápida, atrás de um botão, com CNPJ para conferir. */
const empresas = ref([])
const empresasAbertas = ref(false)
const buscandoEmpresas = ref(false)

async function verEmpresas() {
  empresasAbertas.value = !empresasAbertas.value
  if (!empresasAbertas.value || empresas.value.length) return
  buscandoEmpresas.value = true
  try {
    const r = await api.get(`/api/conversas/${aberta.value.id}/empresas`)
    empresas.value = r.empresas || []
  } catch {
    empresas.value = []
  } finally {
    buscandoEmpresas.value = false
  }
}
const buscaCliente = ref('')
const achadosCliente = ref([])
const buscando = ref(false)
const vinculando = ref(false)

async function procurarCliente() {
  const termo = buscaCliente.value.trim()
  if (termo.length < 2) {
    achadosCliente.value = []
    return
  }
  buscando.value = true
  try {
    const r = await api.get(`/api/conversas/buscar-empresa?termo=${encodeURIComponent(termo)}`)
    achadosCliente.value = r.itens || r.lista || []
  } catch {
    achadosCliente.value = []
  } finally {
    buscando.value = false
  }
}

/* 🚨 CANDIDATO SEM EMPRESA VAI POR `contato_id` (08/10). O botão do candidato
   mandava `c.cliente_id || c.id` -- e técnico não tem empresa, então o id do
   CONTATO ia no campo de EMPRESA. O servidor procurava a empresa com esse id,
   não achava, e tentava criar contato nela. Era a única saída que a trava do
   "marcar tipo" oferece ("vincule a um dos candidatos"), e estava fechada
   justo para os 274 técnicos importados em 02/10. */
async function vincularA(clienteId, contatoId = null) {
  vinculando.value = true
  try {
    /* 🚨 O MODAL FECHA NO SUCESSO, NÃO NO CLIQUE (28/08). Fechar antes da
       resposta deixaria a falha sem lugar para aparecer: o `erro` do catch
       renderiza na tela de trás, e quem clicou já teria perdido a lista de
       onde escolher de novo. */
    await api.post(`/api/conversas/${aberta.value.id}/vincular`, {
      cliente_id: clienteId,
      contato_id: contatoId,
      nome: nomeDoContato.value.trim() || null,
    })
    painelAcao.value = ''
    // Relê do servidor em vez de remendar a tela: o vínculo pode ter criado
    // contato novo, e o que vale é o que o banco diz.
    await abrir(aberta.value.id)
    await carregar({ silencioso: true })
    recado.value = 'Número vinculado ao cadastro.'
    buscaCliente.value = ''
    achadosCliente.value = []
  } catch (e) {
    erro.value = e instanceof ErroDeApi ? e.message : 'Falha ao vincular.'
    relatarErroDeBotao('vincular', e, aberta.value?.id)
  } finally {
    vinculando.value = false
  }
}

async function desvincular() {
  try {
    await api.post(`/api/conversas/${aberta.value.id}/desvincular`)
    await abrir(aberta.value.id)
    await carregar({ silencioso: true })
    recado.value = 'Vínculo desfeito. O telefone continua no cadastro.'
  } catch (e) {
    erro.value = e instanceof ErroDeApi ? e.message : 'Falha ao desvincular.'
    relatarErroDeBotao('desvincular', e, aberta.value?.id)
  }
}

function documentoLegivel(d) {
  if (!d) return ''
  const s = String(d)
  if (s.length === 14) return s.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  if (s.length === 11) return s.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  return s
}

/* A coordenada é o que vem ANTES do ' · ' no conteúdo de uma localização --
   formato escrito por `_texto_do_local` no parser, de propósito estável.
   Devolve vazio se não casar: link de mapa para coisa que não é coordenada
   levaria a pessoa a uma página de erro. */
function coordenadaDe(conteudo) {
  const m = /^(-?\d+\.\d+,-?\d+\.\d+)/.exec(conteudo || '')
  return m ? m[1] : ''
}

function carregarMidiasDaConversa(c) {
  if (!c || !c.mensagens) return
  for (const m of c.mensagens) {
    if (m.midia_id && MOSTRAM_SOZINHAS.includes(tipoDaMidia(m))) carregarMidia(m)
  }
}
</script>

<template>
  <div class="tela tela--conversa">
    <!-- 🚨 O CABEÇALHO DE PÁGINA SAIU EM 27/08. Ele comia uma faixa da altura
         para repetir o nome da tela que o menu já diz, e era o que mais
         afastava a tela do desenho escolhido. O título e os dois números
         passaram para o topo da coluna da lista, onde ficam ao lado do que
         contam.

         ⚠️ OS DOIS NÚMEROS FICARAM. Eles são a decisão de 25/08 -- quantas
         esperam e quantas não têm dono é o que decide trabalho. -->

    <p v-if="filaParada" class="aviso aviso--erro" role="alert">
      <i class="bi bi-exclamation-octagon aviso__icone" aria-hidden="true"></i>
      <span>
        <strong>{{ resumo.eventos_pendentes }} eventos esperando processamento.</strong>
        A fila pode ter parado.
      </span>
    </p>
    <p v-else-if="resumo && resumo.eventos_com_erro" class="aviso aviso--atencao" role="status">
      <i class="bi bi-exclamation-triangle aviso__icone" aria-hidden="true"></i>
      <span>{{ resumo.eventos_com_erro }} evento(s) com erro de interpretação — o payload cru está guardado e dá para reprocessar.</span>
    </p>

    <p v-if="erro" class="aviso aviso--erro" role="alert">
      <i class="bi bi-exclamation-octagon aviso__icone" aria-hidden="true"></i>
      <span>{{ erro }}</span>
    </p>
    <p v-if="recado" class="aviso aviso--ok" role="status">
      <i class="bi bi-check-circle aviso__icone" aria-hidden="true"></i>
      <span>{{ recado }}</span>
    </p>

    <div class="painel" :class="{ 'painel--com-conversa': aberta }">
      <!-- ---------------------------------------------------------- LISTA -->
      <section class="cartao coluna">
        <header class="cartao__cabecalho lista__topo">
          <!-- O título e os dois números, que antes ficavam numa faixa acima
               da tela inteira. Aqui eles ficam ao lado do que contam. -->
          <div class="lista__titulo">
            <h1>WhatsApp</h1>
            <AjudaDaTela titulo="O que esta tela faz e o que a busca alcança">
              As conversas do WhatsApp, suas e da fila. A busca procura no nome
              do WhatsApp, no cadastro, no telefone (pedaço serve:
              <code>6168</code>) e no texto das mensagens, inclusive das notas
              internas.
              <!-- 🚨 24/09 (auditoria): ensinava j/k/a/c SEMPRE. Os atalhos
                   nascem desligados (28/08) e desde 24/09 só o owner os liga:
                   para quem atende, a ajuda anunciava teclas que não faziam
                   nada. E a tecla é a DA PESSOA -- ela pode ter trocado. -->
              <template v-if="atalhosLigados">
                <br /><br />
                <strong>Atalhos:</strong>
                <code>{{ tecla('proxima') }}</code> e <code>{{ tecla('anterior') }}</code> passam de conversa ·
                <code>{{ tecla('buscar') }}</code> vai para a busca ·
                <code>{{ tecla('assumir') }}</code> assume ·
                <code>{{ tecla('concluir') }}</code> abre o concluir.
              </template>
            </AjudaDaTela>
            <div v-if="resumo" class="lista__placar">
              <span>{{ resumo.conversas }} abertas</span>
              <span v-if="resumo.sem_dono" class="lista__pede">
                · {{ resumo.sem_dono }} sem dono
              </span>
              <!-- 🔵 23/09: quem o painel bloqueou. Só existe quando há algum;
                   clicar de novo volta para Todas. -->
              <button v-if="resumo.bloqueados" type="button"
                      class="botao botao--pequeno"
                      :class="filtro === 'bloqueados' ? 'botao--primario' : 'botao--fantasma'"
                      :aria-pressed="filtro === 'bloqueados'"
                      @click="filtro = filtro === 'bloqueados' ? 'todas' : 'bloqueados'">
                <i class="bi bi-slash-circle" aria-hidden="true"></i>
                Bloqueados ({{ resumo.bloqueados }})
              </button>
            </div>
          </div>

          <div class="linha linha--quebra">
            <!-- ⚠️ CONTROLE SEGMENTADO, não botões soltos. Botões com cores
                 diferentes leem como ações; segmentado lê como UMA escolha --
                 que é o que é. Quatro desde 23/09, com a aba Time. -->
            <div class="abas" role="tablist">
              <button
                v-for="f in FILTROS"
                :key="f.valor"
                class="abas__aba"
                :class="{ 'abas__aba--ativa': filtro === f.valor }"
                type="button"
                role="tab"
                :aria-selected="filtro === f.valor"
                @click="filtro = f.valor"
              >
                {{ f.rotulo }}
                <!-- 🔵 24/09: quantas conversas com não lida, só em Minhas e
                     Time (*"somente minhas e time, por enquanto"*). O número
                     vem do Notificador, que já pergunta isso a cada 8 s. -->
                <span v-if="contadorDaAba(f.valor)" class="abas__contador"
                      :title="`${contadorDaAba(f.valor)} conversa(s) com mensagem não lida`">
                  {{ contadorDaAba(f.valor) > 99 ? '99+' : contadorDaAba(f.valor) }}
                </span>
              </button>
            </div>

            <!-- 🚨 O `+` FICA LOGO DEPOIS DE "MINHAS", como o usuário pediu
                 em 25/08. É o único lugar do painel onde se escolhe para quem
                 mandar: em todo o resto o destino sai da conversa. -->
            <button
              class="botao botao--pequeno botao--primario botao--icone"
              type="button"
              title="Nova mensagem para um número"
              aria-label="Nova mensagem para um número"
              @click="abrirNova"
            >
              <i class="bi bi-plus-lg" aria-hidden="true"></i>
            </button>
          </div>

          <label v-if="filtro === 'time' && meusTimes.length > 1" class="campo campo--time">
            <span class="campo__rotulo">Filtrar por time</span>
            <select v-model="timeDaAba" class="campo__entrada">
              <option value="">Todos os meus times</option>
              <option v-for="t in meusTimes" :key="t.id" :value="String(t.id)">{{ t.nome }}</option>
            </select>
          </label>

        </header>

        <div class="cartao__corpo">
          <label class="campo campo--busca">
            <span class="so-leitor">Buscar conversa</span>
            <div class="busca">
              <input
                v-model="busca"
                class="campo__entrada"
                type="search"
                placeholder="nome, telefone ou empresa"
                @keyup.enter="carregar()"
              />
              <!-- 🚨 O FILTRO MORA NO CAMPO DE BUSCA, não numa fileira de
                   chips acima da lista. Buscar e filtrar são a MESMA
                   pergunta ("quais conversas eu quero ver"), e separá-los em
                   dois lugares fazia a coluna virar uma parede de botões
                   antes de qualquer conversa aparecer. -->
              <div class="filtro">
                <button
                  class="botao botao--contorno botao--icone"
                  type="button"
                  :class="{ 'botao--filtrando': contaFiltros() }"
                  :aria-expanded="filtroAberto"
                  title="Filtrar: onde buscar, contato ou grupo, e tipo de cadastro"
                  aria-label="Filtrar: onde buscar, contato ou grupo, e tipo de cadastro"
                  @click.prevent="filtroAberto = !filtroAberto"
                >
                  <i class="bi bi-funnel" aria-hidden="true"></i>
                  <span v-if="contaFiltros()" class="filtro__conta">
                    {{ contaFiltros() }}
                  </span>
                </button>

                <div v-if="filtroAberto" class="filtro__caixa">
                  <!-- 🔵 30/09: ONDE PROCURAR. Vem primeiro porque é o que mais
                       muda o resultado: com "o que foi dito" ligado, a busca
                       devolve toda conversa que mencionou a palavra. -->
                  <p class="filtro__titulo">Buscar em</p>
                  <label class="filtro__linha">
                    <input
                      type="checkbox"
                      :checked="emNome"
                      @change="alternarOnde('nome')"
                    />
                    <span>Nome, telefone e empresa</span>
                  </label>
                  <label class="filtro__linha">
                    <input
                      type="checkbox"
                      :checked="emMensagens"
                      @change="alternarOnde('mensagens')"
                    />
                    <span>O que foi dito na conversa</span>
                  </label>

                  <!-- 🔵 30/09: contato ou grupo. Filtro, não aba. -->
                  <p class="filtro__titulo">Onde</p>
                  <label v-for="e in ESCOPOS" :key="e.valor" class="filtro__linha">
                    <input
                      type="radio"
                      name="escopo-da-conversa"
                      :value="e.valor"
                      :checked="escopo === e.valor"
                      @change="trocarEscopo(e.valor)"
                    />
                    <span>{{ e.rotulo }}</span>
                  </label>

                  <p class="filtro__titulo">Tipos</p>
                  <label v-for="t in TIPOS" :key="t.valor" class="filtro__linha">
                    <input
                      type="checkbox"
                      :checked="tiposMarcados.includes(t.valor)"
                      @change="alternarTipo(t.valor)"
                    />
                    <span>{{ t.rotulo }}</span>
                  </label>
                  <button
                    v-if="contaFiltros()"
                    class="botao botao--pequeno botao--fantasma filtro__limpar"
                    type="button"
                    @click.prevent="limparFiltros()"
                  >
                    limpar filtro
                  </button>
                </div>
              </div>

              <button
                class="botao botao--primario botao--icone"
                type="button"
                title="Buscar conversa"
                aria-label="Buscar conversa"
                @click="carregar()"
              >
                <i class="bi bi-search" aria-hidden="true"></i>
              </button>
            </div>

            <!-- O que está filtrado aparece EMBAIXO do campo, em texto: o
                 ícone diz que há filtro, esta linha diz qual. -->
            <span v-if="contaFiltros()" class="filtro__resumo pequeno">
              <i class="bi bi-funnel-fill" aria-hidden="true"></i>
              {{ resumoFiltros() }}
            </span>
            <!-- 🚨 A EXPLICAÇÃO DA BUSCA SUBIU PARA O ÍCONE DE AJUDA (28/08).
                 Ela era uma faixa fixa embaixo do campo, na coluna de 348px:
                 três linhas de altura permanente numa coluna cujo produto é
                 quantas conversas cabem à vista.

                 🚨 ERA A METADE QUE FALTOU DE 27/08. O pedido dele -- *"as
                 abas tem textos explicativos... transforme em balões ícones
                 apenas"* -- alcançava 15 telas; eu varri por FORMA DE
                 MARCAÇÃO (`<h1>` + `<p class="apagado pequeno">`) e entreguei
                 13. Esta ficou de fora porque o texto não estava no cabeçalho,
                 e o do E-mail porque o `<h1>` de lá tem `class`. Medir a forma
                 do código em vez do alcance do pedido é o M7 outra vez. -->
            <!-- ⚠️ Só depois de 3 s. Antes disso, o normal é responder em
                 milissegundos e piscar seria pior que ficar quieto. -->
            <span v-if="buscaDemorada" class="linha pequeno fraco">
              <span class="girando"></span> Procurando… a base está grande, isso
              pode levar alguns segundos.
            </span>
          </label>
        </div>

        <p v-if="carregando" class="linha fraco cartao__corpo">
          <span class="girando"></span> Lendo…
        </p>

        <div v-else-if="!lista.length" class="vazio">
          <i class="bi bi-chat-dots vazio__icone" aria-hidden="true"></i>
          <p class="vazio__titulo">Nenhuma conversa</p>
          <!-- A aba Time vazia tem OUTRO motivo, e a tela diz qual: ou não
               há conversa esperando nos seus times, ou você não é de time
               nenhum -- quem decide isso é a tela de Times. -->
          <p v-if="filtro === 'time'">
            Nenhuma conversa sem dono nos times de que você faz parte.
          </p>
          <p v-else>Assim que alguém escrever, ela aparece aqui sozinha.</p>
        </div>

        <ul v-else class="conversas">
          <!-- 🚨 O "Assumir" é IRMÃO do botão da conversa, não filho: botão
               dentro de botão é HTML inválido, e o navegador desfaz o
               aninhamento sozinho -- o clique de dentro passa a abrir a
               conversa em vez de assumir, sem erro nenhum aparecer. -->
          <li v-for="c in lista" :key="c.id" class="conversas__item">
            <button
              class="conversa"
              :class="{
                'conversa--aberta': aberta && aberta.id === c.id,
                'conversa--grupo': c.tipo === 'grupo',
              }"
              type="button"
              @click="abrir(c.id)"
            >
              <!-- 🚨 AVATAR COM INICIAL, NÃO ÍCONE GENÉRICO. A lista é lida
                   de relance, dezenas de vezes por dia: cor estável derivada
                   do nome distingue a conversa antes de a pessoa ler. Grupo
                   troca a inicial pelo ícone de pessoas -- ali a inicial de
                   um nome de grupo não identifica ninguém. -->
              <span
                class="conversa__avatar"
                :style="{ background: c.tipo === 'grupo' ? null : corDaInicial(quem(c)) }"
                :class="{ 'conversa__avatar--grupo': c.tipo === 'grupo' }"
                aria-hidden="true"
              >
                <i v-if="c.tipo === 'grupo'" class="bi bi-people"></i>
                <template v-else>{{ iniciais(quem(c)) }}</template>
              </span>

              <span class="conversa__corpo">
              <div class="conversa__topo">
                <strong class="conversa__quem">{{ quem(c) }}</strong>
              </div>
              <!-- ⚠️ Fui CHAMADO para esta, não sou o dono. Sem a marca, ela
                   fica igual às minhas na lista — e só o dono responde por ela. -->
              <p v-if="c.acompanho" class="conversa__marca pequeno">
                <i class="bi bi-people" aria-hidden="true"></i>
                acompanhando<span v-if="c.atendente_nome"> · {{ c.atendente_nome }} responde</span>
              </p>
              <!-- 🚨 CASOU PELO TEXTO: MOSTRAR O TEXTO. Sem isto a conversa
                   entra na lista sem nada visível batendo com o que foi
                   digitado, e quem buscou conclui que a busca está quebrada.
                   O backend só manda `trecho` quando o nome NÃO explica o
                   acerto -- se o nome já bate, a prévia normal continua. -->
              <p v-if="c.trecho" class="conversa__previa conversa__previa--achado pequeno">
                <i class="bi bi-search" aria-hidden="true"></i>
                <span v-for="(p, i) in partir(c.trecho, busca)" :key="i"
                      :class="{ 'achado': p.casa }">{{ p.texto }}</span>
              </p>
              <p v-else class="conversa__previa apagado pequeno">
                <i v-if="ICONE[c.ultimo_tipo]" class="bi" :class="ICONE[c.ultimo_tipo]" aria-hidden="true"></i>
                <span v-if="c.ultima_direcao === 'saida'" class="fraco">você: </span>
                {{ c.ultima_mensagem || '(sem texto)' }}
              </p>
              <!-- 🚨 UMA LINHA DE MARCAS, NÃO CINCO CHIPS. Antes toda
                   conversa carregava até cinco chips do mesmo peso -- e
                   "sem dono" aparecia em TODAS as 336, virando ruído puro. O
                   que fica é o que diferencia esta conversa das outras: quem
                   responde (quando há), a empresa (quando se sabe) e o
                   estado excepcional. -->
              <div class="conversa__marcas pequeno">
                <!-- 🟡 15/10 (minha): sem esta marca, o texto guardado fica
                     invisível até a pessoa reabrir a conversa -- e rascunho
                     que ninguém vê é rascunho esquecido. -->
                <span v-if="comRascunho.has(c.id)" class="chip chip--pequeno"
                      title="Você tem texto digitado e não enviado nesta conversa">
                  <i class="bi bi-pencil" aria-hidden="true"></i>
                  rascunho
                </span>
                <span v-if="!c.contato_nome" class="chip chip--aviso chip--pequeno">
                  sem cadastro
                </span>
                <span v-else-if="c.cliente_nome" class="chip chip--pequeno">
                  {{ c.cliente_nome }}
                </span>
                <span v-if="c.estado === 'resolvida'" class="chip chip--ok chip--pequeno">
                  concluída
                </span>
                <!-- 🔵 25/09: na aba Time, de qual time é -- o mesmo cliente
                     pode estar em dois. -->
                <span v-if="filtro === 'time' && c.time_nome" class="chip chip--pequeno">
                  <i class="bi bi-people" aria-hidden="true"></i>
                  {{ c.time_nome }}
                </span>
                <!-- 🔵 23/09: a bolinha diz se quem responde está DISPONÍVEL -- a
                     conversa de alguém fora do expediente não anda. Mesma régua
                     do Chat interno (`util/estado.js`). -->
                <span v-if="c.atendente_nome" class="chip chip--acento chip--pequeno"
                      :title="`${c.atendente_nome}: ${rotuloDoEstado(c.atendente_estado, { automatico: c.atendente_estado_auto, emJornada: c.atendente_em_jornada })}`">
                  <span class="estado-bolinha" aria-hidden="true"
                        :style="{ background: corDoEstado(c.atendente_estado) }"></span>
                  {{ c.atendente_nome }}
                </span>
              </div>
              </span>
            </button>

            <!-- 🆕 03/11 (pedido dele, com a seta desenhada na imagem): A
                 COLUNA DA DIREITA. O selo de não lidas e a hora saíram da linha
                 do NOME e vieram para cá, acima do botão -- que é o espaço que
                 já estava vazio, porque o botão é centralizado na vertical e o
                 nome fica no topo.

                 🚨 O GANHO É MEDIDO, não estético: o nome tinha ~89 px
                 (~12 caracteres) e passa a ter a linha inteira. Medido em
                 03/11 na base: **68,1% dos 1.014 nomes eram cortados**,
                 mediana 14 caracteres.

                 🚨 LARGURA FIXA, E ISTO NÃO É DETALHE: o botão varia de
                 "Entrar" a "Ir para a aberta" (166 linhas, 16,4%), e sem
                 largura fixa o nome mudaria de tamanho a cada linha -- lista
                 que muda de ritmo é mais difícil de varrer do que lista com
                 nome curto. É a mesma razão que fez a prévia ter uma linha só.

                 ⚠️ O QUE SE PERDE, DITO: o selo e a hora deixam de ser área de
                 clique da conversa (eram, dentro do `<button class="conversa">`).
                 Botão dentro de botão é HTML inválido -- o aviso logo acima --,
                 então eles ficam fora. O resto do card continua abrindo. -->
            <div class="conversas__direita">
              <div class="conversas__meta">
                <!-- 🟢 Erika (15/09): a bolinha de quantidade, como no
                     WhatsApp e no Bitrix. Conta só mensagem DO CLIENTE que
                     esta pessoa ainda não viu -- abrir a conversa zera.
                     ⚠️ Na borda direita desde 03/11, que é onde o WhatsApp a
                     põe -- não foi invenção nova, foi voltar à convenção. -->
                <span v-if="c.nao_lidas > 0" class="conversa__selo"
                      :title="`${c.nao_lidas} mensagem(ns) que você ainda não viu`">
                  {{ c.nao_lidas > 99 ? '99+' : c.nao_lidas }}
                </span>
                <!-- 🔵 28/10: o selo da NOTA INTERNA, SEPARADO do de mensagem.
                     🚨 Não soma nos dois: o número de cima responde "o cliente
                     falou comigo", e nota é um colega. Somar faria a tela dizer
                     que o cliente falou quando não falou. 🚨 E ele VIAJA JUNTO
                     com o verde: separados na tela, a distinção de cor que a
                     regra de 28/10 criou fica ilegível.
                     ⚠️ Vem do estado que o Notificador preenche de 8 em 8 s, e
                     NÃO da lista -- a consulta da lista é a mais quente do
                     painel e tem ordem posicional de parâmetro; mexer nela por
                     um selo seria trocar risco alto por ganho nenhum. -->
                <span v-if="notasDaConversa(c.id) > 0" class="conversa__selo conversa__selo--nota"
                      :title="`${notasDaConversa(c.id)} nota(s) interna(s) que você ainda não viu`">
                  <svg viewBox="0 0 16 16" width="10" height="10" aria-hidden="true"
                       style="vertical-align:-1px">
                    <path fill="currentColor" d="M3 2h10a1 1 0 0 1 1 1v8l-3 3H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zm2 3h6V4H5v1zm0 3h6V7H5v1zm0 3h4v-1H5v1z"/>
                  </svg>
                  {{ notasDaConversa(c.id) > 99 ? '99+' : notasDaConversa(c.id) }}
                </span>
                <span class="apagado pequeno conversa__hora">
                  {{ quando(c.ultima_atividade_em) }}
                </span>
              </div>

            <!-- 🔵 30/09: ENCERRADA que tem uma viva do mesmo grupo/número não
                 oferece reabrir -- o índice `ux_conversa_aberta` recusaria.
                 Leva à conversa aberta, que é onde a resposta chega, e mata o
                 "parece que tem duas conversas" que ele notou. -->
            <button
              v-if="c.estado === 'resolvida' && c.conversa_viva_id"
              class="botao botao--pequeno conversas__assumir"
              type="button"
              title="Este grupo/número já tem uma conversa aberta — ir até ela"
              @click="abrir(c.conversa_viva_id)"
            >
              <i class="bi bi-box-arrow-in-right" aria-hidden="true"></i>
              Ir para a aberta
            </button>
            <!-- Sem dono, ou encerrada sem viva: dá para pegar daqui, sem abrir
                 antes. Encerrada, assumir REABRE -- por isso o rótulo muda. -->
            <button
              v-else-if="!c.atendente_id || c.estado === 'resolvida'"
              class="botao botao--pequeno botao--primario conversas__assumir"
              type="button"
              :title="c.estado === 'resolvida'
                ? 'Reabrir este atendimento e passar a responder por ele'
                : 'Assumir esta conversa'"
              @click="assumirDaLista(c)"
            >
              <i class="bi" :class="c.estado === 'resolvida' ? 'bi-arrow-counterclockwise' : 'bi-hand-index-thumb'" aria-hidden="true"></i>
              {{ c.estado === 'resolvida' ? 'Reabrir' : 'Assumir' }}
            </button>
            <!-- 🔵 30/09 (pedido dele): EM ANDAMENTO com dono -- "deveria entrar
                 pelo botão de entrar pois está em andamento". Antes a linha não
                 tinha botão nenhum aqui e "assumir" respondia "já foi assumida
                 por X" (beco sem saída). Entrar acompanha sem tirar de quem
                 atende. -->
            <button
              v-else-if="c.atendente_id && c.estado !== 'resolvida'"
              class="botao botao--pequeno conversas__assumir"
              type="button"
              title="Entrar para acompanhar — quem responde continua sendo o dono"
              @click="entrarDaLista(c)"
            >
              <i class="bi bi-box-arrow-in-right" aria-hidden="true"></i>
              Entrar
            </button>
            </div>
          </li>
        </ul>
      </section>

      <!-- ------------------------------------------------------- CONVERSA -->
      <section class="cartao coluna coluna--larga">
        <div v-if="!aberta" class="vazio">
          <i class="bi bi-chat-text vazio__icone" aria-hidden="true"></i>
          <p class="vazio__titulo">Escolha uma conversa</p>
        </div>

        <template v-else>
          <header class="cartao__cabecalho conversa__cabecalho">
            <!-- 🔵 25/09, celular: sem a lista ao lado, o caminho de volta é
                 este botão (e o "voltar" do aparelho). Some no computador. -->
            <button
              class="botao botao--fantasma botao--icone so-celular conversa__voltar"
              type="button"
              aria-label="Voltar para a lista de conversas"
              @click="voltarParaLista"
            >
              <i class="bi bi-arrow-left" aria-hidden="true"></i>
            </button>
            <div class="conversa__quem">
              <strong>
                <!-- 🟢 Erika (15/09): a foto de perfil de quem escreve.
                     🚨 SÓ NA CONVERSA ABERTA, NUNCA NA LISTA. Na lista seriam
                     dezenas de buscas por carga de tela -- e a primeira de
                     cada número vai à rede, ao Evolution. Aqui é uma, quando
                     a pessoa já escolheu com quem falar.
                     ⚠️ Some sozinha se não houver foto (404 é o normal: metade
                     das pessoas não tem ou escondeu), e a inicial colorida
                     continua sendo o que identifica. -->
                <img
                  v-if="!ehGrupo && fotoUrl"
                  :src="fotoUrl"
                  class="conversa__foto"
                  alt=""
                />
                <i v-if="ehGrupo" class="bi bi-people" aria-hidden="true"></i>
                {{ quem(aberta) }}
              </strong>
              <!-- 🚨 GRUPO NÃO TEM FICHA. A gaveta mostra UM cliente, e num
                   grupo há vários ou nenhum -- abri-la ali mostraria a ficha
                   de quem, exatamente? -->
              <!-- 🚨 O BOTÃO DIZ DE QUEM É A FICHA, e muda de cara quando não
                   há ficha. Era um botão fantasma com "Ver ficha", do mesmo
                   peso de tudo na barra. Medido em 28/08: 231 das 381
                   conversas (61%) não têm cadastro -- então a falta de
                   cadastro é o estado mais COMUM, e é o convite para
                   resolver, não um detalhe. Por isso ele é contorno âmbar
                   quando falta, e contorno normal com o nome dentro quando
                   existe. -->
              <button
                v-if="!ehGrupo"
                class="botao botao--pequeno"
                :class="aberta.contato_id ? 'botao--contorno' : 'botao--faltando'"
                type="button"
                :aria-expanded="gaveta"
                @click="abrirFicha"
              >
                <i class="bi" :class="aberta.contato_id
                     ? 'bi-person-lines-fill' : 'bi-person-exclamation'"
                   aria-hidden="true"></i>
                <!-- 🚨 A PALAVRA "FICHA" ABRE TODOS OS CASOS (28/08). Pedido
                     dele: *"não vejo mais a ficha nas conversas"* -- e a ficha
                     estava lá o tempo todo. O rótulo "Sem ficha — vincular",
                     que eu escrevi em 25/08 (48bdfd4), lê como AUSÊNCIA e não
                     como porta, e ele cai em 231 das 381 conversas (61%,
                     medido em 28/08): o estado mais comum da tela anunciava
                     que a função não existia.

                     ⚠️ O que distingue os dois casos continua sendo o CONTORNO
                     âmbar e o ícone, não a palavra. Cor diz estado; a palavra
                     diz que coisa é. -->
                <template v-if="gaveta">Fechar ficha</template>
                <template v-else-if="aberta.empresa && aberta.empresa.cliente">
                  Ficha · {{ aberta.empresa.cliente.nome }}
                </template>
                <template v-else-if="aberta.contato_nome">
                  Ficha · {{ aberta.contato_nome }}
                </template>
                <template v-else>Ficha · vincular</template>
              </button>
              <p class="apagado pequeno mono conversa__detalhe">
                {{ aberta.telefone_e164 }}
                <span v-if="aberta.nome_whatsapp && aberta.contato_nome">
                  · cadastro: {{ aberta.contato_nome }}
                </span>
                <span v-if="aberta.canal_nome"> · {{ aberta.canal_nome }}</span>
                · {{ aberta.estado }}
              </p>
            </div>
            <!-- 🔵 25/09, celular: a barra de ações (Transferir, Convidar,
                 Devolver, Sair, Bloquear, Concluir) ocupava metade da altura.
                 No celular ela ficava atrás deste botão.
                 🔵 07/10: passa a valer TAMBÉM no computador (decisão dele) —
                 recolhida atrás de "Ações" nas duas pontas, pelo ganho de
                 tela. Por isso o `so-celular` saiu. -->
            <button
              v-if="aberta.estado !== 'resolvida'"
              class="botao botao--contorno botao--pequeno"
              type="button"
              :aria-expanded="acoesCelular"
              @click="acoesCelular = !acoesCelular"
            >
              <i class="bi" :class="acoesCelular ? 'bi-x-lg' : 'bi-three-dots'" aria-hidden="true"></i>
              {{ acoesCelular ? 'Fechar' : 'Ações' }}
            </button>
            <!-- Encerrada TEM dono (quem fechou), então o `!atendente_id` de
                 antes escondia o botão justo onde ele é mais necessário. -->
            <button
              v-if="!aberta.atendente_id || aberta.estado === 'resolvida'"
              class="botao botao--pequeno botao--primario"
              type="button"
              @click="pedirParaAssumir"
            >
              <i class="bi" :class="aberta.estado === 'resolvida' ? 'bi-arrow-counterclockwise' : 'bi-hand-index-thumb'" aria-hidden="true"></i>
              {{ aberta.estado === 'resolvida' ? 'Reabrir e assumir' : 'Assumir' }}
            </button>
            <span v-else class="chip chip--acento">
              <span class="estado-bolinha" aria-hidden="true"
                    :style="{ background: corDoEstado(aberta.atendente_estado) }"></span>
              {{ aberta.atendente_nome }}
              <span class="estado-rotulo">· {{ rotuloDoEstado(aberta.atendente_estado, { automatico: aberta.atendente_estado_auto, emJornada: aberta.atendente_em_jornada }) }}</span>
            </span>
          </header>

          <!-- ⚠️ Só aparece quando há alguém: linha vazia em toda conversa
               seria ruído numa tela que já é densa. -->
          <p v-if="acompanham.length" class="conversa__acompanham pequeno">
            <i class="bi bi-people" aria-hidden="true"></i>
            <span class="apagado">acompanhando:</span>
            <span v-for="p in acompanham" :key="p.atendente_id" class="chip chip--pequeno">
              {{ p.nome }}
              <button
                v-if="souDono"
                class="chip__x"
                type="button"
                :disabled="mexendo"
                :title="`tirar ${p.nome} da conversa`"
                @click="removerParticipante(p.atendente_id)"
              >×</button>
            </span>
          </p>

          <!-- ══ BARRA DE AÇÕES ══════════════════════════════════════════
               Fica no TOPO, logo abaixo de quem responde pela conversa
               (pedido do usuário em 12/08): é ali que se olha para saber de
               quem é a conversa, e é ali que se decide o que fazer com ela.
               Antes estava lá embaixo, depois de todos os balões.

               🚨 ÍCONE SEM NOME É ADIVINHAÇÃO: os quatro carregam `title` e
               `aria-label`. "Devolver à fila" e "Sair da conversa" fazem
               coisas diferentes e têm setas parecidas. *Encerrar* mantém o
               texto — é o fim do atendimento, e é o único que não deve
               depender de reconhecer desenho. -->
          <div v-if="aberta.estado !== 'resolvida'" class="acoes cartao__corpo"
               :class="{ 'acoes--aberta-celular': acoesCelular }">
            <!-- DE FORA: só dá para ler. A rota recusa com 409 de qualquer
                 jeito; aqui a tela para de oferecer o que seria negado. -->
            <div v-if="!posso" class="linha linha--quebra">
              <span class="chip chip--aviso">
                <i class="bi bi-eye" aria-hidden="true"></i> só leitura
              </span>
              <span class="apagado pequeno">
                Você não está nesta conversa.
              </span>
              <span class="espaco"></span>
              <button
                v-if="aberta.atendente_id"
                class="botao botao--pequeno botao--primario"
                type="button"
                :disabled="mexendo"
                @click="entrarNaConversa"
              >
                <i class="bi bi-box-arrow-in-right" aria-hidden="true"></i> Entrar
              </button>
            </div>

            <div v-else class="linha linha--quebra">
              <!-- 🚨 O QUE ABRE A BUSCA (27/08). Fica na fileira das ações da
                   conversa porque é isso que ela é: uma ação eventual. Antes
                   era uma faixa fixa comendo altura em toda conversa.
                   ⚠️ Fica ACESO enquanto a busca está aberta -- botão que
                   alterna e não mostra em que estado está é adivinhação. -->
              <button
                class="botao botao--pequeno botao--icone"
                :class="buscaAberta ? 'botao--primario' : 'botao--contorno'"
                type="button"
                :title="buscaAberta ? 'Fechar a busca' : 'Buscar nesta conversa'"
                :aria-label="buscaAberta ? 'Fechar a busca' : 'Buscar nesta conversa'"
                :aria-pressed="buscaAberta"
                @click="alternarBusca"
              >
                <i class="bi bi-search" aria-hidden="true"></i>
              </button>
              <!-- 🚨 AÇÃO SOBRE A CONVERSA TEM PALAVRA (28/08). Esta fileira
                   tinha CINCO botões só de ícone e UM com texto -- e o único
                   legível era "Concluir atendimento", o que encerra. Tudo que
                   é rotina e reversível era adivinhação.

                   🚨 O PAR PERIGOSO ERA ESTE: "devolver à fila" e "sair da
                   conversa" são duas setas apontando para a ESQUERDA, lado a
                   lado (`arrow-return-left` e `box-arrow-left`), e fazem
                   coisas diferentes -- uma larga a conversa para a fila, a
                   outra tira você dela. Só o balão do navegador distinguia as
                   duas, e ele não existe em toque.

                   ⚠️ A RÉGUA, para as telas seguintes: fica só ícone o que é
                   convenção do gênero E mora colado ao campo que serve (lupa,
                   funil, clipe, microfone, emoji, X). Ganha palavra o que age
                   sobre a conversa. O texto longo continua no `title`. -->
              <button
                class="botao botao--pequeno botao--contorno"
                type="button"
                title="Transferir para outra pessoa ou para um time"
                @click="abrirPainel('transferir')"
              >
                <i class="bi bi-arrow-left-right" aria-hidden="true"></i>
                Transferir
              </button>
              <button
                class="botao botao--pequeno botao--contorno"
                type="button"
                title="Convidar atendentes para esta conversa"
                @click="abrirPainel('convidar')"
              >
                <i class="bi bi-person-plus" aria-hidden="true"></i>
                Convidar
              </button>
              <button
                v-if="aberta.atendente_id"
                class="botao botao--pequeno botao--contorno"
                type="button"
                title="A conversa fica sem dono e volta para a fila"
                @click="pedirParaDevolver"
              >
                <i class="bi bi-arrow-return-left" aria-hidden="true"></i>
                Devolver à fila
              </button>
              <button
                class="botao botao--pequeno botao--contorno"
                type="button"
                :disabled="mexendo"
                title="A conversa some da sua lista"
                @click="pedirParaSair"
              >
                <i class="bi bi-box-arrow-left" aria-hidden="true"></i>
                Sair
              </button>
              <!-- 🔵 23/09: só conversa com UMA pessoa se bloqueia -- grupo não. -->
              <button
                v-if="!aberta.grupo_jid && !aberta.bloqueio"
                class="botao botao--pequeno botao--contorno"
                type="button"
                :disabled="mexendo"
                title="O número deixa de conseguir falar com a empresa pelo WhatsApp"
                @click="pedirParaBloquear"
              >
                <i class="bi bi-slash-circle" aria-hidden="true"></i>
                Bloquear
              </button>
              <span class="espaco"></span>
              <button
                class="botao botao--pequeno botao--contorno"
                type="button"
                @click="abrirPainel('encerrar')"
              >
                <i class="bi bi-check2-square" aria-hidden="true"></i>
                Concluir atendimento
              </button>
            </div>
          </div>

          <!-- 🚨 A FAIXA DE AVISO SAIU (28/08). Ela aparecia em 61% das
               conversas — faixa larga, quatro linhas, dizendo o que o botão
               da ficha logo acima já diz em âmbar e por escrito: não há
               cadastro. Dois lugares para o mesmo estado, e o segundo era o
               que gastava altura.

               ⚠️ O ÚNICO FATO QUE ELA CARREGAVA E O BOTÃO NÃO — o número
               responder por vários cadastros — ficou na gaveta, em uma linha.
               Ele cortou o padrão inteiro: *"elas ajudaram nas etapas de
               lógica, mas agora em teste sujam a tela"*. -->

          <!-- ================= GAVETA DO CONTATO ================= -->
          <aside v-if="gaveta && !ehGrupo" class="gaveta">
            <p class="gaveta__topo">
              <strong>{{ aberta.nome_whatsapp || 'Sem nome no WhatsApp' }}</strong>
              <span class="apagado pequeno mono">{{ aberta.telefone_e164 }}</span>
            </p>

            <!-- ══ AS CONSULTAS, EM UMA BARRA (desenho A, 15/10) ═══════════
                 🔵 *"essa tela dentro da ficha esta redundante e um pouco
                 feia"* → três botões empilhados, do mesmo peso e cada um numa
                 linha, numa gaveta que já luta por altura (`max-height: 45%`).
                 Em barra eles gastam uma linha, e não três.

                 🚨 SÓ CONSULTA MORA AQUI. "Desvincular" apaga um vínculo e
                 está no pé do cartão, de propósito: ação destrutiva não pode
                 ter a mesma cara de um botão de ler.

                 ⚠️ Rótulo CURTO ("Contatos", "Empresas") porque em barra o
                 espaço é lateral; o `title` carrega o nome inteiro, que é o que
                 o leitor de tela anuncia. -->
            <div class="gaveta__acoes">
              <!-- 🔵 15/10, Bloco 5: as LIGAÇÕES deste número, últimos 7 dias.
                   Modal, e não gaveta: é consulta de um minuto. -->
              <button
                class="botao botao--pequeno botao--contorno"
                type="button"
                title="Ligações deste número nos últimos 7 dias"
                @click="verLigacoes"
              >
                <i class="bi bi-telephone" aria-hidden="true"></i>
                Ligações
              </button>

              <!-- 🔵 15/10, Bloco 6: os outros contatos da MESMA empresa. Só
                   aparece havendo empresa: sem ela o modal só teria "este
                   número não está no cadastro", que a própria ficha já diz. -->
              <button
                v-if="aberta.empresa && aberta.empresa.cliente && !ehTecnico"
                class="botao botao--pequeno botao--contorno"
                type="button"
                title="Outros contatos desta empresa"
                @click="verOutrosContatos"
              >
                <i class="bi bi-person-lines-fill" aria-hidden="true"></i>
                Contatos
              </button>

              <!-- Empresas do grupo desta pessoa. Consulta, não escolha: o
                   número identifica quem fala, não a empresa do assunto.
                   🔵 07/10: escondido para TÉCNICO — técnico é pessoa, não se
                   liga a empresa (decisão dele). -->
              <button
                v-if="!ehTecnico"
                class="botao botao--pequeno botao--contorno"
                type="button"
                :aria-expanded="empresasAbertas"
                :title="empresasAbertas
                  ? 'Ocultar as empresas deste número'
                  : 'Todas as empresas que este número alcança'"
                @click="verEmpresas"
              >
                <i class="bi bi-buildings" aria-hidden="true"></i>
                {{ empresasAbertas ? 'Ocultar' : 'Empresas' }}
              </button>
            </div>

            <!-- 🔵 21/10: o vínculo com o cadastro de técnicos do Harmonit.
                 🚨 É daqui que sai o NOME QUE O ORÇAMENTO IMPRIME -- *"mesmo
                 que nomeiem errado, ele vai trazer no orçamento o nome do
                 cadastro harmonit"*. -->
            <div v-if="ehTecnico" class="gaveta__bloco vinculo">
              <p class="vinculo__titulo">Técnico no Harmonit</p>

              <p v-if="vinculoCarregando" class="vinculo__fraco">Lendo…</p>

              <template v-else-if="vinculoTecnico && vinculoTecnico.harmonit_tecnico_id">
                <p class="vinculo__nome">
                  {{ vinculoTecnico.harmonit_tecnico_nome }}
                  <small>
                    ({{ vinculoTecnico.harmonit_vinculo_origem === 'manual'
                        ? 'vinculado à mão' : 'pelo telefone' }})
                  </small>
                </p>
                <p class="vinculo__fraco">
                  É este o nome que sai no orçamento.
                </p>
                <button class="botao botao--pequeno botao--contorno" type="button"
                        @click="definirVinculoTecnico(null)">Desvincular</button>
              </template>

              <template v-else>
                <p class="vinculo__fraco">
                  <template v-if="vinculoTecnico && vinculoTecnico.sugestao">
                    Pelo telefone, parece ser
                    <b>{{ vinculoTecnico.sugestao.nome }}</b>.
                  </template>
                  <template v-else>
                    Este telefone não bate com nenhum cadastro de técnico do
                    Harmonit. O orçamento sai com o nome daqui.
                  </template>
                </p>
                <button v-if="vinculoTecnico && vinculoTecnico.sugestao"
                        class="botao botao--pequeno"
                        type="button"
                        @click="definirVinculoTecnico(vinculoTecnico.sugestao.tecnico_id)">
                  Vincular a {{ vinculoTecnico.sugestao.nome }}
                </button>

                <div class="vinculo__busca">
                  <input v-model="buscaTecnico" class="campo__entrada"
                         placeholder="Buscar no cadastro por nome ou e-mail"
                         @keyup.enter="buscarTecnicoHarmonit" />
                  <button class="botao botao--pequeno botao--contorno" type="button"
                          @click="buscarTecnicoHarmonit">Buscar</button>
                </div>
                <ul v-if="tecnicosAchados.length" class="vinculo__lista">
                  <li v-for="t in tecnicosAchados" :key="t.tecnico_id">
                    <button type="button" class="vinculo__item"
                            @click="definirVinculoTecnico(t.tecnico_id)">
                      <span>{{ t.nome }}</span>
                      <small>{{ t.email }}</small>
                    </button>
                  </li>
                </ul>
              </template>

              <p v-if="vinculoErro" class="aviso aviso--erro">{{ vinculoErro }}</p>
            </div>

            <div v-if="empresasAbertas && !ehTecnico" class="gaveta__bloco">
              <p v-if="buscandoEmpresas" class="apagado pequeno">consultando…</p>
              <p v-else-if="!empresas.length" class="apagado pequeno">
                Este número não está em nenhum cadastro.
              </p>
              <ul v-else class="gaveta__lista">
                <li v-for="e in empresas" :key="e.id" class="gaveta__empresa">
                  <strong>{{ e.nome }}</strong>
                  <span v-if="!e.ativo" class="chip chip--erro">inativa</span>
                  <span class="apagado pequeno mono">{{ documentoLegivel(e.documento) || 'sem CNPJ' }}</span>
                  <span class="apagado pequeno">contato: {{ e.contato_nome }}</span>
                </li>
              </ul>
              <p v-if="empresas.length > 1" class="apagado pequeno">
                {{ empresas.length }} empresas do mesmo responsável.
              </p>
            </div>

            <!-- 🚨 TRÊS ESTADOS, NÃO DOIS (28/08). Havia "com empresa" e "sem
                 nada". Desde que o tipo pode ser marcado sem vincular empresa,
                 existe o do meio -- cadastro sem empresa -- e sem este ramo a
                 gaveta continuaria dizendo "Sem cadastro" logo depois de a
                 pessoa ter criado um. A tela negaria o que ela acabou de
                 fazer. Achado na validação, antes de escrever. -->
            <p v-if="aberta.empresa && aberta.empresa.contato && !aberta.empresa.cliente"
               class="chip chip--aviso">Cadastro sem empresa</p>

            <!-- COM vínculo: os dados da empresa -->
            <template v-if="aberta.empresa && aberta.empresa.cliente">
              <!-- ══ A EMPRESA, EM CARTÃO (desenho A, 15/10) ═══════════════
                   Antes eram quatro pares rótulo/valor com o MESMO peso --
                   "Empresa", "Nome fantasia", "CNPJ/CPF", "E-mail" --, e o
                   fato mais útil da ficha (de quem é a conversa) não se
                   distinguia do rótulo ao lado. Aqui a razão social tem peso,
                   a fantasia é subtítulo, e CNPJ e e-mail descem para uma
                   linha de apoio: a mesma informação, em menos altura e com
                   onde olhar primeiro. -->
              <div class="gaveta__cartao">
                <p class="gaveta__razao">
                  {{ aberta.empresa.cliente.nome }}
                  <span v-if="!aberta.empresa.cliente.ativo" class="chip chip--erro">inativo</span>
                </p>
                <p v-if="aberta.empresa.cliente.nome_fantasia" class="gaveta__fantasia">
                  {{ aberta.empresa.cliente.nome_fantasia }}
                </p>
                <div v-if="aberta.empresa.cliente.documento || aberta.empresa.cliente.email"
                     class="gaveta__meta pequeno">
                  <span v-if="aberta.empresa.cliente.documento">
                    <i class="bi bi-hash" aria-hidden="true"></i>
                    <span class="mono">{{ documentoLegivel(aberta.empresa.cliente.documento) }}</span>
                  </span>
                  <span v-if="aberta.empresa.cliente.email">
                    <i class="bi bi-envelope" aria-hidden="true"></i>
                    {{ aberta.empresa.cliente.email }}
                  </span>
                </div>
              </div>

              <dl class="gaveta__dados">
                <!-- ⚠️ NÃO mostrar `contato.papeis` aqui. Os papéis existem no
                     banco desde a migração 001, vieram do modelo do ERP e não
                     acionam nada; anunciá-los na ficha promete recurso que não
                     existe, sobre um eixo que não é do atendimento. -->
                <dt>Contato</dt>
                <!-- 🔵 07/10: LEAD deixa o nome do contato editável; 🔵 19/10:
                     TÉCNICO também. Nos outros tipos, só-leitura. O que foi salvo
                     permanece ao trocar de tipo, porque mora em `contato.nome`. -->
                <dd v-if="nomeEditavel" class="gaveta__nome-edit">
                  <input
                    v-model="rascunhoNome"
                    class="campo__entrada campo__entrada--compacto"
                    type="text"
                    maxlength="120"
                    :placeholder="aberta.empresa.contato.nome"
                    @keyup.enter="salvarNomeContato"
                  />
                  <button
                    class="botao botao--pequeno botao--contorno"
                    type="button"
                    :disabled="salvandoNome || !rascunhoNome.trim()
                               || rascunhoNome.trim() === aberta.empresa.contato.nome"
                    @click="salvarNomeContato"
                  >Salvar</button>
                  <span v-if="nomeSalvo" class="chip chip--ok">gravado</span>
                </dd>
                <!-- 🚨 15/10: NOME DERIVADO NÃO SE MOSTRA COMO NOME. Sem
                     ninguém ter digitado um, o backend usa o apelido do
                     WhatsApp ou o próprio telefone -- e aí a ficha repetia o
                     número que o topo já diz, fazendo falta de cadastro
                     parecer cadastro. Foi o que ele viu: o mesmo número três
                     vezes na mesma tela. -->
                <dd v-else-if="contatoSemNome" class="apagado">
                  sem nome no cadastro
                </dd>
                <dd v-else>{{ aberta.empresa.contato.nome }}</dd>
                <template v-if="aberta.empresa.contato.email">
                  <dt>E-mail do contato</dt>
                  <dd>{{ aberta.empresa.contato.email }}</dd>
                </template>

                <!-- 🚨 O TIPO DECIDE SE A AUTOMAÇÃO E A IA ATENDEM ESTA
                     PESSOA (CFG_5.1). Fica na ficha porque quem fala com ela
                     é quem sabe o que ela é. -->
                <dt>Tipo</dt>
                <dd>
                  <!-- ⚠️ Mesma classe da ficha do contato (CAD_1.2): o tipo é
                       um campo só e tem de ter uma cara só. -->
                  <select
                    v-if="podeTrocarTipo"
                    :value="aberta.empresa.contato.relacao"
                    class="campo__entrada campo__entrada--compacto"
                    @change="trocarTipo($event.target.value)"
                  >
                    <option v-for="[valor, rotulo] in RELACOES" :key="valor" :value="valor">
                      {{ rotulo }}
                    </option>
                  </select>
                  <span v-else class="chip">
                    {{ NOME_RELACAO[aberta.empresa.contato.relacao]
                       || aberta.empresa.contato.relacao }}
                  </span>
                  <span v-if="tipoSalvo" class="chip chip--ok">gravado</span>
                </dd>
              </dl>

              <!-- ⚠️ AÇÃO DESTRUTIVA SEPARADA DAS CONSULTAS (desenho A).
                   "Desvincular" estava na mesma pilha e com o mesmo peso de
                   "Ligações" e "Empresas vinculadas" -- três botões de ler e
                   um de apagar, iguais. Aqui ele fica no pé, depois de um
                   fio, ao lado do fato que dá contexto a ele. -->
              <div class="gaveta__pe">
                <span class="apagado pequeno">
                  {{ empresas.length > 1
                     ? empresas.length + ' empresas vinculadas'
                     : '1 empresa vinculada' }}
                </span>
                <button class="botao botao--pequeno botao--fantasma" type="button"
                        title="Desfaz o vínculo deste número com a empresa"
                        @click="desvincular">
                  <i class="bi bi-x-circle" aria-hidden="true"></i> Desvincular
                </button>
              </div>
            </template>

            <!-- SEM EMPRESA: o caso comum. Marcar o tipo e/ou vincular. -->
            <template v-else>
              <p v-if="!aberta.contato_id" class="chip chip--aviso">
                Não está no cadastro
              </p>

              <!-- 🚨 O TIPO APARECE MESMO SEM CADASTRO (27/08). Ele apontou
                   que a lista "não aparece na Ficha do contato" -- e não
                   aparecia mesmo: o Tipo é coluna de CONTATO, e **63% das
                   conversas abertas não têm contato** (234 de 374, medido).

                   Só que a pessoa TEM um tipo: a automação e a IA já a tratam
                   como `sem_cadastro`, e esse é o tipo que decide se elas
                   respondem. Esconder isso deixava a ficha muda justamente no
                   caso mais comum.

                   ⚠️ É A REGRA QUE VOCÊ APROVOU NA ESCADA DA IA: nada some;
                   o que não dá para mudar aparece dizendo o que falta para
                   destravar. Trocar exige vincular, porque é no contato que o
                   tipo mora -- criar contato a partir daqui é decisão sua, e
                   não a tomei.

                   🚨 "SEM CADASTRO" NÃO ESTÁ EM `RELACOES` DE PROPÓSITO, e
                   não é esquecimento: `contato.relacao` tem 8 valores no CHECK
                   do banco, e este não é um deles. Ele é chave da
                   `relacao_automacao` -- a linha que decide o que fazer com
                   quem NÃO tem contato. Pôr no seletor faria a tela oferecer
                   um valor que o banco recusa. -->
              <!-- 🚨 O SELETOR APARECE SEM CADASTRO (28/08), e escolher CRIA
                   o contato. Antes aqui havia um chip morto dizendo "Sem
                   cadastro" -- estado, sem saída. O tipo mora em `contato`, e
                   o que faltava não era o campo: era o registro.

                   🚫 CORRIGIDO EM 18/09: `sem_identificacao` VOLTOU à lista.
                   Eu o tinha tirado em 28/08 chamando-o de valor de
                   nascimento; ele corrigiu -- *"sem identificação é sem
                   identificação mesmo, não é só porque tem empresa vinculada
                   que é cliente. esse cad deve ser feito a mão"*. É estado
                   real de quem ninguém classificou ainda, e sem ele na lista
                   não havia como desfazer uma marcação errada daqui. -->
              <dl class="gaveta__dados">
                <dt>Tipo</dt>
                <dd>
                  <select
                    v-if="podeMarcarTipo"
                    v-model="tipoAtual"
                    class="campo__entrada campo__entrada--compacto"
                  >
                    <!-- O placeholder só existe enquanto é verdade: com
                         contato criado não há como voltar a "sem cadastro",
                         e opção que não leva a lugar nenhum é ruído. Ele
                         fica selecionável (não cinza) porque o painel inteiro
                         não tem uma única `<option>` desabilitada -- placeholder
                         aqui é `<option value="">`, como nas outras 7. -->
                    <option v-if="!aberta.contato_id" value="">sem cadastro</option>
                    <option v-for="[valor, rotulo] in RELACOES"
                            :key="valor" :value="valor">{{ rotulo }}</option>
                  </select>
                  <!-- 🚨 O CHIP MENTIA IGUAL AO SELETOR (18/09): dizia sempre
                       "Sem cadastro", inclusive para quem já tinha tipo. Quem
                       não pode marcar continua sem poder -- mas passa a LER a
                       verdade. -->
                  <span v-else class="chip">
                    {{ NOME_RELACAO[tipoAtual] || 'Sem cadastro' }}
                  </span>
                </dd>

                <!-- 🔵 07/10: LEAD sem empresa também edita o nome. Só quando
                     já existe contato (o nome mora nele). 🔵 19/10: vale para
                     técnico também, e é aqui que cai, porque técnico não tem
                     empresa por desenho. -->
                <template v-if="nomeEditavel && aberta.empresa && aberta.empresa.contato">
                  <dt>Nome</dt>
                  <dd class="gaveta__nome-edit">
                    <input
                      v-model="rascunhoNome"
                      class="campo__entrada campo__entrada--compacto"
                      type="text"
                      maxlength="120"
                      :placeholder="aberta.empresa.contato.nome"
                      @keyup.enter="salvarNomeContato"
                    />
                    <button
                      class="botao botao--pequeno botao--contorno"
                      type="button"
                      :disabled="salvandoNome || !rascunhoNome.trim()
                                 || rascunhoNome.trim() === aberta.empresa.contato.nome"
                      @click="salvarNomeContato"
                    >Salvar</button>
                    <span v-if="nomeSalvo" class="chip chip--ok">gravado</span>
                  </dd>
                </template>
              </dl>

              <!-- 🚨 SELO AMARELO: informação sem afirmação. Diz que a pessoa
                   aparece no Bitrix, sem dizer que ela é cliente. -->
              <div v-if="aberta.bitrix" class="gaveta__bitrix">
                <strong class="pequeno">Aparece no Bitrix</strong>
                <span v-if="aberta.bitrix.nome">{{ aberta.bitrix.nome }}</span>
                <span v-if="aberta.bitrix.empresa" class="apagado pequeno">
                  {{ aberta.bitrix.empresa }}
                </span>
                <span class="linha pequeno">
                  <span v-if="aberta.bitrix.tipo" class="chip">{{ aberta.bitrix.tipo }}</span>
                  <span v-if="aberta.bitrix.cargo" class="apagado">{{ aberta.bitrix.cargo }}</span>
                </span>
                <span class="apagado pequeno">Sistema antigo — não é vínculo.</span>
              </div>
              <!-- 🚨 A ESCOLHA DA EMPRESA SAIU DAQUI E VIROU MODAL (28/08).
                   Pedido dele: *"ao selecionar algumas empresas pelo campo de
                   busca, na ficha, para vínculo, não está exibindo claramente,
                   verifique o uso de uma caixa modal, acaba sendo uma saída
                   para não ficar espremendo as coisas"*.

                   Ele estava certo, e dá para medir: a gaveta tem teto de
                   `42vh` e já carregava, ACIMA da lista, o nome, o telefone,
                   o botão de empresas, o Tipo, o selo do Bitrix, um parágrafo,
                   o bloco de candidatos e o campo de busca. Os 10 resultados
                   que o backend devolve (teto da rota) caíam no que sobrasse
                   -- perto de 180px numa janela de 900px, com uma SEGUNDA
                   barra de rolagem dentro da primeira.

                   ⚠️ A gaveta fica com o que é ESTADO (quem é, que tipo é, o
                   que o Bitrix acha); o modal fica com o que é ESCOLHA. Era a
                   mistura dos dois no mesmo teto que espremia. -->
              <button
                v-if="!ehTecnico"
                class="botao botao--pequeno botao--primario"
                type="button"
                @click="abrirPainel('vincular')"
              >
                <i class="bi bi-link-45deg" aria-hidden="true"></i>
                Vincular a uma empresa
              </button>
              <!-- O único fato que a faixa de aviso carregava e o botão da
                   ficha não carrega. Uma linha, sem a lição. -->
              <p v-if="aberta.candidatos && aberta.candidatos.length"
                 class="apagado pequeno">
                Responde por {{ aberta.candidatos.length }} cadastros.
              </p>
            </template>
          </aside>

          <!-- BUSCAR NA CONVERSA — outra pergunta que a busca da lista:
               lá é "com quem eu falei", aqui é "onde ele disse isso". -->
          <!-- 🚨 A BUSCA SÓ APARECE QUANDO SE PEDE (27/08, pedido dele: *"o
               campo de busca na conversa, pode abrir de um botão, pois ocupa
               muito espaço"*). Ela é uma ação eventual ocupando uma faixa fixa
               na altura mais disputada da tela — e altura, aqui, é conversa
               visível.

               ⚠️ FECHAR LIMPA O TERMO. Deixar o termo guardado esconderia um
               filtro ativo: os balões continuariam marcados e o contador
               sumiria, sem nada dizendo por quê. -->
          <div v-if="buscaAberta" class="buscaconversa">
            <div class="busca">
              <input
                v-model="buscaNaConversa"
                class="campo__entrada"
                type="search"
                placeholder="Buscar na conversa"
                ref="campoBuscaConversa"
                aria-label="Buscar na conversa"
                @keyup.enter="irParaAchado(1)"
                @keydown.esc="fecharBusca"
                :disabled="!aberta"
              />
              <template v-if="buscaNaConversa.trim()">
                <span class="pequeno fraco buscaconversa__conta">
                  {{ achadosNaConversa.length
                     ? `${achadoAtual + 1}/${achadosNaConversa.length}`
                     : '0' }}
                </span>
                <button
                  class="botao botao--pequeno botao--contorno botao--icone"
                  type="button"
                  :disabled="!achadosNaConversa.length"
                  title="Ocorrência anterior"
                  aria-label="Ocorrência anterior"
                  @click="irParaAchado(-1)"
                >
                  <i class="bi bi-chevron-up" aria-hidden="true"></i>
                </button>
                <button
                  class="botao botao--pequeno botao--contorno botao--icone"
                  type="button"
                  :disabled="!achadosNaConversa.length"
                  title="Próxima ocorrência"
                  aria-label="Próxima ocorrência"
                  @click="irParaAchado(1)"
                >
                  <i class="bi bi-chevron-down" aria-hidden="true"></i>
                </button>
              </template>
              <button
                class="botao botao--pequeno botao--fantasma botao--icone"
                type="button"
                title="Fechar a busca"
                aria-label="Fechar a busca"
                @click="fecharBusca"
              >
                <i class="bi bi-x-lg" aria-hidden="true"></i>
              </button>
            </div>
            <!-- ⚠️ O AVISO DE "TRUNCADA" SAIU. Ele existia porque a busca só
                 via o que estava carregado; agora ela roda no servidor e
                 alcança a conversa inteira, então "nada com esse termo" é
                 verdade quando aparece. -->
            <p v-if="buscandoNaConversa" class="linha pequeno fraco">
              <span class="girando"></span> procurando…
            </p>
            <p v-else-if="achadosLimitados" class="chip chip--aviso pequeno">
              Mostrando os primeiros {{ achadosNaConversa.length }} acertos —
              use um termo mais específico.
            </p>
            <p v-else-if="buscaNaConversa.trim() && !achadosNaConversa.length"
               class="apagado pequeno">
              Nada com esse termo nesta conversa.
            </p>
          </div>

          <div ref="baloes" class="baloes">
            <!-- 🚨 O TOPO DA CONVERSA. A tela abre com as 60 mais recentes e
                 sobe de 200 em 200: o teto de 1.000 saiu quando a maior
                 conversa da base chegou a 776. -->
            <div v-if="aberta.tem_anteriores" class="anteriores">
              <button
                class="botao botao--pequeno botao--contorno"
                type="button"
                :disabled="carregandoAnteriores"
                @click="carregarAnteriores"
              >
                <span v-if="carregandoAnteriores" class="girando"></span>
                <i v-else class="bi bi-arrow-up" aria-hidden="true"></i>
                Carregar {{ aberta.janela }} anteriores
              </button>
            </div>
            <template v-for="(m, i) in aberta.mensagens" :key="m.id">
            <!-- 🔵 07/10: fronteira entre atendimentos do mesmo número no fio
                 contínuo (transferência/reabertura). -->
            <p v-if="comecaOutraConversa(m, i)" class="fio-anterior">
              <span class="fio-anterior__marca">
                <i class="bi bi-arrow-down-up" aria-hidden="true"></i>
                nova conversa · {{ rotuloDoDia(m.criada_em) }}
              </span>
            </p>
            <p v-if="comecaODia(m, i)" class="diario">
              <span class="diario__marca">{{ rotuloDoDia(m.criada_em) }}</span>
            </p>
            <!-- ══ EVENTO: LINHA, NÃO BALÃO (Bloco 11, 15/10) ════════════
                 🚨 Como balão, o log entraria no fio com o mesmo peso do que o
                 cliente escreveu e ganharia o menu de editar e apagar --
                 justamente o que um log não pode ter. Como linha discreta ele
                 se lê de relance e se ignora de relance.

                 ⚠️ `data-mensagem` fica: é por ele que `irParaMensagem` rola
                 até uma linha quando a busca casa com o texto dela. -->
            <p v-if="m.evento" class="evento" :data-mensagem="m.id">
              <i class="bi" :class="ICONE_EVENTO[m.evento] || 'bi-dot'"
                 aria-hidden="true"></i>
              <span>
                <strong v-if="m.atendente_nome">{{ m.atendente_nome }}</strong>
                {{ m.conteudo }}
              </span>
              <span class="evento__hora mono">{{ hora(m.criada_em) }}</span>
            </p>

            <div
              v-else
              class="balao"
              :class="[`balao--${m.direcao}`, {
                'balao--casa': casaNaConversa(m),
                'balao--atual': m.id === idAchado,
                'balao--tocado': m.id === balaoTocado,
                'balao--realcado': m.id === balaoRealcado,
              }]"
              :data-mensagem="m.id"
              :data-nota="m.tipo === 'nota' && m.leitura_rastreada ? m.id : null"
              @click="tocarBalao(m, $event)"
            >
              <!-- A mensagem que esta está respondendo. Clicar rola até ela e
                   a destaca (29/09) -- o mesmo gesto do WhatsApp. -->
              <p v-if="m.citada_id" class="balao__citada pequeno"
                 @click.stop="irParaMensagem(m.citada_id)">
                <i class="bi bi-reply" aria-hidden="true"></i>
                <span class="fraco">{{ m.citada_autor === 'cliente' ? 'cliente' : 'nós' }}:</span>
                {{ m.citada_conteudo || `(${m.citada_tipo})` }}
              </p>

              <!-- 🚨 CLICAR ABRE EM TELA CHEIA. A foto abria do tamanho do
                   balão, e print de erro e foto de avaria são exatamente o
                   que precisa de zoom. -->
              <img
                v-if="m.midia_id && ['imagem', 'figurinha'].includes(tipoDaMidia(m)) && midias[m.midia_id]"
                :src="midias[m.midia_id]"
                class="balao__imagem balao__imagem--clicavel"
                :alt="m.conteudo || 'imagem da conversa'"
                @click="emTelaCheia = midias[m.midia_id]"
              />
              <audio
                v-else-if="m.midia_id && tipoDaMidia(m) === 'audio' && midias[m.midia_id]"
                :src="midias[m.midia_id]"
                controls
                class="balao__audio"
              ></audio>
              <video
                v-else-if="m.midia_id && tipoDaMidia(m) === 'video' && midias[m.midia_id]"
                :src="midias[m.midia_id]"
                controls
                class="balao__imagem"
              ></video>

              <p v-if="m.tipo !== 'texto' && (m.midia_id || m.tipo !== 'nota')"
                 class="balao__tipo pequeno">
                <i class="bi" :class="ICONE[tipoDaMidia(m)] || 'bi-paperclip'" aria-hidden="true"></i>
                {{ m.midia_nome || tipoDaMidia(m) }}
                <span v-if="m.midia_tamanho" class="fraco">· {{ tamanhoLegivel(m.midia_tamanho) }}</span>
                <button
                  v-if="m.midia_id"
                  class="botao botao--pequeno botao--fantasma"
                  type="button"
                  @click="baixarMidia(m)"
                >
                  <i class="bi bi-download" aria-hidden="true"></i> Baixar
                </button>
                <span v-else class="fraco">— sem arquivo guardado</span>
              </p>
              <!-- 🚨 NOTA SEM AUTOR NÃO SERVE PARA CONSULTAR DEPOIS. O nome já
                   vinha da API (`mensagens()` faz o JOIN em atendente desde
                   sempre) e a tela simplesmente não o imprimia: meses depois,
                   "cliente pediu desconto" não dizia quem tinha escrito. -->
              <!-- 🚨 EM GRUPO, QUEM FALOU NÃO É A CONVERSA. Sem esta linha o
                   histórico de um grupo de quinze vira monólogo de autor
                   desconhecido. Só na ENTRADA: o que sai é nosso. -->
              <p v-if="ehGrupo && m.direcao === 'entrada' && m.remetente_nome"
                 class="balao__autor pequeno">
                <i class="bi bi-person" aria-hidden="true"></i>
                {{ m.remetente_nome }}
              </p>
              <p v-if="m.tipo === 'nota'" class="balao__autor pequeno">
                <i class="bi bi-sticky" aria-hidden="true"></i>
                Nota de <strong>{{ m.atendente_nome || 'autor não registrado' }}</strong>
              </p>
              <!-- 🔵 Relato da Erika (15/09): abrindo a conversa de outro
                   atendente, nenhuma resposta diz quem escreveu -- só a nota
                   interna dizia. O nome já vinha da API, só a tela não
                   imprimia pra este caso. -->
              <p v-if="m.direcao === 'saida' && m.tipo !== 'nota' && m.atendente_nome"
                 class="balao__autor pequeno">
                <i class="bi bi-person-badge" aria-hidden="true"></i>
                {{ m.atendente_nome }}
              </p>
              <!-- Pedaços, não `v-html`: o texto é do cliente. Link vira
                   `<a>` pelo `:href` do Vue -- a URL nunca passa por HTML
                   bruto, só pelo mesmo recorte que já existia. -->
              <!-- 🚨 APAGADA PARA TODOS: o texto sai da vista, mas NÃO some
                   do registro. O atendente agiu sobre o que leu, e pode
                   precisar dizer depois o que foi dito — por isso fica atrás
                   de um clique, em vez de sumir ou continuar exposto. -->
              <p v-if="m.apagada_em && !originalAberto.has(m.id)"
                 class="balao__texto balao__apagada">
                <i class="bi bi-slash-circle" aria-hidden="true"></i>
                mensagem excluída
                <button v-if="m.conteudo" type="button" class="balao__revelar"
                        @click="alternarOriginal(m.id)">ver o que dizia</button>
              </p>
              <p v-else-if="m.conteudo" class="balao__texto">
                <template v-for="(p, i) in marcar(m.conteudo, buscaNaConversa)" :key="i">
                  <template v-for="(q, j) in linkificar(p.texto)" :key="`${i}-${j}`">
                    <a v-if="q.link" :href="q.texto" target="_blank"
                       rel="noopener noreferrer" class="balao__link"
                       :class="{ 'achado': p.casa }">{{ q.texto }}</a>
                    <span v-else :class="{ 'achado': p.casa }">{{ q.texto }}</span>
                  </template>
                </template>
              </p>
              <p v-else class="balao__texto fraco">(sem texto)</p>
              <!-- Revelada: diz que está revelada, e deixa esconder de novo. -->
              <p v-if="m.apagada_em && originalAberto.has(m.id)"
                 class="balao__marca pequeno">
                <i class="bi bi-slash-circle" aria-hidden="true"></i>
                excluída ·
                <button type="button" class="balao__revelar"
                        @click="alternarOriginal(m.id)">esconder</button>
              </p>
              <!-- 🔵 15/09: localização mostrava só o ícone e a palavra
                   "localizacao". A coordenada sempre chegou no payload; o que
                   faltava era extrair (feito no parser) e dar o que fazer com
                   ela — que é abrir o mapa. -->
              <a
                v-if="m.tipo === 'localizacao' && coordenadaDe(m.conteudo)"
                class="balao__mapa pequeno"
                :href="`https://www.google.com/maps?q=${coordenadaDe(m.conteudo)}`"
                target="_blank"
                rel="noopener"
              >
                <i class="bi bi-geo-alt" aria-hidden="true"></i> Ver no mapa
              </a>
              <!-- Veio de outra conversa: o histórico precisa dizer isso.
                   Seis meses depois ninguém sabe se a frase foi escrita para
                   este cliente ou repassada. -->
              <p v-if="m.encaminhada_de" class="balao__marca pequeno">
                <i class="bi bi-arrow-right" aria-hidden="true"></i> encaminhada
              </p>

              <!-- O texto ANTES da edição, quando alguém pede para ver.
                   🚨 Fica guardado porque o atendente AGIU sobre o que leu:
                   se o cliente corrige depois, o que foi lido não pode sumir
                   do registro. Mesma razão de "esconder conversa é para mim,
                   não para o outro" (27/08). -->
              <p v-if="m.editada_em && m.conteudo_original && originalAberto.has(m.id)"
                 class="balao__original pequeno">
                <template v-if="m.tipo === 'nota'">editada por {{ m.atendente_nome || 'quem escreveu' }}, em {{ hora(m.editada_em) }} · </template>
                antes: {{ m.conteudo_original }}
              </p>
              <!-- 🚨 EDIÇÃO CIFRADA (06/10): o cliente editou e a versão nova
                   chegou cifrada; o balão segue com o texto ANTIGO. Sem esta
                   linha, o selo "editada" faria o atendente ler o antigo como
                   se fosse o novo. `conteudo_original` NULL é o sinal.
                   🔵 07/10: só a ação, sem "ilegível"/"cifrada" (decisão dele;
                   upgrade do Evolution e patch do Baileys descartados). -->
              <p v-if="m.editada_em && !m.conteudo_original && originalAberto.has(m.id)"
                 class="balao__original pequeno">
                O texto acima é o de antes da edição.
              </p>

              <p class="balao__rodape apagado pequeno">
                {{ hora(m.criada_em) }}
                <!-- Só na saída: quem respondeu pelo painel. O eco do WhatsApp
                     chega sem atendente, e aí não há nome a mostrar. -->
                <span v-if="m.direcao === 'saida' && m.atendente_nome">
                  · {{ m.atendente_nome }}
                </span>
                <!-- "editada" ao lado da hora, como no WhatsApp: é onde quem
                     atende procura. 🚨 PALAVRA VISÍVEL, não `title` -- balão
                     do navegador demora ~1 s e não existe em toque (M10).
                     Clicar mostra o texto de antes, para quem precisa
                     conferir o que leu. -->
                <button v-if="m.editada_em" type="button" class="balao__editada"
                        :aria-expanded="originalAberto.has(m.id)"
                        :title="m.conteudo_original ? 'ver o texto anterior' : 'ver detalhe da edição'"
                        @click="alternarOriginal(m.id)">
                  · editada
                </button>
                <!-- 🚨 O TIQUE, não a palavra (27/08). "enviada / entregue /
                     lida" é vocabulário nosso, do CHECK do banco; quem atende
                     lê ✓ e ✓✓ sem pensar. O segundo tique fica AZUL só quando
                     foi lida -- é a única diferença entre entregue e lida, e é
                     a que o atendente procura.
                     ⚠️ `title` mantém a palavra, para quem passar o mouse e
                     para leitor de tela: o símbolo não pode ser a única
                     fonte. -->
                <span v-if="TIQUE[m.entrega]"
                      class="balao__tique"
                      :class="{ 'balao__lida': m.entrega === 'lida' }"
                      :title="m.entrega">{{ TIQUE[m.entrega] }}</span>
                <span v-else-if="m.entrega"> · {{ m.entrega }}</span>

                <!-- 🔵 15/10, Bloco 12: "visto por", junto ao carimbo, bem
                     pequeno -- do tamanho que ele pediu.
                     🚨 O OLHO NÃO DIZ "LIDA". Ele diz quantas pessoas tiveram
                     a nota na tela, e o popover diz quem e quando. -->
                <button v-if="m.tipo === 'nota'" type="button" class="balao__visto"
                        :aria-expanded="vistoAberto === m.id"
                        title="quem teve esta nota na tela"
                        @click.stop="alternarVisto(m.id)">
                  · <i class="bi"
                       :class="m.vistos && m.vistos.length ? 'bi-eye-fill' : 'bi-eye'"
                       aria-hidden="true"></i>
                  <template v-if="m.vistos && m.vistos.length">{{ m.vistos.length }}</template>
                </button>
              </p>

              <!-- O popover do "visto por". Fora do rodapé porque pode ter
                   várias linhas, e o rodapé é de uma. -->
              <p v-if="m.tipo === 'nota' && vistoAberto === m.id"
                 class="balao__vistos pequeno">
                <!-- 🚨 "NÃO HAVIA REGISTRO" NÃO É "NINGUÉM VIU". Nota anterior
                     ao início do registro não pode aparecer como não vista por
                     ninguém: seria uma afirmação falsa sobre o passado.
                     ⚠️ Sem data escrita aqui: a data mora no backend
                     (`LEITURA_DE_NOTA_DESDE`), e repeti-la na tela criaria uma
                     segunda verdade para divergir no dia em que ela mudar. -->
                <template v-if="!m.leitura_rastreada">
                  Sem registro: esta nota é anterior ao dia em que o MoviZap
                  passou a anotar quem vê.
                </template>
                <template v-else-if="!m.vistos || !m.vistos.length">
                  Ninguém teve esta nota na tela ainda.
                </template>
                <template v-else>
                  <strong>Visto por</strong>
                  <span v-for="v in m.vistos" :key="v.atendente_id"
                        class="balao__visto-quem">
                    {{ v.nome }} <span class="apagado">{{ hora(v.visto_em) }}</span>
                  </span>
                </template>
              </p>

              <!-- A reação fica PENDURADA no canto do balão, como no
                   WhatsApp: dentro dele, viraria mais uma linha de texto.
                   ⚠️ A CONTA SÓ APARECE A PARTIR DE DOIS. Num grupo, "👍 3"
                   é informação; numa conversa direta, "👍 1" é ruído em todo
                   balão. -->
              <span v-if="(m.reacoes || []).length" class="balao__reacao">
                <span v-for="r in m.reacoes" :key="r.emoji"
                      class="balao__reacao-item"
                      :class="{ 'balao__reacao-item--nosso': r.nosso }"
                      :title="r.nosso ? 'inclui a sua reação' : ''">
                  {{ r.emoji }}<em v-if="r.n > 1">{{ r.n }}</em>
                </span>
              </span>

              <!-- ⚠️ AS AÇÕES APARECEM NO HOVER, não sempre. Três botões fixos
                   em cada balão transformam o fio numa grade de botões, e o
                   que se lê é a conversa. -->
              <div v-if="posso && (m.tipo !== 'nota' || minhaNota(m))" class="balao__acoes">
                <button v-if="m.tipo !== 'nota'" class="balao__acao" type="button"
                        title="Responder citando"
                        aria-label="Responder citando" @click="citar(m)">
                  <i class="bi bi-reply" aria-hidden="true"></i>
                </button>
                <button v-if="m.tipo !== 'nota'" class="balao__acao" type="button"
                        title="Reagir" aria-label="Reagir"
                        @click="reagindoEm = reagindoEm === m.id ? null : m.id">
                  <i class="bi bi-emoji-smile" aria-hidden="true"></i>
                </button>
                <button v-if="m.tipo !== 'nota' && m.conteudo && !m.midia_id"
                        class="balao__acao"
                        type="button" title="Encaminhar" aria-label="Encaminhar"
                        @click="abrirEncaminhar(m)">
                  <i class="bi bi-arrow-right" aria-hidden="true"></i>
                </button>
                <!-- 🔵 23/09: só na MINHA mensagem, e dentro da janela do
                     WhatsApp (15 min para editar, 48 h para apagar).
                     🔵 28/09: a nota é minha, editável/apagável sem janela. -->
                <button v-if="podeEditar(m)" class="balao__acao" type="button"
                        :title="m.tipo === 'nota' ? 'Editar nota' : 'Editar (até 15 minutos depois de enviar)'"
                        :aria-label="m.tipo === 'nota' ? 'Editar nota' : 'Editar mensagem'"
                        @click="abrirEdicao(m)">
                  <i class="bi bi-pencil" aria-hidden="true"></i>
                </button>
                <button v-if="podeApagar(m)" class="balao__acao" type="button"
                        :title="m.tipo === 'nota' ? 'Apagar nota' : 'Apagar para todos'"
                        :aria-label="m.tipo === 'nota' ? 'Apagar nota' : 'Apagar para todos'"
                        @click="pedirParaApagar(m)">
                  <i class="bi bi-trash" aria-hidden="true"></i>
                </button>

                <div v-if="reagindoEm === m.id" class="reacoes">
                  <!-- ⚠️ A NOSSA FICA MARCADA NO SELETOR TAMBÉM: clicar nela
                       de novo é o que TIRA a reação, e sem a marca o gesto
                       parece não fazer nada. -->
                  <button v-for="e in REACOES" :key="e" class="reacoes__item"
                          :class="{ 'reacoes__item--nosso': nossaReacao(m) === e }"
                          type="button" @click="reagir(m, e)">{{ e }}</button>
                </div>
              </div>
            </div>
            </template>
          </div>

          <div v-if="aberta.estado === 'resolvida'" class="cartao__corpo pilha">
            <p class="aviso aviso--ok">
              <i class="bi bi-check-circle aviso__icone" aria-hidden="true"></i>
              <span>
                Atendimento concluído. A conversa está no Histórico e sem dono.
                <strong>Para voltar a responder, reabra.</strong>
              </span>
            </p>
            <!-- 🚨 SEM ISTO, ENCERRAR ERA PORTA SÓ DE IDA. O `responder` recusa
                 conversa resolvida, e a tela escondia a barra inteira: só o
                 cliente escrevendo de novo trazia a conversa de volta. -->
            <button class="botao botao--primario" type="button" @click="pedirParaAssumir">
              <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
              Reabrir e assumir
            </button>
          </div>

          <!-- DE FORA: nem o campo aparece. Poder escrever sem estar na
               conversa é o mesmo furo da barra de ações. -->
          <p v-else-if="!posso" class="cartao__corpo apagado pequeno">
            <i class="bi bi-lock" aria-hidden="true"></i>
            Entre na conversa para responder ou anotar.
          </p>

          <div v-else class="cartao__corpo pilha rodape-conversa">
            <!-- 🔵 23/09: NÚMERO BLOQUEADO. O campo continua aberto porque a
                 NOTA interna continua valendo; o envio ao cliente o servidor
                 recusa, e a faixa diz antes de a pessoa tentar. -->
            <p v-if="aberta.bloqueio" class="aviso aviso--atencao" role="status">
              <i class="bi bi-slash-circle aviso__icone" aria-hidden="true"></i>
              <span>
                <strong>Número bloqueado pelo painel</strong>
                <template v-if="aberta.bloqueio.bloqueado_por_nome">
                  por {{ aberta.bloqueio.bloqueado_por_nome }}</template>.
                Mensagem ao cliente está travada; nota interna continua valendo.
              </span>
              <button class="botao botao--pequeno botao--contorno" type="button"
                      :disabled="mexendo" @click="desbloquear">
                Desbloquear
              </button>
            </p>
            <!-- 🚨 NÃO EXISTE MAIS SELETOR DE MODO. Havia um par
                 "Para o cliente | Nota interna" que só trocava um estado
                 invisível: clicar no lado que já estava ativo não fazia nada,
                 e o usuário perguntou duas vezes para que servia. Estado que
                 não se vê é o que confunde. Agora o destino é o BOTÃO: um
                 campo, duas ações, e o que se clica é o que acontece. -->
            <!-- A mensagem que está sendo citada, com um X para desistir. -->
            <div v-if="citando" class="citando">
              <i class="bi bi-reply" aria-hidden="true"></i>
              <span class="citando__texto">
                {{ citando.conteudo || `(${citando.tipo})` }}
              </span>
              <button class="botao botao--pequeno botao--fantasma" type="button"
                      title="Não citar" @click="citando = null">×</button>
            </div>

            <label class="campo compositor">
              <span class="so-leitor">Mensagem</span>
              <textarea
                ref="campoResposta"
                v-model="resposta"
                class="campo__entrada"
                rows="3"
                :placeholder="ehGrupo
                  ? 'Escreva — @ chama alguém do grupo'
                  : 'Escreva — @ chama quem está na conversa, na nota interna'"
                maxlength="4000"
                @input="olharArroba"
                @keydown.ctrl.enter.prevent="enviar"
                @keydown.enter.exact="enterNoCompositor"
                @keydown.down="andarNaLista(1, $event)"
                @keydown.up="andarNaLista(-1, $event)"
                @keydown.esc="listaArroba = []"
                @paste="colar"
              ></textarea>

              <!-- A lista sobe: o compositor mora no rodapé da conversa. -->
              <ul v-if="listaArroba.length" class="arroba" role="listbox">
                <li v-for="(p, i) in listaArroba" :key="p.jid || p.id">
                  <button type="button"
                          class="arroba__item"
                          :class="{ 'arroba__item--aqui': i === arrobaEscolhido }"
                          :aria-selected="i === arrobaEscolhido"
                          @mousedown.prevent="escolherArroba(p)">
                    {{ p.nome }}
                  </button>
                </li>
              </ul>
            </label>

            <!-- 🚨 QUEM VAI SER CHAMADO APARECE ANTES DE ENVIAR, e o aviso diz
                 que isso só vale em grupo — a tela não pode prometer o que o
                 WhatsApp ignora fora dele. -->
            <!-- 🚨 O CHAMADO DA NOTA DIZ POR ONDE O AVISO VAI. "Chamando" sem
                 mais nada faria a pessoa esperar notificação no WhatsApp, que
                 não existe em conversa direta -- o aviso é pelo chat interno. -->
            <p v-if="chamados.length" class="linha pequeno fraco chamados">
              <i class="bi bi-at" aria-hidden="true"></i>
              <span>na nota interna, aviso pelo chat</span>
              <button v-for="p in chamados" :key="p.id"
                      class="chamados__chip" type="button"
                      :title="`não chamar ${p.nome}`"
                      @click="tirarChamado(p.id)">
                {{ p.nome }} <i class="bi bi-x" aria-hidden="true"></i>
              </button>
            </p>

            <p v-if="mencionados.length" class="linha pequeno fraco chamados">
              <i class="bi bi-at" aria-hidden="true"></i>
              <span>chamando no grupo</span>
              <button v-for="p in mencionados" :key="p.jid"
                      class="chamados__chip" type="button"
                      :title="`não chamar ${p.nome}`"
                      @click="tirarMencionado(p.jid)">
                {{ p.nome }} <i class="bi bi-x" aria-hidden="true"></i>
              </button>
            </p>

            <!-- ANEXO nos DOIS modos (decisão do usuário, 12/08). No modo
                 nota o arquivo é guardado na conversa e NÃO sai para o
                 cliente -- é o print do erro, o PDF que chegou por outro
                 canal, o comprovante que se quer deixar registrado.
                 🔵 25/09: até 10 por envio -- cada um vira um balão, o texto
                 escrito acima vai só no primeiro. -->
            <div v-if="arquivos.length" class="anexo anexo--lista">
              <div v-for="(a, i) in arquivos" :key="i" class="anexo__item">
                <i class="bi bi-paperclip" aria-hidden="true"></i>
                <span class="anexo__nome">{{ a.name }}</span>
                <span class="apagado pequeno">{{ tamanhoDoArquivo(a) }}</span>
                <button
                  class="botao botao--pequeno botao--fantasma"
                  type="button"
                  title="Tirar este anexo"
                  @click="limparArquivo(i)"
                >×</button>
              </div>
              <button
                v-if="arquivos.length > 1"
                class="botao botao--pequeno botao--fantasma"
                type="button"
                @click="limparArquivos"
              >Tirar todos</button>
            </div>

            <div class="linha linha--quebra">
              <!-- 🚨 GRAVAR ÁUDIO: o que o atendente mais usa no celular.
                   Enquanto grava, os outros botões dão lugar ao contador --
                   gravando, a única decisão é enviar ou desistir. -->
              <template v-if="gravando">
                <span class="gravando">
                  <span class="gravando__ponto" aria-hidden="true"></span>
                  gravando {{ Math.floor(segundosGravados / 60) }}:{{
                    String(segundosGravados % 60).padStart(2, '0') }}
                </span>
                <button class="botao botao--pequeno botao--fantasma" type="button"
                        @click="cancelarGravacao">Cancelar</button>
                <button class="botao botao--primario" type="button"
                        :disabled="enviando" @click="enviarGravacao">
                  <i class="bi bi-send" aria-hidden="true"></i> Enviar áudio
                </button>
              </template>

              <!-- 🔵 07/10: anexo, áudio e mensagens rápidas recolhidos atrás de
                   "Outro" — mesmo conceito do "Ações" lá no topo, pelo ganho de
                   tela no compositor (celular e computador). -->
              <button v-if="!gravando" class="botao botao--contorno botao--icone"
                      type="button"
                      :title="outroAberto ? 'Fechar' : 'Outras ações (anexo, áudio, mensagens rápidas)'"
                      :aria-label="outroAberto ? 'Fechar' : 'Outras ações'"
                      :aria-expanded="outroAberto"
                      @click="outroAberto = !outroAberto">
                <i class="bi" :class="outroAberto ? 'bi-x-lg' : 'bi-plus-lg'" aria-hidden="true"></i>
              </button>

              <span v-show="!gravando && outroAberto" class="extras">
                <!-- 🔵 25/09: *"no canto inferior das conversas, pode ter o botão
                     redondinho onde abre um menu dos tipos"*. -->
                <BotaoMensagensRapidas onde="cliente" :dados="dadosDaConversa"
                                       @inserir="inserirNoCampo" />

                <button class="botao botao--contorno botao--icone"
                        type="button" title="Gravar áudio" aria-label="Gravar áudio"
                        @click="comecarGravacao">
                  <i class="bi bi-mic" aria-hidden="true"></i>
                </button>

                <!-- 🔵 Decisão 4 dele: aqui dentro, com os outros três.
                     ⚠️ ÍCONE **E** PALAVRA, pela régua escrita nesta tela:
                     "ganha palavra o que age sobre a conversa". -->
                <button class="botao botao--contorno"
                        type="button"
                        title="Orçamento, agendamento e confirmação"
                        @click="agendarAberto = true; outroAberto = false">
                  <i class="bi bi-calendar-plus" aria-hidden="true"></i>
                  <span>Agendar</span>
                </button>

                <label class="botao botao--contorno botao--icone"
                       :title="`Anexar arquivo (até ${TETO_ANEXOS})`">
                  <i class="bi bi-paperclip" aria-hidden="true"></i>
                  <span class="so-leitor">Anexar arquivo</span>
                  <input
                    id="campo-arquivo"
                    class="so-leitor"
                    type="file"
                    multiple
                    @change="escolherArquivo"
                  />
                </label>
              </span>

              <!-- O DESTINO É O BOTÃO. Verde é WhatsApp em toda a casa;
                   amarelo é nota. A cor diz para onde vai antes do clique. -->
              <button
                class="botao botao--primario"
                type="button"
                :disabled="ocupado || !temAlgoParaEnviar"
                :title="temAlgoParaEnviar ? '' : 'Escreva algo ou anexe um arquivo'"
                @click="arquivos.length ? enviarArquivo(false) : enviar(false)"
              >
                <span v-if="ocupado" class="girando"></span>
                <i v-else class="bi bi-whatsapp" aria-hidden="true"></i>
                {{ arquivos.length > 1 ? `Enviar ${arquivos.length} arquivos ao cliente`
                  : arquivos.length === 1 ? 'Enviar arquivo ao cliente' : 'Enviar ao cliente' }}
              </button>

              <button
                class="botao botao--nota"
                type="button"
                :disabled="ocupado || !temAlgoParaEnviar"
                :title="temAlgoParaEnviar ? '' : 'Escreva algo ou anexe um arquivo'"
                @click="arquivos.length ? enviarArquivo(true) : enviar(true)"
              >
                <i class="bi bi-sticky" aria-hidden="true"></i>
                {{ arquivos.length > 1 ? `Guardar ${arquivos.length} como nota`
                  : arquivos.length === 1 ? 'Guardar como nota' : 'Salvar nota' }}
              </button>

              <span v-if="arquivos.length" class="apagado pequeno">
                O texto acima vai só no primeiro arquivo.
              </span>
            </div>
          </div>
        </template>
      </section>
    </div>

    <!-- ================================================================ MODAIS
         Ficam fora das colunas de propósito: `position: fixed` dentro de um
         container que rola acompanha a rolagem e a caixa some da vista. -->

    <!-- TELA CHEIA — clique em qualquer lugar fecha, como em toda galeria. -->
    <div v-if="emTelaCheia" class="cheia" @click="emTelaCheia = null">
      <img :src="emTelaCheia" class="cheia__img" alt="imagem da conversa" />
      <button class="cheia__fechar" type="button" aria-label="Fechar">
        <i class="bi bi-x-lg" aria-hidden="true"></i>
      </button>
    </div>

    <!-- ENCAMINHAR — lista de conversas com caixas de seleção, como o
         WhatsApp faz. Cinco por vez, que é o limite do próprio aplicativo. -->
    <div v-if="encaminhando" class="modal" @click.self="encaminhando = null">
      <div class="modal__caixa" role="dialog" aria-modal="true" aria-label="Encaminhar">
        <p class="modal__titulo">Encaminhar para</p>
        <p class="modal__texto pequeno">
          A mensagem chega como <strong>mensagem nova</strong>, marcada como
          encaminhada — não como citação.
          <!-- ⚠️ O ARQUIVO PASSOU A IR JUNTO EM 26/08. Dizer isso aqui evita
               a dúvida de quem lembra que antes só ia o texto — e avisa do
               teto ANTES do clique, não depois da falha. -->
          <template v-if="encaminhando.midia_id">
            <br />O <strong>arquivo vai junto</strong>. Acima de 25 MB o
            WhatsApp recusa.
          </template>
        </p>

        <div class="modal__opcoes">
          <label v-for="c in lista.filter((x) => x.id !== aberta.id
                                                 && x.estado !== 'resolvida')"
                 :key="c.id" class="modal__opcao">
            <input v-model="destinosEscolhidos" type="checkbox" :value="c.id"
                   :disabled="destinosEscolhidos.length >= 5
                              && !destinosEscolhidos.includes(c.id)" />
            <span>{{ quem(c) }}</span>
          </label>
        </div>

        <p class="apagado pequeno">
          {{ destinosEscolhidos.length }} de 5 escolhidos.
        </p>

        <div class="modal__acoes">
          <button class="botao botao--contorno" type="button"
                  @click="encaminhando = null">Cancelar</button>
          <button class="botao botao--primario" type="button"
                  :disabled="!destinosEscolhidos.length"
                  :title="destinosEscolhidos.length ? '' : 'Marque ao menos uma conversa de destino'"
                  @click="confirmarEncaminhar">
            Encaminhar
          </button>
        </div>
      </div>
    </div>

    <!-- NOVA MENSAGEM — o `+`. Um destinatário: este painel responde
         "falar com esta pessoa". -->
    <div v-if="novaAberta" class="modal" @click.self="novaAberta = false">
      <div class="modal__caixa" role="dialog" aria-modal="true" aria-label="Nova mensagem">
        <p class="modal__titulo">{{ modoGrupo ? 'Novo grupo' : 'Nova mensagem' }}</p>

        <!-- 🟡 S15, 15/09: criar grupo era o que faltava atrás do `+`. Duas
             abas em vez de dois botões no menu: é a mesma pergunta ("falar
             com quem ainda não está aqui"), com dois destinos. -->
        <div class="modal__abas">
          <button class="botao botao--pequeno"
                  :class="modoGrupo ? 'botao--fantasma' : 'botao--primario'"
                  type="button" @click="modoGrupo = false">
            <i class="bi bi-person" aria-hidden="true"></i> Uma pessoa
          </button>
          <button class="botao botao--pequeno"
                  :class="modoGrupo ? 'botao--primario' : 'botao--fantasma'"
                  type="button" @click="modoGrupo = true">
            <i class="bi bi-people" aria-hidden="true"></i> Grupo
          </button>
        </div>

        <p class="modal__texto pequeno">
          {{ modoGrupo
            ? 'O grupo nasce com o nosso número como dono — é o WhatsApp que manda nisso.'
            : 'Para um número que ainda não escreveu.' }}
        </p>

        <label v-if="modoGrupo" class="campo">
          <span class="campo__rotulo">Nome do grupo</span>
          <input v-model="grupoNome" class="campo__entrada" type="text"
                 maxlength="100" placeholder="Obra Rua das Flores" />
        </label>

        <label class="campo">
          <span class="campo__rotulo">{{ modoGrupo ? 'Números' : 'Número' }}</span>
          <textarea
            v-if="modoGrupo"
            v-model="grupoNumeros"
            class="campo__entrada"
            rows="3"
            placeholder="(18) 99811-6168, (19) 99999-0000"
          ></textarea>
          <input
            v-else
            v-model="novoNumero"
            class="campo__entrada"
            type="tel"
            placeholder="(18) 99811-6168"
            autocomplete="off"
          />
          <span class="campo__ajuda">
            {{ modoGrupo
              ? 'Um por linha ou separados por vírgula. Até 50. Quem não tiver WhatsApp é apontado antes.'
              : 'Com DDD. Pode colar como estiver.' }}
          </span>
        </label>

        <label v-if="!modoGrupo" class="campo">
          <span class="campo__rotulo">Mensagem</span>
          <textarea
            v-model="novoTexto"
            class="campo__entrada"
            rows="3"
            maxlength="4000"
          ></textarea>
        </label>

        <!-- 🚨 A RECUSA POR FALTA DE WHATSAPP TEM CARA PRÓPRIA. É a diferença
             entre "erra o número" e "o sistema falhou", e a reação de quem
             está na tela é outra em cada caso. -->
        <p v-if="semWhatsapp" class="aviso aviso--atencao">
          <i class="bi bi-whatsapp aviso__icone" aria-hidden="true"></i>
          <span>{{ erroNova }}</span>
        </p>
        <p v-else-if="erroNova" class="aviso aviso--erro">
          <i class="bi bi-exclamation-octagon aviso__icone" aria-hidden="true"></i>
          <span>{{ erroNova }}</span>
        </p>

        <div class="modal__acoes">
          <button class="botao botao--contorno" type="button" @click="novaAberta = false">
            Cancelar
          </button>
          <button
            v-if="modoGrupo"
            class="botao botao--primario"
            type="button"
            :disabled="enviandoNova || !grupoNome.trim() || !grupoNumeros.trim()"
            :title="!grupoNome.trim() ? 'Dê um nome ao grupo'
              : !grupoNumeros.trim() ? 'Informe ao menos um número' : ''"
            @click="criarGrupo"
          >
            <span v-if="enviandoNova" class="girando"></span>
            <i v-else class="bi bi-people" aria-hidden="true"></i>
            Criar grupo
          </button>
          <button
            v-else
            class="botao botao--primario"
            type="button"
            :disabled="enviandoNova || !novoNumero.trim() || !novoTexto.trim()"
            :title="!novoNumero.trim() ? 'Informe o número, com DDD'
              : !novoTexto.trim() ? 'Escreva a mensagem' : ''"
            @click="enviarNova"
          >
            <span v-if="enviandoNova" class="girando"></span>
            <i v-else class="bi bi-whatsapp" aria-hidden="true"></i>
            Enviar
          </button>
        </div>
      </div>
    </div>

    <!-- CONVIDAR — vários de uma vez, por caixa de seleção -->
    <!-- 🔵 23/09: EDITAR A MINHA MENSAGEM. 🔵 28/09: mesmo modal serve a nota. -->
    <div v-if="editando && aberta" class="modal" @click.self="editando = null">
      <div class="modal__caixa" role="dialog" aria-modal="true"
           :aria-label="editando.nota ? 'Editar nota' : 'Editar mensagem'">
        <p class="modal__titulo">{{ editando.nota ? 'Editar nota' : 'Editar mensagem' }}</p>
        <p class="modal__texto pequeno">
          {{ editando.nota
            ? 'A nota é só para a equipe, nunca vai para o cliente. Sem prazo para editar.'
            : 'O cliente vê a versão nova, marcada como editada. Dá para editar por 15 minutos depois de enviar.' }}
        </p>
        <textarea v-model="editando.texto" class="campo__entrada" rows="4"
                  maxlength="4096"></textarea>
        <div class="modal__acoes">
          <button class="botao botao--contorno" type="button" @click="editando = null">
            Cancelar
          </button>
          <button class="botao botao--primario" type="button"
                  :disabled="!editando.texto.trim() || mexendo" @click="salvarEdicao">
            <span v-if="mexendo" class="girando"></span>
            Salvar
          </button>
        </div>
      </div>
    </div>

    <div v-if="painelAcao === 'convidar' && aberta" class="modal" @click.self="fecharPainel">
      <div class="modal__caixa" role="dialog" aria-modal="true" aria-label="Convidar atendentes">
        <p class="modal__titulo">Convidar para esta conversa</p>
        <p class="modal__texto pequeno">
          Quem for chamado passa a ver esta conversa e responde por ela.
          <strong>Quem responde pela conversa continua sendo
          {{ aberta.atendente_nome || 'ninguém — está na fila' }}.</strong>
        </p>

        <div v-if="convidaveis.length" class="modal__opcoes">
          <label v-for="a in convidaveis" :key="a.id" class="modal__opcao">
            <input v-model="convidados" type="checkbox" :value="a.id" />
            <span class="estado-bolinha" aria-hidden="true"
                  :style="{ background: corDoEstado(a.estado) }"></span>
            <span>{{ a.nome }}
              <span class="apagado pequeno">· {{ rotuloDoEstado(a.estado) }}</span></span>
          </label>
        </div>
        <p v-else class="apagado pequeno">Todo mundo já está nesta conversa.</p>

        <label v-if="convidaveis.length" class="campo">
          <span class="campo__rotulo">Recado para quem você chamou (opcional)</span>
          <input v-model="recadoConvite" class="campo__entrada" maxlength="2000" />
          <span class="campo__ajuda">Fica na conversa como nota interna — o cliente não vê.</span>
        </label>

        <div class="modal__acoes">
          <button class="botao botao--contorno" type="button" @click="fecharPainel">
            Cancelar
          </button>
          <button
            class="botao botao--primario"
            type="button"
            :disabled="!convidados.length || mexendo"
            :title="convidados.length ? '' : 'Marque quem você quer chamar'"
            @click="convidar"
          >
            <span v-if="mexendo" class="girando"></span>
            {{ convidados.length > 1 ? `Convidar ${convidados.length}` : 'Convidar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ============ LIGAÇÕES DESTE NÚMERO — 7 dias (Bloco 5, 15/10) ======
         🔵 *"o modal só trará ultimos 7 dias, para mais dias um botão vai
         levar para a aba Ligações"*. -->
    <div v-if="ligacoesAbertas && aberta" class="modal" @click.self="fecharLigacoes">
      <div class="modal__caixa modal__caixa--larga" role="dialog" aria-modal="true"
           aria-label="Ligações deste número">
        <p class="modal__titulo">
          Ligações · últimos 7 dias
          <span class="apagado pequeno mono">{{ aberta.telefone_e164 }}</span>
        </p>

        <p v-if="ligacoesErro" class="aviso aviso--erro pequeno" role="alert">
          {{ ligacoesErro }}
        </p>
        <p v-if="ligacoesCarregando" class="apagado pequeno">consultando…</p>

        <template v-else-if="ligacoesDados">
          <!-- 🚨 A FAIXA QUE IMPEDE A TELA DE MENTIR: "nenhuma ligação" e
               "aquele PC não manda ligação nenhuma" são coisas diferentes, e
               só esta linha distingue as duas. -->
          <p class="apagado pequeno">
            Só aparecem ligações de ramais com o agente instalado:
            <strong>{{ ligacoesDados.cobertura.com_agente }}</strong>
            de {{ ligacoesDados.cobertura.atendentes }} atendentes.
          </p>

          <p v-if="!ligacoesDados.total" class="apagado pequeno">
            Nenhuma ligação com este número nos últimos 7 dias.
          </p>

          <div v-for="[valor, rotulo, icone] in SENTIDOS_LIGACAO" :key="valor">
            <template v-if="ligacoesDados.por_sentido[valor].length">
              <p class="campo__rotulo">
                <i class="bi" :class="icone" aria-hidden="true"></i>
                {{ rotulo }} · {{ ligacoesDados.por_sentido[valor].length }}
              </p>
              <ul class="ligacoes__lista">
                <li v-for="l in ligacoesDados.por_sentido[valor]" :key="l.id"
                    class="ligacoes__item pequeno">
                  <span class="mono">{{ horaLigacao(l.inicio) }}</span>
                  <span class="apagado">{{ duracaoLigacao(l.duracao_s) }}</span>
                  <span>{{ l.atendente || 'ramal ' + l.ramal }}</span>
                  <span v-if="!l.gravacoes.length" class="apagado">sem gravação</span>
                  <span v-for="g in l.gravacoes" :key="g.id" class="ligacoes__grav">
                    <button class="botao botao--pequeno botao--contorno" type="button"
                            @click="tocarLigacao(g)">
                      <i class="bi" aria-hidden="true"
                         :class="tocandoLigacao.id === g.id ? 'bi-stop-fill' : 'bi-play-fill'"></i>
                      {{ tocandoLigacao.id === g.id ? 'Parar' : 'Ouvir' }}
                    </button>
                    <button class="botao botao--pequeno botao--contorno" type="button"
                            @click="baixarLigacao(g)">
                      <i class="bi bi-download" aria-hidden="true"></i>
                    </button>
                    <audio v-if="tocandoLigacao.id === g.id" :src="tocandoLigacao.url"
                           controls autoplay></audio>
                  </span>
                </li>
              </ul>
            </template>
          </div>
        </template>

        <div class="modal__acoes">
          <!-- O salto só existe para quem tem a aba: para os outros seria um
               botão que termina em 403. -->
          <button v-if="vejoAbaLigacoes" class="botao botao--contorno" type="button"
                  @click="todasAsLigacoes">
            Ver todas na aba Ligações
          </button>
          <button class="botao botao--primario" type="button"
                  @click="fecharLigacoes">Fechar</button>
        </div>
      </div>
    </div>

    <!-- ============ OUTROS CONTATOS DA EMPRESA (Bloco 6, 15/10) ========== -->
    <div v-if="outrosAbertos && aberta" class="modal" @click.self="fecharOutros">
      <div class="modal__caixa modal__caixa--larga" role="dialog" aria-modal="true"
           aria-label="Outros contatos da empresa">
        <p class="modal__titulo">
          Outros contatos
          <span v-if="outros && outros.cliente" class="apagado pequeno">
            {{ outros.cliente.nome }}
          </span>
        </p>

        <p v-if="outrosErro" class="aviso aviso--erro pequeno" role="alert">
          {{ outrosErro }}
        </p>
        <p v-if="outrosCarregando" class="apagado pequeno">consultando…</p>

        <template v-else-if="outros">
          <!-- ⚠️ VAZIO DIZ POR QUÊ. "Número sem cadastro" e "empresa sem
               outro contato" são casos diferentes, e a tela não pode
               devolver a mesma frase para os dois. -->
          <p v-if="outros.motivo === 'sem_cadastro'" class="apagado pequeno">
            Este número não está no cadastro — então não há empresa de onde
            tirar outros contatos. Dá para vincular pela ficha.
          </p>
          <p v-else-if="outros.motivo === 'contato_sem_empresa'" class="apagado pequeno">
            Esta pessoa não está vinculada a nenhuma empresa.
          </p>
          <p v-else-if="outros.contatos.length <= 1" class="apagado pequeno">
            Esta empresa tem só este contato cadastrado.
          </p>

          <ul v-if="outros.contatos.length" class="outros__lista">
            <li v-for="(l, i) in outros.contatos" :key="i" class="outros__item">
              <span class="outros__quem">
                {{ l.nome }}
                <span v-if="l.este_contato" class="chip chip--pequeno">este</span>
                <span v-for="p in l.papeis" :key="p" class="chip chip--pequeno">{{ p }}</span>
              </span>
              <span class="mono pequeno">{{ l.e164 || 'sem telefone' }}</span>
              <span v-if="l.tem_whatsapp === false"
                    class="chip chip--aviso chip--pequeno">sem WhatsApp</span>
              <!-- 🟡 O botão não aparece sem número nem para quem foi
                   conferido e NÃO tem WhatsApp: mandar ali só produz 409.
                   `tem_whatsapp` nulo (nunca conferido) continua tendo botão
                   -- recusar por falta de conferência seria inventar o `false`. -->
              <button
                v-if="l.e164 && l.tem_whatsapp !== false && !l.esta_conversa"
                class="botao botao--pequeno botao--contorno"
                type="button"
                @click="conversarCom(l)"
              >
                <i class="bi bi-chat-dots" aria-hidden="true"></i>
                Mensagem
              </button>
            </li>
          </ul>
        </template>

        <div class="modal__acoes">
          <button class="botao botao--primario" type="button"
                  @click="fecharOutros">Fechar</button>
        </div>
      </div>
    </div>

    <!-- VINCULAR A UMA EMPRESA — a escolha que morava espremida na gaveta -->
    <div v-if="painelAcao === 'vincular' && aberta" class="modal" @click.self="fecharPainel">
      <div class="modal__caixa modal__caixa--larga" role="dialog" aria-modal="true"
           aria-label="Vincular a uma empresa">
        <p class="modal__titulo">Vincular a uma empresa</p>
        <!-- ⚠️ SÓ O FATO: de quem é o número. A explicação de que o telefone
             entra no cadastro como vindo do atendimento era aula, e ele
             cortou (28/08): *"muito textinho no modal"*. -->
        <p class="modal__texto pequeno">
          <strong class="mono">{{ aberta.telefone_e164 }}</strong>
          <span v-if="aberta.nome_whatsapp"> · {{ aberta.nome_whatsapp }}</span>
        </p>

        <!-- Candidatos que o próprio sistema já achou pelo telefone. Ficam
             ANTES da busca: quando existem, quase sempre a resposta está
             entre eles, e digitar seria trabalho à toa. -->
        <div v-if="aberta.candidatos && aberta.candidatos.length" class="vincular__bloco">
          <p class="campo__rotulo">Este número já responde por</p>
          <div class="modal__opcoes">
            <button
              v-for="c in aberta.candidatos"
              :key="c.id"
              class="vincular__item"
              type="button"
              :disabled="vinculando"
              @click="c.cliente_id ? vincularA(c.cliente_id) : vincularA(null, c.id)"
            >
              <span>{{ c.nome }}</span>
            </button>
          </div>
        </div>

        <!-- 🔵 O nome de quem vai virar contato, digitável (15/09). Vazio
             mantém o que o sistema deriva do apelido do WhatsApp. -->
        <label class="campo">
          <span class="campo__rotulo">Nome da pessoa (opcional)</span>
          <input
            v-model="nomeDoContato"
            class="campo__entrada"
            type="text"
            maxlength="120"
            :placeholder="aberta.nome_whatsapp || aberta.telefone_e164"
          />
          <span class="campo__ajuda">
            Vazio usa o nome do WhatsApp — que às vezes é só um emoji.
          </span>
        </label>

        <label class="campo">
          <span class="campo__rotulo">Buscar empresa</span>
          <input
            v-model="buscaCliente"
            class="campo__entrada"
            type="search"
            placeholder="nome, CNPJ ou CPF"
            @input="procurarCliente"
          />
        </label>

        <p v-if="buscando" class="linha apagado pequeno">
          <span class="girando"></span> procurando…
        </p>
        <!-- ⚠️ O TETO DA ROTA APARECE NA TELA. `/api/conversas/buscar-empresa`
             devolve no máximo 10, e teto que ninguém vê não é limite: é dado
             sumindo. É a mesma regra dos 200 acertos da busca na conversa. -->
        <template v-else-if="achadosCliente.length">
          <div class="modal__opcoes">
            <button
              v-for="c in achadosCliente"
              :key="c.id"
              class="vincular__item"
              type="button"
              :disabled="vinculando"
              @click="vincularA(c.id)"
            >
              <span class="vincular__nome">{{ c.nome }}</span>
              <span class="apagado pequeno mono">
                {{ documentoLegivel(c.documento) || 'sem CNPJ' }}
              </span>
            </button>
          </div>
          <p v-if="achadosCliente.length >= 10" class="apagado pequeno">
            Mostrando as 10 primeiras. Refine o termo se a sua não estiver aqui.
          </p>
        </template>
        <p v-else-if="buscaCliente.trim().length >= 2" class="apagado pequeno">
          Nenhuma empresa com esse termo.
        </p>
        <!-- ⚠️ O estado de "ainda não dá para buscar" não precisa de frase: o
             campo vazio já diz. A explicação de POR QUE são 2 letras era aula
             sobre a regra, não instrução de uso. -->

        <div class="modal__acoes">
          <button class="botao botao--contorno" type="button" @click="fecharPainel">
            Cancelar
          </button>
        </div>
      </div>
    </div>

    <!-- TRANSFERIR -->
    <div v-if="painelAcao === 'transferir' && aberta" class="modal" @click.self="fecharPainel">
      <div class="modal__caixa" role="dialog" aria-modal="true" aria-label="Transferir conversa">
        <p class="modal__titulo">Transferir conversa</p>
        <!-- ⚠️ A consequência de cada destino é DIFERENTE, e é isso que a
             pessoa precisa ler antes de escolher: time larga na fila, pessoa
             entrega com dono. Está assim na própria função do backend. -->
        <p class="modal__texto pequeno">
          {{ atendenteEscolhido
            ? 'Para uma pessoa: ela passa a ser a dona e responde por ela.'
            : 'Para um time: tira o dono e a conversa volta para a fila do time.' }}
        </p>
        <label class="campo">
          <span class="campo__rotulo">Pessoa</span>
          <select v-model="atendenteEscolhido" class="campo__entrada"
                  @change="timeEscolhido = ''">
            <option value="">escolha…</option>
            <!-- 🔵 23/09: o estado vai em TEXTO -- <option> não desenha bolinha,
                 e passar a conversa para quem está fora do expediente é
                 entregá-la a ninguém. -->
            <option v-for="a in transferiveis" :key="a.id" :value="a.id">
              {{ a.nome }} — {{ rotuloDoEstado(a.estado) }}
            </option>
          </select>
          <span class="campo__ajuda">
            Entrega direto: quem receber já vira dono da conversa.
          </span>
          <!-- 🔵 24/09, a frase é dele: *"Não é possivel receber conversa se
               estiver offline, ao transferir, no painel, aparece informação
               para o atendente"*. A escolha continua possível (é assim que a
               pessoa LÊ o aviso); o botão é que trava -- e a API também. -->
          <p v-if="destinoOffline" class="aviso aviso--erro pequeno" role="alert">
            <i class="bi bi-person-slash aviso__icone" aria-hidden="true"></i>
            <span>O atendente escolhido está offline e não poderá continuar o atendimento.</span>
          </p>
        </label>
        <label class="campo">
          <span class="campo__rotulo">…ou um time</span>
          <select v-model="timeEscolhido" class="campo__entrada"
                  @change="atendenteEscolhido = ''">
            <option value="">escolha…</option>
            <option v-for="t in times" :key="t.id" :value="t.id">
              {{ t.nome }}{{ t.qtd_membros ? '' : ' — sem ninguém dentro!' }}
            </option>
          </select>
          <span class="campo__ajuda">
            Time sem membro aceita a transferência, mas ninguém recebe.
          </span>
        </label>
        <label class="campo">
          <span class="campo__rotulo">Resumo para quem vai receber</span>
          <input v-model="motivo" class="campo__entrada" maxlength="2000" />
          <span class="campo__ajuda">
            O que já foi conversado. Fica na conversa como nota interna — quem
            assume lê ali, e o cliente não vê.
          </span>
        </label>
        <div class="modal__acoes">
          <button class="botao botao--contorno" type="button" @click="fecharPainel">
            Cancelar
          </button>
          <button class="botao botao--primario" type="button"
                  :disabled="(!timeEscolhido && !atendenteEscolhido) || destinoOffline"
                  :title="(timeEscolhido || atendenteEscolhido)
                    ? '' : 'Escolha uma pessoa ou um time de destino'"
                  @click="transferir">
            Transferir
          </button>
        </div>
      </div>
    </div>

    <!-- ENCERRAR — caixa de confirmação. A classificação vem NO FIM e é
         opcional desde 11/08: ninguém pediu a lista, e o analytics que a
         justificava é Fase 3. -->
    <div v-if="painelAcao === 'encerrar' && aberta" class="modal" @click.self="fecharPainel">
      <div class="modal__caixa" role="dialog" aria-modal="true" aria-label="Concluir atendimento">
        <p class="modal__titulo">Concluir este atendimento?</p>
        <p class="modal__texto">
          A conversa passa para o Histórico e <strong>volta para "sem
          dono"</strong>. O cliente <strong>não</strong> é avisado, e você
          pode reabrir esta a qualquer momento.
        </p>
        <!-- ⚠️ Concluir vale mesmo com gente dentro: concluir é conclusão.
             Quem quer só se retirar usa "Sair da conversa", que é outra ação
             e continua existindo. -->
        <p v-if="acompanham.length" class="modal__texto pequeno">
          <strong>{{ acompanham.length }} pessoa(s) acompanham</strong> esta
          conversa. Concluir tira todo mundo — inclusive elas.
        </p>

        <details class="encerrar__extra">
          <summary class="pequeno">Classificar (opcional)</summary>
          <label class="campo">
            <span class="campo__rotulo">Classificação</span>
            <select v-model="classificacaoEscolhida" class="campo__entrada">
              <option value="">não classificar</option>
              <option v-for="c in classificacoes" :key="c.id" :value="c.id">{{ c.nome }}</option>
            </select>
            <span class="campo__ajuda">
              Concluir sem classificar é o caminho normal.
            </span>
          </label>
          <label v-if="exigeComentario" class="campo">
            <span class="campo__rotulo">Comentário — obrigatório nesta</span>
            <textarea v-model="comentario" class="campo__entrada" rows="2" maxlength="2000"></textarea>
            <span class="campo__ajuda">
            </span>
          </label>
        </details>

        <div class="modal__acoes">
          <button class="botao botao--contorno" type="button" @click="fecharPainel">
            Cancelar
          </button>
          <button
            class="botao botao--primario"
            type="button"
            :disabled="exigeComentario && !comentario.trim()"
            :title="exigeComentario && !comentario.trim()
              ? 'Esta classificação exige um comentário dizendo o que foi' : ''"
            @click="encerrar"
          >
            Concluir atendimento
          </button>
        </div>
      </div>
    </div>

    <!-- CONFIRMAÇÃO genérica — devolver, sair, reabrir -->
    <div v-if="confirmacao" class="modal" @click.self="confirmacao = null">
      <div class="modal__caixa" role="dialog" aria-modal="true" :aria-label="confirmacao.titulo">
        <p class="modal__titulo">{{ confirmacao.titulo }}</p>
        <p class="modal__texto">{{ confirmacao.texto }}</p>
        <div class="modal__acoes">
          <button class="botao botao--contorno" type="button" @click="confirmacao = null">
            Cancelar
          </button>
          <button
            class="botao"
            :class="confirmacao.perigo ? 'botao--perigo' : 'botao--primario'"
            type="button"
            :disabled="mexendo"
            @click="confirmar"
          >
            {{ confirmacao.rotulo }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <ModalAgendar
    v-if="agendarAberto && aberta"
    :conversa-id="aberta.id"
    :relacao="relacaoContato"
    :contato-nome="aberta?.empresa?.contato?.nome || ''"
    :cliente-harmonit-id="clienteHarmonitDaConversa"
    :cliente-nome="clienteNomeDaConversa"
    @fechar="agendarAberto = false"
    @inserir="inserirNoCampo"
  />
</template>

<style scoped>
.vinculo { display: flex; flex-direction: column; gap: var(--e-2); }
.vinculo__titulo { margin: 0; font-size: var(--txt-xs); text-transform: uppercase; letter-spacing: .06em; color: var(--texto-fraco); }
.vinculo__nome { margin: 0; font-size: var(--txt-md); font-weight: var(--peso-forte); }
.vinculo__nome small { font-weight: 400; color: var(--texto-fraco); }
.vinculo__fraco { margin: 0; font-size: var(--txt-sm); color: var(--texto-fraco); }
.vinculo__busca { display: flex; gap: var(--e-2); }
/* 16px: abaixo disso o iOS dá zoom ao focar (padrão dos quatro painéis). */
.vinculo__busca .campo__entrada { flex: 1; font-size: 16px; }
.vinculo__lista { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 2px; max-height: 200px; overflow-y: auto; }
.vinculo__item { display: flex; flex-direction: column; align-items: flex-start; width: 100%; padding: var(--e-2); border: var(--borda-fina) solid var(--borda); border-radius: var(--r-sm); background: var(--superficie-2); color: inherit; text-align: left; cursor: pointer; }
.vinculo__item small { color: var(--texto-fraco); }

/* 🔵 23/09: o estado de quem responde (util/estado.js decide a cor). */
.estado-bolinha {
  display: inline-block; width: 8px; height: 8px; border-radius: 50%;
  margin-right: 4px; vertical-align: middle; flex-shrink: 0;
}
.estado-rotulo { font-weight: normal; opacity: .8; }
/* A ficha é uma faixa dentro da conversa, não uma tela: some ao trocar de
   conversa e não tem rota. É o equivalente a clicar no contato no WhatsApp. */
.gaveta {
  border: var(--borda-fina) solid var(--borda);
  border-radius: var(--r-md);
  background: var(--superficie-2);
  padding: var(--e-3);
  margin-bottom: var(--e-3);
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
  /* ⚠️ A COLUNA PASSOU A TER ALTURA, então a ficha também precisa saber
     encolher: aberta numa pessoa com muitas empresas, ela empurraria o fio e
     o compositor para fora do cartão -- e `.coluna { overflow: hidden }`
     cortaria em silêncio, sem barra para rolar. */
  flex: 0 1 auto;
  max-height: 45%;
  overflow-y: auto;
}
.gaveta__topo {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.gaveta__dados {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: var(--e-1) var(--e-3);
  margin: 0;
}
.gaveta__dados dt {
  color: var(--texto-fraco);
  font-size: var(--txt-sm);
}
.gaveta__dados dd {
  margin: 0;
  overflow-wrap: anywhere;
}
/* 🔵 07/10: linha do nome editável do Lead — input + Salvar + "gravado". */
.gaveta__nome-edit {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--e-1);
}
.gaveta__nome-edit .campo__entrada--compacto { flex: 1 1 8rem; min-width: 0; }
.gaveta__bitrix {
  display: flex; flex-direction: column; gap: 2px;
  padding: var(--e-2);
  border-left: 3px solid var(--aviso);
  background: var(--aviso-suave);
  border-radius: var(--r-sm);
}
.gaveta__bloco {
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
}
.gaveta__lista {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
  max-height: 240px;
  overflow-y: auto;
}
.gaveta__empresa {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--e-2);
  border: var(--borda-fina) solid var(--borda);
  border-radius: var(--r-sm);
  background: var(--superficie);
}
/* O seletor de tipo na ficha: largura de conteúdo, não de formulário. Um
   `<select>` de 100% ao lado de "Empresa" e "CNPJ" desequilibra a lista. */
/* ⚠️ O teto de 12rem saiu junto com o de 14rem da CAD_1.2: quem dimensiona
   agora é `--compacto`, e teto por tela devolveria a divergência. */

/* `.gaveta__tipo-ajuda` saiu: as duas frases que ela formatava eram as
   explicações do tipo que ele mandou tirar em 28/08. Regra sem elemento é
   peso morto que a próxima pessoa tenta entender. */


/* ---- modal de vínculo (28/08) --------------------------------------------
   🚨 MAIS LARGO QUE OS OUTROS, DE PROPÓSITO. `.modal__caixa` é 420px, que
   serve para escolher entre nomes de atendente; razão social com CNPJ ao lado
   quebra em duas linhas nessa largura e a lista vira parede. */
.modal__caixa--larga { max-width: 560px; }

/* Ligações e Outros contatos: linha por item, sem tabela. São listas curtas
   (7 dias, um punhado de contatos) e tabela aqui só acrescentaria moldura. */
.ligacoes__lista,
.outros__lista { list-style: none; margin: 0 0 var(--e-3); padding: 0; }
.ligacoes__item,
.outros__item {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  flex-wrap: wrap;
  padding: 4px 0;
  border-bottom: var(--borda-fina) solid var(--borda);
}
.ligacoes__grav { display: inline-flex; align-items: center; gap: 4px; }
.ligacoes__item audio { max-width: 200px; height: 30px; }
.outros__quem { display: inline-flex; align-items: center; gap: 4px; flex: 1; min-width: 40%; }
/* ---- desenho A da ficha (15/10) --------------------------------------
   🔵 *"está redundante e um pouco feia"*. A gaveta é `flex-direction: column`,
   então TODO botão solto nela ganhava uma linha inteira; estes três passam a
   dividir uma. `flex-wrap` porque no celular a gaveta vira a tela da conversa
   e três rótulos podem não caber lado a lado. */
.gaveta__acoes {
  display: flex;
  flex-wrap: wrap;
  gap: var(--e-2);
}
.gaveta__acoes .botao { flex: 1 1 auto; justify-content: center; }

/* O cartão da empresa: fundo de superfície dentro da gaveta, que é
   `--superficie-2`. É o contraste que faz o cartão existir sem precisar de
   sombra nem de borda grossa. */
.gaveta__cartao {
  background: var(--superficie);
  border: var(--borda-fina) solid var(--borda);
  border-radius: var(--r-md);
  padding: var(--e-3);
}
.gaveta__razao {
  margin: 0;
  font-weight: var(--peso-forte);
  line-height: var(--entrelinha-apertada);
  overflow-wrap: anywhere;
}
.gaveta__fantasia {
  margin: 1px 0 0;
  font-size: var(--txt-sm);
  color: var(--texto-fraco);
  overflow-wrap: anywhere;
}
.gaveta__meta {
  display: grid;
  gap: 2px;
  margin-top: var(--e-2);
  padding-top: var(--e-2);
  border-top: var(--borda-fina) dashed var(--borda);
  color: var(--texto-fraco);
}
.gaveta__meta span { display: flex; align-items: center; gap: 6px; min-width: 0; }
.gaveta__meta i { color: var(--texto-apagado); flex: none; }
/* ⚠️ `anywhere` e não `ellipsis`: e-mail cortado com reticências não dá para
   copiar, e copiar é o que se faz com um e-mail. */
.gaveta__meta span > :last-child { overflow-wrap: anywhere; min-width: 0; }

.gaveta__pe {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--e-2);
  flex-wrap: wrap;
  padding-top: var(--e-2);
  border-top: var(--borda-fina) solid var(--borda);
}

.vincular__bloco { margin-bottom: var(--e-4); }

/* A linha da empresa: nome à esquerda, documento à direita, e o alvo de
   clique ocupando a linha inteira -- não só o texto. */
.vincular__item {
  width: 100%;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--e-3);
  padding: var(--e-2) var(--e-3);
  border: 0;
  border-radius: var(--r-sm);
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.vincular__item:hover:not(:disabled) { background: var(--superficie-2); }
.vincular__item:focus-visible { outline: none; box-shadow: var(--foco); }
.vincular__item:disabled { opacity: .55; cursor: default; }
/* ⚠️ `min-width: 0` no nome: sem ele, razão social longa empurra o documento
   para fora da caixa em vez de quebrar. */
.vincular__nome { min-width: 0; }

.gaveta__achado {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--e-2);
  text-align: left;
}
/* A imagem ocupa a largura do balão, com teto de altura: foto de celular em
   pé tem 4000px e empurraria a conversa inteira para fora da tela. */
.balao__imagem {
  display: block;
  max-width: 100%;
  max-height: 320px;
  width: auto;
  border-radius: var(--r-md);
  margin-bottom: var(--e-2);
  background: var(--superficie-2);
}
.balao__audio {
  width: 100%;
  max-width: 320px;
  margin-bottom: var(--e-2);
}
/* A citação é uma faixa presa à esquerda, como no WhatsApp: precisa parecer
   subordinada à mensagem, não outra mensagem. */
.balao__citada {
  border-left: 3px solid var(--acento-borda);
  padding: var(--e-1) var(--e-2);
  margin-bottom: var(--e-2);
  background: var(--superficie-2);
  border-radius: var(--r-sm);
  color: var(--texto-fraco);
  overflow-wrap: anywhere;
  /* Clicar vai até a mensagem original (29/09). */
  cursor: pointer;
}
.tela { max-width: 1280px; }

/* ============================================================================
   OS CINCO DEFEITOS QUE A AUDITORIA DE 27/08 ACHOU
   ----------------------------------------------------------------------------
   Ele viu antes de mim: *"visualmente já vejo erros de scroll, alinhamento e
   etc"*. Eu tinha rodado suíte e build -- e nenhum dos dois vê layout. É a
   regra que este projeto já tinha escrita: placar verde não prova que a tela
   abre.

   1. 🚨 A ALTURA NÃO CHEGAVA. `.painel` pedia `height: 100%` e o pai `.tela`
      não tinha altura nenhuma -- em CSS, `height: 100%` sobre pai `auto`
      resolve para `auto`. A tela inteira voltava a crescer com o conteúdo, e
      daí o scroll errado: rolava a PÁGINA em vez de rolar o fio.
   2. `max-width: 1280px` deixava uma faixa vazia em monitor largo, num
      desenho que ocupa a tela toda.
   3. `.cartao__cabecalho` mantinha o raio dos cantos de cima, agora que o
      cartão perdeu o dele -- 12px de curva contra uma borda reta.
   4. e mantinha o fundo cinza, num desenho em que a barra do topo é branca.
   5. os avisos ficavam entre a tela e o painel, sem nenhum respiro lateral:
      colavam na borda da janela.
   ========================================================================== */
.tela--conversa {
  max-width: none;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Os avisos são raros e ficam ACIMA das colunas. Sem margem própria eles
   colariam na borda, agora que a página não tem mais padding. */
.tela--conversa > .aviso {
  flex: none;
  margin: var(--e-3) var(--e-4) 0;
}

/* A barra do topo de cada coluna deixa de ser "cabeçalho de cartão": sem raio,
   fundo branco, e a linha de baixo é quem separa. */
.tela--conversa .cartao__cabecalho {
  border-radius: 0;
  background: var(--superficie);
}

.tela__cabecalho {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--e-4);
  flex-wrap: wrap;
  margin-bottom: var(--e-4);
}
.tela__cabecalho p { max-width: var(--largura-texto); margin-top: var(--e-1); }

/* ============================================================================
   A TELA É UM APP DE CONVERSA, NÃO UMA PÁGINA COM CARTÕES (27/08).
   ----------------------------------------------------------------------------
   🚨 Foi o que ele apontou comparando com o mockup que escolheu: *"não ficou
   igual ao modelo, só mudou o fundo, pensei que as dimensões e etc ficariam
   igual"*. Ele estava certo -- eu tinha trocado a pele do balão e deixado a
   estrutura como estava.

   O que muda: as duas colunas COLAM (sem `gap`), ocupam a ALTURA TODA, e
   deixam de ser cartões flutuando com raio e sombra. O respiro da página é
   anulado pelo `meta.cheio` da rota, no App.

   ⚠️ SÓ ESTA TELA. As outras continuam sendo páginas com cartões, e o
   `.painel` daqui é `scoped`. ============================================= */
.painel {
  display: grid;
  grid-template-columns: 348px 1fr;
  /* ⚠️ `flex: 1 1 auto` + `min-height: 0`, NÃO `height: 100%`. O pai é um
     flex-column, e é ele quem distribui a altura; `height: 100%` num filho de
     flex briga com a distribuição e foi metade do defeito de scroll. */
  flex: 1 1 auto;
  min-height: 0;
  gap: 0;
  align-items: stretch;
}
@media (max-width: 1100px) { .painel { grid-template-columns: 300px 1fr; } }
/* ══ CELULAR (🔵 25/09) ═════════════════════════════════════════════════
   *"os mesmos recursos, porém somente os chats"*. Antes disto, a 390 px a
   lista e a conversa ficavam EMPILHADAS na mesma altura e as mensagens
   sobravam numa faixa de ~30 px (medido na prévia de 25/09).
   Agora é UMA COISA POR VEZ: sem conversa, a lista; com conversa, só ela --
   e o "Voltar" do cabeçalho (ou o do aparelho) traz a lista de volta.
   ⚠️ Nada daqui vale acima de 860 px: o computador fica como estava. */
@media (max-width: 860px) {
  .painel { grid-template-columns: 1fr; grid-template-rows: minmax(0, 1fr); }
  .painel--com-conversa > .coluna:not(.coluna--larga) { display: none; }
  .painel:not(.painel--com-conversa) > .coluna--larga { display: none; }

  .conversa__cabecalho { flex-wrap: nowrap; gap: var(--e-2); align-items: center; }
  .conversa__quem { min-width: 0; flex: 1 1 auto; }
  /* ⚠️ REDUNDANTE DESDE 14/10: o cabeçalho virou flex com quebra, e o nome já
     tem teto e reticências em todas as larguras. Fica por enquanto porque não
     atrapalha (item de flex é blocado de todo jeito) e porque tirar linha de
     layout sem olhar o celular é como o defeito nasceu. Sai quando ele
     conferir no aparelho. */
  .conversa__quem > strong { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  /* O telefone fica; canal, cadastro e estado saem da faixa (estão na ficha). */
  .conversa__detalhe > span { display: none; }
  .conversa__cabecalho .chip { display: none; }
  .conversa__voltar { flex: none; margin-left: calc(-1 * var(--e-2)); }

  /* A barra de ações recolhida vale em todas as larguras (regra global, acima);
     aqui não precisa repetir. */

  /* A ficha vira a tela da conversa por cima, e não uma faixa de 42% dela. */
  .coluna--larga > .gaveta { max-height: 60vh; }

  /* Alvos de toque: nada abaixo de 44 px nas duas colunas. */
  .coluna .botao--icone,
  .coluna .botao--pequeno,
  .coluna :deep(.mr__botao) { min-height: 44px; min-width: 44px; }
  .coluna .botao { min-height: 44px; }
  /* As abas (Todas, Sem dono, Minhas, Time) também são alvo de dedo. */
  .coluna .abas__aba { min-height: 44px; }
}

/* Tela de toque (celular, tablet): as ações do balão aparecem pelo toque
   (`balao--tocado`), e cada uma ganha tamanho de dedo. */
@media (hover: none) {
  .balao--tocado .balao__acoes { opacity: 1; pointer-events: auto; transform: none; }
  .balao__acao { min-width: 40px; min-height: 40px; }
}

/* As colunas perdem a casca de cartão: sem raio, sem sombra, divididas por
   uma linha de 1px -- é o que faz a tela parecer contínua. */
.painel > .cartao {
  border-radius: 0;
  border: 0;
  box-shadow: none;
  padding: 0;
  min-height: 0;
}
.painel > .cartao + .cartao { border-left: var(--borda-fina) solid var(--borda); }

.coluna {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--superficie);
}

/* ---- o topo da coluna da lista (27/08) --------------------------------- */
.lista__topo {
  flex: none;
  display: block;
  padding: var(--e-4) var(--e-4) var(--e-3);
  border-bottom: var(--borda-fina) solid var(--borda);
}
/* ⚠️ O ÍCONE DE AJUDA AO LADO DO TÍTULO (28/08). A regra global que faz isso
   nas outras 13 telas mira `.tela__cabecalho > div:first-child`, e este
   cabeçalho é `.cartao__cabecalho` -- não é a mesma casa. Sem esta regra o
   ícone cai embaixo do `<h1>`, que foi exatamente o defeito que a regra
   global nasceu para consertar. */
.lista__titulo {
  margin-bottom: var(--e-3);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--e-2);
  min-width: 0;
}
.lista__titulo h1 {
  margin: 0;
  font-size: var(--txt-lg);
  font-weight: var(--peso-forte);
  letter-spacing: -.01em;
}
/* ⚠️ Os dois números viraram uma linha de apoio, não dois cartões: eles
   informam, e o que decide a atenção é a lista abaixo. */
.lista__placar {
  /* Linha inteira: o placar é apoio do título, não vizinho dele. */
  flex-basis: 100%;
  margin-top: 2px;
  font-size: var(--txt-sm);
  color: var(--texto-apagado);
}
.lista__pede { color: var(--aviso); font-weight: var(--peso-medio); }

/* ⚠️ A BUSCA NÃO ESTÁ DENTRO DO TOPO -- ela é o `.cartao__corpo` seguinte, e
   uma regra `.lista__topo .busca` não pegaria nada. A auditoria pegou; o
   ajuste vai onde ela de fato mora, e o padding de baixo é menor porque a
   lista começa logo abaixo. */
.coluna > .cartao__corpo { padding: var(--e-3) var(--e-4); }

/* 🚨 A COLUNA DA CONVERSA VIRA UMA PILHA COM ALTURA, e o fio ocupa o que
   sobra. Apontado por ele em 26/08: o fio tinha `max-height: 52vh` fixo, então
   metade da tela ficava vazia embaixo enquanto a conversa rolava numa
   janelinha. Altura fixa em `vh` é chute sobre o monitor de quem usa.

   ⚠️ `max-height`, NÃO `height`: conversa curta continua sendo um cartão
   curto, sem um vão cinza embaixo. O `calc` desconta o cabeçalho da tela e as
   margens; o `min` impede que num monitor muito alto o fio fique maior do que
   se lê de uma vez. */
/* ⚠️ O `max-height` em `vh` SAIU. Ele existia porque a tela era uma página que
   rolava; agora a coluna ocupa exatamente a altura disponível e quem rola é o
   fio, por dentro. Altura em `vh` era chute sobre o monitor de quem usa. */
.coluna--larga {
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--fundo);
}

/* A mesma regra da outra coluna: ninguém cresce, exceto a lista. Aqui os
   filhos são o topo, a busca, e então a lista (ou "carregando", ou o vazio). */
.coluna > * { flex: none; }
.coluna > .conversas,
.coluna > .vazio { flex: 1 1 auto; min-height: 0; }

/* Pelo mesmo motivo, a lista rola inteira em vez de parar em 60vh. */
.conversas {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
}

/* O "Assumir" fica ao lado do botão da conversa, não dentro dele — o item é
   quem vira a linha, e a borda de topo passou para cá porque agora são dois
   elementos irmãos dividindo a mesma linha. */
.conversas__item {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  padding-right: var(--e-3);
  border-top: 1px solid var(--borda, rgba(128, 128, 128, .25));
}
.conversas__item:hover { background: rgba(128, 128, 128, .08); }
.conversas__assumir { flex: none; }

/* 🆕 03/11: A COLUNA DA DIREITA do item da lista -- o selo, a hora e o
   botão, empilhados. Pedido dele com a seta na imagem: *"consegue puxar essas
   informações mais pra direita e aumentar os caracteres do nome do contato"*.

   🚨 LARGURA FIXA, E É ELA QUE RESOLVE O PEDIDO INTEIRO. Antes, o selo e a
   hora dividiam a linha do nome e comiam ~71 px dela; e a largura do botão
   variava de "Entrar" a "Ir para a aberta" (**166 das 1.014 linhas, 16,4%**),
   então o nome mudava de tamanho a cada linha -- em algumas sobravam ~5
   caracteres. Com a coluna travada, **toda** linha dá ao nome a mesma largura.

   ⚠️ 100 px é medida, não chute: cabe "Assumir" com ícone (o mais largo dos
   rótulos curtos) sem apertar, e "Ir para a aberta" quebra em duas linhas em
   vez de empurrar o nome. */
.conversas__direita {
  flex: 0 0 100px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--e-1);
  padding: var(--e-2) 0;
}
/* O botão ocupa a coluna inteira e PODE quebrar -- é o que deixa o rótulo
   comprido caber sem alargar a coluna. */
.conversas__direita .conversas__assumir {
  width: 100%;
  white-space: normal;
  line-height: 1.25;
}
/* O selo e a hora na mesma linha, encostados à direita. */
.conversas__meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--e-1);
  min-height: 18px;
}

/* 🚨 A MARGEM CARREGA UM SIGNIFICADO SÓ: conversa direta × grupo.
   É o fichário que o usuário pediu em 25/08 -- "para sabermos qual é".
   Duas leituras na mesma faixa é a faixa não querer dizer nada, e por isso
   "não identificado" e "concluída" continuam como CHIP, não como cor de
   borda. Os tokens são os do sistema: nenhum valor de cor escrito aqui. */
.conversa {
  display: flex;
  align-items: flex-start;
  gap: var(--e-3);
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  background: none;
  border: 0;
  border-left: 4px solid var(--acento);
  padding: var(--e-3);
  cursor: pointer;
  font-family: var(--fonte);
}
.conversa--grupo { border-left-color: var(--ok); }

/* `min-width: 0` é o que permite o texto TRUNCAR dentro do flex: sem ele o
   item cresce e a hora sai da coluna. */
.conversa__corpo { flex: 1 1 auto; min-width: 0; display: block; }

/* ⚠️ 44px, não 36 (27/08). É o mesmo número do alvo de toque (`--altura-toque`)
   e o que dá presença à lista sem trocar a densidade: a linha já tinha essa
   altura por causa das duas linhas de texto. */
.conversa__avatar {
  flex: none;
  width: var(--altura-toque);
  height: var(--altura-toque);
  border-radius: var(--r-full);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: var(--txt-sm);
  font-weight: var(--peso-forte);
  letter-spacing: .02em;
}
.conversa__avatar--grupo {
  background: var(--superficie-3);
  color: var(--texto-fraco);
}

.conversa__hora { flex: none; white-space: nowrap; }

/* 🚨 UMA LINHA SÓ, COM RETICÊNCIAS. A prévia com duas linhas fazia o item
   pular de altura conforme o texto -- e uma lista que muda de ritmo é mais
   difícil de varrer do que uma lista densa.

   🚨 PRESA À LISTA (`.conversa__topo`), e isto é o conserto de 14/10.
   `conversa__quem` serve a DOIS lugares com necessidades opostas: aqui, o item
   da lista, onde cortar é o certo; e o CABEÇALHO da conversa, onde a mesma
   classe envolve o botão "Ficha". Solta, ela punha o nome e o botão na mesma
   linha sem poder quebrar (`white-space: nowrap`) dentro de uma caixa com
   `overflow: hidden` -- então **nome comprido empurrava o botão para fora e o
   recortava**. Era a queixa dele, *"o botão Ficha tem ficado invisível"*, e o
   "tem ficado" é a assinatura do defeito: dependia do tamanho do nome. No
   celular não aparecia, porque a media query de 860px dá linha própria ao
   nome. Nenhum teste viu: DOM não calcula recorte de layout. */
.conversa__topo .conversa__quem,
.conversa__previa {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.conversa__marcas {
  display: flex;
  gap: var(--e-1);
  flex-wrap: wrap;
  margin-top: 2px;
}
.conversa__marcas:empty { display: none; }
.conversa:hover { background: rgba(128, 128, 128, .08); }
.conversa--aberta { background: rgba(128, 128, 128, .14); }

/* ---- filtro dentro do campo de busca ------------------------------------
   O popover ancora no botão do funil. `position: relative` no pai é o que
   segura o `absolute` do painel; sem ele, ele iria para o canto da tela. */
.filtro { position: relative; flex: none; }

.botao--filtrando {
  border-color: var(--acento);
  color: var(--acento);
  background: var(--acento-suave);
}

/* O contador no canto do funil: diz QUE há filtro sem abrir o painel. */
.filtro__conta {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: var(--r-full);
  background: var(--acento);
  color: var(--acento-texto);
  font-size: var(--txt-xs);
  line-height: 16px;
  text-align: center;
}

.filtro__caixa {
  position: absolute;
  top: calc(100% + var(--e-1));
  right: 0;
  z-index: var(--z-flutuante);
  min-width: 220px;
  padding: var(--e-3);
  background: var(--superficie);
  border: var(--borda-fina) solid var(--borda);
  border-radius: var(--r-lg);
  box-shadow: var(--sombra-2);
}
.filtro__titulo {
  margin: 0 0 var(--e-2);
  font-size: var(--txt-sm);
  font-weight: var(--peso-forte);
  color: var(--texto-fraco);
}
.filtro__linha {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  padding: var(--e-1) 0;
  cursor: pointer;
}
.filtro__limpar { margin-top: var(--e-2); padding-left: 0; }
.filtro__resumo {
  display: inline-flex;
  align-items: center;
  gap: var(--e-1);
  color: var(--acento);
}

/* 🆕 03/11: o topo passou a ter UM filho só, o nome -- o selo e a hora
   foram para `.conversas__direita`. `min-width: 0` é o que permite o nome
   truncar dentro do flex; sem ele o item cresce e empurra a coluna da direita.
   ⚠️ O seletor `.conversa__topo .conversa__quem` logo abaixo CONTINUA preso à
   lista -- `teste_contrato_sidebar`/`ficha_visivel_1410` prendem isso, e foi o
   conserto de 14/10 (nome comprido escondia o botão "Ficha" no cabeçalho). */
.conversa__topo { display: flex; align-items: baseline; gap: var(--e-2); min-width: 0; }
.conversa__topo .conversa__quem { flex: 1 1 auto; min-width: 0; }

/* ---- o cabeçalho da conversa: o nome tem TETO, o botão não encolhe --------
   🚨 A PRIORIDADE É O BOTÃO NÃO SUMIR (decisão dele, 14/10): *"proponha limite
   do tamanho da exibição de nome, com a prioridade do botão não sumir. ao abrir
   a ficha ele aparece completo mesmo"*. Então o nome corta com reticências num
   teto, e o botão é `flex: none` -- ele não encolhe, não quebra e não é
   recortado, nem com nome absurdo nem com janela estreita.
   ⚠️ O nome inteiro continua na ficha, que é onde ele importa; aqui ele é
   identificação de relance. */
.conversa__cabecalho .conversa__quem {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--e-2);
  /* `flex` e `min-width` explícitos, iguais aos que a media query de 860px já
     declarava: a caixa ocupa a folga da barra e encolhe sem empurrar nada. */
  flex: 1 1 auto;
  min-width: 0;
}
.conversa__cabecalho .conversa__quem > strong {
  display: inline-flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  /* O teto. Em `ch` porque o que importa é quantos caracteres cabem, não
     pixels: assim ele acompanha o tamanho da fonte. */
  max-width: 32ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* 🚨 O botão da ficha é o último a ceder espaço -- na verdade, não cede. */
.conversa__cabecalho .conversa__quem > .botao { flex: none; }
/* O telefone é a segunda linha da identificação, não concorrente do botão. */
.conversa__cabecalho .conversa__detalhe { flex: 1 0 100%; margin: 0; }
.conversa__acompanham {
  display: flex; align-items: center; gap: var(--e-1);
  flex-wrap: wrap; padding: 0 var(--e-3) var(--e-2);
}
.chip--pequeno { font-size: var(--txt-sm); padding: 2px var(--e-1); }
.conversa__marca { color: var(--acento); display: flex; gap: 4px; align-items: center; }
.chip__x {
  background: none; border: 0; cursor: pointer; padding: 0 0 0 4px;
  color: inherit; opacity: .6; font-size: 1em; line-height: 1;
}
.chip__x:hover { opacity: 1; }
.conversa__quem { overflow-wrap: anywhere; }
.conversa__previa {
  margin: var(--e-1) 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.baloes {
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
  /* 🚨 A MESMA MARGEM LATERAL DO COMPOSITOR. O fio usava `--e-3` e o campo de
     escrever vive dentro de `.cartao__corpo`, que usa `--e-4`: 4px de
     diferença de cada lado, e o campo aparecia mais estreito que as mensagens.
     Apontado por ele em 26/08. Alinhar é usar o mesmo token, não somar um
     ajuste -- valor escolhido a olho volta a desalinhar na próxima mudança. */
  /* ⚠️ MARGEM LATERAL MAIOR QUE A VERTICAL (27/08, do mockup escolhido). O
     balão tem no máximo 66% da largura; sem margem generosa nas laterais ele
     encosta na borda e a conversa perde o ar que o desenho tem. */
  padding: var(--e-4) var(--e-7);
  /* Ocupa o que sobra da coluna em vez de um `vh` fixo. `min-height: 0` é o
     que permite o filho de um flex ENCOLHER e rolar; sem ele o fio empurra o
     compositor para fora da tela em conversa longa. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* Balão já quebra palavra; barra horizontal aqui só apareceria por acidente
     de conteúdo largo, e rolar a conversa de lado não serve para nada. */
  overflow-x: hidden;

  /* 🚨 O PAPEL DA CONVERSA (27/08, escolha dele entre cinco desenhos). A
     textura é o que o olho reconhece antes de ler: quem atende passa o dia no
     WhatsApp, e a semelhança vale treinamento.

     ⚠️ PADRÃO EM SVG EMBUTIDO, não arquivo: sem requisição, sem asset para
     versionar, e some junto com o CSS se um dia isto mudar. */
  background-color: var(--conversa-fundo);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='52' height='52' viewBox='0 0 52 52'%3E%3Cg fill='%23000' fill-opacity='.028'%3E%3Ccircle cx='9' cy='9' r='1.6'/%3E%3Ccircle cx='35' cy='22' r='1.2'/%3E%3Ccircle cx='20' cy='40' r='1.4'/%3E%3Ccircle cx='45' cy='47' r='1'/%3E%3C/g%3E%3C/svg%3E");
  gap: 3px;
}

/* ---- o balão ------------------------------------------------------------
   🚨 ELE TINHA ESCAPADO DO SISTEMA, e é a peça mais vista do painel: usava
   `var(--raio, 12px)` -- token que NUNCA EXISTIU, então caía no valor de
   emergência -- e três `rgba()` escritos à mão, fora da paleta. Agora cada
   valor é token, e o token está documentado no `tokens.css`. */
.balao {
  position: relative;
  max-width: min(66%, 520px);
  padding: 7px 11px 6px;
  border-radius: var(--conversa-raio);
  background: var(--conversa-balao);
  box-shadow: var(--sombra-1);
  line-height: 1.5;
}

/* 🚨 O BICO. É o detalhe que faz a tela ser reconhecida de longe, e ele é
   feito com borda -- nenhuma imagem, nenhum pseudo-elemento posicionado a
   olho. O canto do lado do bico perde o raio: é o que encaixa os dois. */
.balao--entrada { align-self: flex-start; border-top-left-radius: 0; }
.balao--entrada::before {
  content: "";
  position: absolute;
  left: calc(var(--conversa-bico) * -1);
  top: 0;
  border: var(--conversa-bico) solid transparent;
  border-left: 0;
  border-right-color: var(--conversa-balao);
  border-top-color: var(--conversa-balao);
}

.balao--saida {
  align-self: flex-end;
  background: var(--conversa-saida);
  border-top-right-radius: 0;
}
.balao--saida::before {
  content: "";
  position: absolute;
  right: calc(var(--conversa-bico) * -1);
  top: 0;
  border: var(--conversa-bico) solid transparent;
  border-right: 0;
  border-left-color: var(--conversa-saida);
  border-top-color: var(--conversa-saida);
}

/* ⚠️ A NOTA INTERNA NÃO TEM BICO, e isso é significado: ela não veio de
   ninguém e não vai para o cliente. Fica centrada, em papel próprio. */
.balao--interna {
  align-self: center;
  max-width: min(70%, 460px);
  background: var(--conversa-nota);
  color: var(--conversa-nota-texto);
  text-align: center;
  font-size: var(--txt-sm);
  font-style: normal;
}
.balao--interna::before { display: none; }

.balao__texto { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; }
/* Mesmo azul de link do resto do painel (`--acento`); sublinhado porque a
   cor sozinha não basta pra quem não distingue bem cores. */
.balao__link { color: var(--acento); text-decoration: underline; }
.balao__tipo { margin: 0 0 var(--e-1); }

/* ⚠️ HORA À DIREITA, sempre -- inclusive no balão de entrada. É onde o olho
   já procura, e alinhar à esquerda faria a hora competir com o texto. */
.balao__rodape {
  margin: 2px 0 0;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.modal__abas { display: flex; gap: var(--e-2); margin-bottom: var(--e-3); }

/* 🔵 24/09: o contador da aba, no mesmo verde da bolinha da conversa.
   ⚠️ SELO NO CANTO, FORA DO FLUXO: em linha ele alargava a aba e as quatro
   quebravam em duas linhas ("Sem / dono"), crescendo a barra -- visto na
   prévia. No canto não ocupa largura nenhuma. */
.abas__aba { position: relative; }
.abas__contador {
  position: absolute; top: -6px; right: -4px;
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 17px; height: 17px; padding: 0 4px;
  border-radius: var(--r-full); background: #25D366; color: #fff;
  box-shadow: 0 0 0 2px var(--superficie);
  font-size: 10px; font-weight: var(--peso-forte); line-height: 1;
  pointer-events: none;
}

/* A bolinha de não lidas. Verde do WhatsApp de propósito: é o lugar onde
   quem atende já espera encontrá-la. */
.conversa__selo {
  flex: none;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--ok);
  color: #fff;
  font-size: 11px;
  font-weight: var(--peso-forte);
  line-height: 18px;
  text-align: center;
}

/* 🔵 28/10: o selo da NOTA INTERNA. Cor DIFERENTE do de mensagem de propósito:
   verde é o cliente esperando; este é recado de colega, e quem olha a lista
   precisa distinguir num relance sem ler o título. */
.conversa__selo--nota {
  background: var(--aviso, #D97706);
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 0 6px;
}

/* Foto de perfil no cabeçalho da conversa: pequena e redonda, ao lado do
   nome. Não substitui a inicial colorida da lista -- convive com ela. */
.conversa__foto {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  vertical-align: middle;
  margin-right: var(--e-2);
}

.balao__mapa {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  color: var(--acento);
  text-decoration: none;
}
.balao__mapa:hover { text-decoration: underline; }

/* O segundo tique só existe quando a mensagem foi lida. */
.balao__tique { margin-left: 3px; letter-spacing: -2px; }
.balao__lida { color: var(--conversa-lida); font-weight: var(--peso-forte); }

/* Quem escreveu a nota. Fora do itálico do balão interno: é etiqueta, não
   parte do texto que a pessoa digitou. */
.balao__autor {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0 0 var(--e-1);
  font-style: normal;
  color: var(--texto-fraco);
}

.linha--quebra { flex-wrap: wrap; gap: var(--e-2); }

/* ---- busca --------------------------------------------------------------- */
.busca { display: flex; align-items: center; gap: var(--e-2); }
.busca .campo__entrada { flex: 1 1 auto; min-width: 0; }
.campo--busca { margin-bottom: var(--e-2); }
/* O seletor de time da aba Time (25/09): logo abaixo das abas. */
.campo--time { margin: var(--e-2) 0 0; max-width: 280px; }

/* ---- placar do cabeçalho -------------------------------------------------
   Número grande + rótulo pequeno: dois números com hierarquia leem-se de
   relance; quatro chips iguais obrigam a ler todos. */
.placar { display: flex; gap: var(--e-5); }
.placar__item { display: flex; flex-direction: column; line-height: 1.1; }
.placar__numero { font-size: var(--txt-xl); color: var(--texto); }
.placar__rotulo { font-size: var(--txt-sm); color: var(--texto-fraco); }
/* O que pede trabalho ganha cor. O resto fica neutro -- se tudo grita,
   nada grita. */
.placar__item--pede .placar__numero { color: var(--aviso); }

/* ---- controle segmentado -------------------------------------------------
   Uma escolha entre três, e não três ações. */
.abas {
  display: inline-flex;
  padding: 2px;
  background: var(--superficie-2);
  border: var(--borda-fina) solid var(--borda);
  border-radius: var(--r-full);
}
.abas__aba {
  border: 0;
  background: none;
  padding: 5px var(--e-3);
  border-radius: var(--r-full);
  font-family: var(--fonte);
  font-size: var(--txt-sm);
  color: var(--texto-fraco);
  cursor: pointer;
}
.abas__aba:hover { color: var(--texto); }
.abas__aba--ativa {
  background: var(--superficie);
  color: var(--texto);
  font-weight: var(--peso-forte);
  box-shadow: var(--sombra-1);
}
.abas__aba:focus-visible { outline: none; box-shadow: var(--foco); }

.anteriores { display: flex; justify-content: center; padding: var(--e-2) 0; }

/* ---- chamar alguém com @, só em grupo (27/08) --------------------------- */
/* `position: relative` porque a lista do `@` se ancora no compositor. */
/* 🚨 A FAIXA CINZA ATRÁS DO COMPOSITOR. É ela que faz o campo branco virar
   uma ilha; sem o contraste, o compositor se dissolvia no fim do fio. */
/* 🚨 QUEM CRESCE É SÓ O FIO. A coluna da conversa tem NOVE filhos diretos --
   barra do topo, acompanham, ações, aviso de ficha, gaveta, busca, fio e as
   três formas do rodapé. Num flex-column sem regra, vários deles crescem ou
   encolhem juntos, e foi isso que jogou a rolagem para o lugar errado.

   A regra é uma só e vale para todos: ninguém cresce, exceto o fio. */
.coluna--larga > * { flex: none; }
.coluna--larga > .baloes { flex: 1 1 auto; min-height: 0; }
/* Sem conversa escolhida, o vazio ocupa a coluna em vez de virar uma tira. */
.coluna--larga > .vazio {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* ⚠️ A GAVETA ROLA POR DENTRO. Ela é a ficha do cliente e pode ser mais alta
   que a tela; sem teto próprio, empurrava o fio e o compositor para fora. */
.coluna--larga > .gaveta { max-height: 42vh; overflow-y: auto; }

/* ⚠️ O `padding` SUBSTITUI o do `.cartao__corpo`, não soma com ele -- a
   auditoria pegou os dois empilhados, e o compositor ficava afundado.

   A faixa cinza vale para as TRÊS formas do rodapé (compositor, conversa
   resolvida, sem permissão): a barra de baixo não pode mudar de cor conforme
   o estado da conversa. */
.rodape-conversa,
.coluna--larga > .cartao__corpo.pilha,
.coluna--larga > p.cartao__corpo {
  background: var(--superficie-2);
  border-top: var(--borda-fina) solid var(--borda);
  padding: var(--e-3) var(--e-4) var(--e-4);
  margin: 0;
}

/* ---- o compositor (27/08) -----------------------------------------------
   🚨 O CAMPO É UMA ILHA BRANCA SOBRE FUNDO CINZA, e isso é a coisa que mais
   separa "campo de formulário" de "lugar de escrever mensagem". O fio tem
   papel próprio; sem o contraste aqui, o compositor sumia dentro do cartão. */
.compositor {
  position: relative;
  background: var(--superficie);
  border: var(--borda-fina) solid var(--borda);
  /* 🚨 14px, não 8: no desenho escolhido o campo é uma ILHA arredondada sobre
     a faixa cinza, e é o raio que faz ela parecer lugar de escrever mensagem
     em vez de campo de formulário. */
  border-radius: 14px;
  transition: border-color var(--tempo) var(--curva),
              box-shadow var(--tempo) var(--curva);
}
.compositor:focus-within {
  border-color: var(--acento-borda);
  box-shadow: 0 0 0 3px var(--acento-suave);
}
/* O `.campo__entrada` de dentro perde a própria borda: quem desenha a caixa
   agora é o compositor inteiro, senão ficam duas molduras concêntricas. */
.compositor .campo__entrada {
  border: 0;
  background: none;
  box-shadow: none;
}
.compositor .campo__entrada:focus { box-shadow: none; outline: 0; }

/* A lista SOBE: o compositor mora no rodapé da conversa, e abaixo dele a
   lista sairia da tela. */
.arroba {
  position: absolute;
  bottom: calc(100% + var(--e-1));
  left: 0;
  z-index: var(--z-flutuante);
  min-width: 220px;
  max-height: 220px;
  overflow-y: auto;
  margin: 0;
  padding: var(--e-1);
  list-style: none;
  background: var(--superficie);
  border: var(--borda-fina) solid var(--borda);
  border-radius: var(--r-md);
  box-shadow: var(--sombra-2);
}
.arroba__item {
  display: block;
  width: 100%;
  min-height: var(--altura-toque);
  padding: 0 var(--e-3);
  border: 0;
  border-radius: var(--r-sm);
  background: none;
  color: var(--texto);
  font-family: inherit;
  font-size: var(--txt-md);
  text-align: left;
  cursor: pointer;
}
.arroba__item:hover,
.arroba__item--aqui { background: var(--acento-suave); color: var(--acento-texto); }

.chamados { flex-wrap: wrap; gap: var(--e-1); margin: 0; }
.chamados__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px var(--e-2);
  border: var(--borda-fina) solid var(--acento-borda);
  border-radius: var(--r-full);
  background: var(--acento-suave);
  color: var(--acento-texto);
  font-family: inherit;
  font-size: var(--txt-sm);
  cursor: pointer;
}

/* ---- ações do balão -----------------------------------------------------
   Aparecem no hover: três botões fixos em cada balão transformam o fio numa
   grade de botões, e o que se lê é a conversa. */
.balao__acoes {
  position: absolute;
  top: -11px;
  right: var(--e-2);
  display: flex;
  gap: 2px;
  padding: 2px;
  background: var(--superficie);
  border: var(--borda-fina) solid var(--borda);
  border-radius: var(--r-full);
  box-shadow: var(--sombra-2);
  /* 🚨 `opacity`, NÃO `display: none` (27/08). Medido: com `display:none` o
     bloco sai do fluxo de foco, então `:focus-within` NUNCA dispara -- a linha
     existia e era código morto, e reagir, citar e encaminhar eram
     inalcançáveis por teclado.

     ⚠️ `pointer-events` acompanha a opacidade: invisível não pode continuar
     clicável por acidente, senão vira alvo fantasma sobre o balão. */
  opacity: 0;
  pointer-events: none;
  transform: translateY(3px);
  transition: opacity var(--tempo) var(--curva),
              transform var(--tempo) var(--curva);
}
.balao:hover .balao__acoes,
.balao:focus-within .balao__acoes {
  opacity: 1;
  pointer-events: auto;
  transform: none;
}
.balao__acao {
  border: 0;
  background: none;
  cursor: pointer;
  padding: 3px 6px;
  border-radius: var(--r-full);
  color: var(--texto-fraco);
  line-height: 1;
}
.balao__acao:hover { background: var(--superficie-2); color: var(--texto); }

.reacoes {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  display: flex;
  gap: 2px;
  padding: var(--e-1);
  background: var(--superficie);
  border: var(--borda-fina) solid var(--borda);
  border-radius: var(--r-full);
  box-shadow: var(--sombra-2);
  z-index: var(--z-flutuante);
}
.reacoes__item {
  border: 0;
  background: none;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 3px;
  border-radius: var(--r-full);
}
.reacoes__item:hover { background: var(--superficie-2); transform: scale(1.15); }
.reacoes__item--nosso { background: var(--superficie-2); outline: 2px solid var(--acento); }

/* A reação pendurada no canto: dentro do balão viraria mais uma linha. */
.balao__reacao {
  position: absolute;
  bottom: -10px;
  left: var(--e-3);
  display: flex;
  gap: 3px;
  line-height: 1.4;
}
.balao__reacao-item {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 5px;
  background: var(--superficie);
  border: var(--borda-fina) solid var(--borda);
  border-radius: var(--r-full);
  font-size: var(--txt-sm);
}
/* A nossa fica marcada: sem isso, num balão com três reações ninguém sabe
   qual foi a sua — e clicar de novo tiraria a de outra pessoa na cabeça de
   quem clica. */
.balao__reacao-item--nosso { border-color: var(--acento); }
.balao__reacao-item em {
  font-style: normal;
  font-size: var(--txt-xs);
  color: var(--texto-apagado);
}
.balao__marca { color: var(--texto-apagado); display: flex; gap: 4px; align-items: center; }

/* "editada" vive no rodapé, junto da hora: é do mesmo assunto que o tique.
   Nasce botão para poder abrir o texto anterior, mas não se veste como
   botão -- no meio do rodapé, uma caixa desenhada roubaria a leitura da
   conversa. */
.balao__editada {
  background: none;
  border: 0;
  padding: 0;
  margin-left: 3px;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-decoration: underline dotted;
  text-underline-offset: 2px;
}
.balao__editada:hover { text-decoration: underline; }

/* Apagada: em itálico e apagado, para não se confundir com fala do cliente. */
.balao__apagada {
  color: var(--texto-apagado);
  font-style: italic;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

/* Mesmo desenho do "editada": link de texto, não caixa de botão -- dentro do
   balão, um botão desenhado competiria com a conversa. */
.balao__revelar {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-decoration: underline dotted;
  text-underline-offset: 2px;
}
.balao__revelar:hover { text-decoration: underline; }

/* O texto de antes. Recuado e apagado: é prova, não é a conversa. */
.balao__original {
  margin-top: var(--e-1);
  padding-left: var(--e-2);
  border-left: 2px solid var(--borda);
  color: var(--texto-apagado);
  white-space: pre-wrap;
}

.balao__imagem--clicavel { cursor: zoom-in; }

/* ---- citar --------------------------------------------------------------- */
.citando {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  padding: var(--e-2);
  border-left: 3px solid var(--acento);
  background: var(--acento-suave);
  border-radius: var(--r-sm);
}
.citando__texto {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--txt-sm);
  color: var(--texto-fraco);
}

/* ---- gravação ------------------------------------------------------------ */
.gravando {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  color: var(--erro);
  font-size: var(--txt-sm);
  font-variant-numeric: tabular-nums;
}
.gravando__ponto {
  width: 9px;
  height: 9px;
  border-radius: var(--r-full);
  background: var(--erro);
  animation: pulsa 1.2s infinite;
}
@keyframes pulsa { 50% { opacity: .25; } }

/* ---- tela cheia ---------------------------------------------------------- */
.cheia {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  background: rgba(0, 0, 0, .85);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
}
.cheia__img { max-width: 92vw; max-height: 92vh; object-fit: contain; }
.cheia__fechar {
  position: absolute;
  top: var(--e-4);
  right: var(--e-4);
  border: 0;
  background: rgba(255, 255, 255, .15);
  color: #fff;
  width: 40px;
  height: 40px;
  border-radius: var(--r-full);
  cursor: pointer;
}

/* Separador de dia: linha fina atravessando, com o rótulo no meio. É o padrão
   que todo mensageiro usa, e por isso ninguém precisa aprender. */
.diario {
  display: flex;
  align-items: center;
  gap: var(--e-3);
  margin: var(--e-4) 0 var(--e-2);
  color: var(--texto-apagado);
  font-size: var(--txt-sm);
}
.diario::before,
.diario::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--borda);
}
.diario__marca { flex: none; text-transform: lowercase; }

/* 🔵 15/10, Bloco 11 — o log de ações.
   ⚠️ SEM os fios de `::before`/`::after` do separador de dia, de propósito:
   separador de dia aparece uma vez por dia, evento aparece várias vezes por
   atendimento. Com dois traços cada, o fio da conversa viraria uma escada. */
.evento {
  display: flex;
  align-items: baseline;
  gap: var(--e-2);
  margin: 2px 0;
  padding: 2px var(--e-2);
  color: var(--texto-apagado);
  font-size: var(--txt-sm);
  line-height: var(--entrelinha-apertada);
}
.evento i { flex: none; }
/* O nome de quem agiu é o que se procura numa linha de log: ganha peso, mas
   não cor -- cor aqui competiria com o balão ao lado. */
.evento strong { font-weight: var(--peso-medio); color: var(--texto-fraco); }
.evento__hora { margin-left: auto; flex: none; font-size: var(--txt-xs); }

/* 🔵 15/10, Bloco 12 — "visto por" na nota. `bem pequeno mesmo`, no tamanho do
   carimbo, e com alvo de toque decente mesmo sendo pequeno de desenho. */
.balao__visto {
  appearance: none;
  border: 0;
  background: none;
  padding: 0 2px;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.balao__visto:hover { color: var(--texto-fraco); }
.balao__vistos {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin: var(--e-1) 0 0;
  padding: var(--e-2);
  border-radius: var(--r-sm);
  background: var(--superficie-2);
  color: var(--texto-fraco);
}
.balao__visto-quem { display: flex; gap: var(--e-2); justify-content: space-between; }
@media (max-width: 860px) {
  /* No celular a hora completa empurra o texto para duas linhas; ali o que
     importa é o que aconteceu. */
  .evento__hora { display: none; }
}

/* 🔵 07/10: fronteira entre atendimentos do mesmo número (histórico contínuo).
   Mesma forma do separador de dia, mas com cor de acento e traço mais forte,
   para ler como "outro atendimento", não como "outro dia". */
.fio-anterior {
  display: flex;
  align-items: center;
  gap: var(--e-3);
  margin: var(--e-4) 0 var(--e-2);
  color: var(--acento, var(--texto-apagado));
  font-size: var(--txt-sm);
}
.fio-anterior::before,
.fio-anterior::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--acento, var(--borda));
  opacity: .5;
}
.fio-anterior__marca {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: var(--e-1);
  text-transform: lowercase;
}

.buscaconversa {
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
  padding: var(--e-2) var(--e-4);
  border-bottom: 1px solid var(--borda, rgba(128, 128, 128, .25));
}
.buscaconversa__conta { flex: none; min-width: 3.2em; text-align: right; }

/* O anexo escolhido, antes de ir. Sem esta faixa, o único sinal de que há
   arquivo seria o botão mudar de rótulo. */
.anexo {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  padding: var(--e-2) var(--e-3);
  border: 1px dashed var(--borda-forte, var(--borda));
  border-radius: var(--r-sm);
  background: var(--superficie-2);
}
.anexo__nome { overflow-wrap: anywhere; font-weight: var(--peso-medio); }
/* 🔵 25/09: até 10 anexos por envio -- a lista empilha, cada linha com o
   mesmo layout que o anexo único já tinha. */
.anexo--lista { flex-direction: column; align-items: stretch; gap: var(--e-1); }
.anexo__item { display: flex; align-items: center; gap: var(--e-2); }

/* O botão de nota é amarelo porque a nota é amarela em toda a tela. A cor diz
   para onde vai a mensagem ANTES do clique — que é justamente o que o seletor
   de modo, sendo estado invisível, não conseguia dizer. */
.botao--nota {
  background: rgba(255, 193, 7, .85);
  color: #241c00;
  border-color: rgba(255, 193, 7, .85);
}
.botao--nota:hover:not(:disabled) { background: rgba(255, 193, 7, 1); }

/* O termo achado. Amarelo é a convenção de "achado" em toda parte, e é o
   mesmo amarelo que a nota interna já usa nesta tela. */
.achado {
  background: rgba(255, 193, 7, .45);
  border-radius: 3px;
  font-weight: var(--peso-forte);
}
.conversa__previa--achado { color: var(--texto-fraco); }

/* A mensagem em que a busca está parada AGORA, distinta das outras que também
   casaram: sem isso, num balão longo, não se sabe qual das 17 é a "3". */
/* ⚠️ `--aviso-borda`, nao um ambar escrito a mao: a trava de estilo
   pegou este aqui em 27/08, junto com as tres cores do balao. */
.balao--casa { outline: 1px solid var(--aviso-borda); }
.balao--atual { outline: 2px solid var(--acento); }
/* Destaque de "clicar na citação vai até a original" (29/09) -- transiente,
   por isso é uma classe própria e não reaproveita `balao--atual` (que fica
   preso ao estado da busca). */
.balao--realcado { outline: 2px solid var(--acento); }

/* 🔵 07/10: a barra de ações nasce RECOLHIDA em qualquer largura; o botão
   "Ações" a abre. Antes isto valia só no celular (media query 860px); agora
   vale no computador também, pelo ganho de tela nas duas pontas. */
.acoes {
  border-top: 1px solid var(--borda, rgba(128, 128, 128, .25));
  display: none;
}
.acoes.acoes--aberta-celular { display: block; }

/* 🔵 07/10: o menu "Outro" do compositor — recolhe anexo, áudio e mensagens
   rápidas atrás de um botão, mesmo conceito do "Ações". `display: contents`
   faz os botões participarem da mesma linha flex quando abertos; o `v-show`
   cuida de esconder. */
.extras { display: contents; }

textarea.campo__entrada { resize: vertical; }
.campo--nota { background: rgba(255, 193, 7, .10); }

/* Classificar ficou no fim e fechado: é opcional desde 11/08, e caixa aberta
   por padrão lê como campo que falta preencher. */
.encerrar__extra { margin-top: var(--e-2); }
.encerrar__extra summary { cursor: pointer; color: var(--texto-fraco); }
.encerrar__extra > .campo { margin-top: var(--e-3); }
</style>
